/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
/**
 * Returns a local store instance
 */
function buildStore() {
    var storage = new Map();
    return {
        setItem: function (key, value) {
            storage.set(key, value);
        },
        getItem: function (key) {
            var result = storage.get(key);
            return result ? result : null;
        },
        removeItem: function (key) {
            storage.delete(key);
        },
        get length() {
            return storage.size;
        },
        clear: function () { return storage.clear(); },
        key: function (index) {
            var iterator = storage.entries();
            var item = iterator.next();
            for (var i = 0; i < index; i++) {
                item = iterator.next();
            }
            return item && item.value ? item.value[0] : null;
        },
    };
}
/**
 * Returns localStore in a browser environment, and a local store instance otherwise
 */
function getLocalStore() {
    if (typeof window !== "undefined" &&
        typeof window.localStorage !== "undefined") {
        return window.localStorage;
    }
    return buildStore();
}

/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
var CONTEXT_KEY_LOCALE = "i18nextLng";
// Key that specifies a preferred fallback language - e.g. if the user selects
// 'French' as the language for the current page, but there is no French, then
// we'll check if the user has a preferred fallback language, e.g. maybe in
// their profile they have selected 'Spanish' as their preferred fallback.
var CONTEXT_KEY_PREFERRED_FALLBACK_LANGUAGE = "lang_preferred_fallback";
/**
 * Simple class to hold 'context', which could include things like a chosen language, localization settings, process
 * details (like the credentials of the process, time the process started, the process ID, etc.).
 *
 * We can be configured with a storage instance on construction (e.g. to attempt to read values from 'localStorage' in
 * cases when we are deployed within a browser - e.g. a language drop-down might set the current language using a
 * simple key value of say 'i18nLanguage' in localStorage).
 */
var VocabContext = /** @class */ (function () {
    function VocabContext(locale, storage) {
        if (!locale) {
            throw new Error("A new context *MUST* be provided a locale, but none was provided.");
        }
        if (!storage) {
            throw new Error("A new context *MUST* be provided storage (we expect 'localStorage').");
        }
        this._initialLocale = locale;
        this._storage = storage;
        this._storage.setItem(CONTEXT_KEY_LOCALE, locale);
        this._createdAt = Date.now();
    }
    VocabContext.prototype.getLocale = function () {
        var _a;
        return (_a = this._storage.getItem(CONTEXT_KEY_LOCALE)) !== null && _a !== void 0 ? _a : this._initialLocale;
    };
    VocabContext.prototype.setLocale = function (locale) {
        this._storage.setItem(CONTEXT_KEY_LOCALE, locale);
        return this;
    };
    VocabContext.prototype.getInitialLocale = function () {
        return this._initialLocale;
    };
    VocabContext.prototype.getCreatedAt = function () {
        return this._createdAt;
    };
    return VocabContext;
}());

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
var VocabContextError = /** @class */ (function (_super) {
    __extends(VocabContextError, _super);
    function VocabContextError(context, message, wrappedException) {
        var _this = 
        // The ignore is required because of code coverage bug
        // https://github.com/gotwarlost/istanbul/issues/690
        _super.call(this, message) /* istanbul ignore next */ || this;
        if (wrappedException) {
            if (wrappedException instanceof VocabContextError) {
                _this._wrappedException = wrappedException;
                _this.message = _this.message + "\nContains context error: " + wrappedException.message;
            }
            else if (wrappedException instanceof Error) {
                _this._wrappedException = wrappedException;
                _this.message = _this.message + "\nContains error: " + wrappedException.message;
            }
            else {
                throw new Error("Context error can only wrap ContextErrors or Errors, but got [" + wrappedException + "] (message was [" + message + "]).");
            }
        }
        _this._context = context;
        _this._createdAt = Date.now();
        // See https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(_this, VocabContextError.prototype);
        return _this;
    }
    VocabContextError.prototype.report = function (level, totalLevels, exception) {
        var _a;
        var result = exception.message;
        var stack = exception.stack ? exception.stack.toString() : "";
        // Ignoring the next line is required for full code coverage, because when
        // testing in a Node environment, it is not possible to have `process`
        // undefined.
        // istanbul ignore next
        if (((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV) !== "production") {
            result += "\n" + ("Level " + level + " of " + totalLevels + ":\n" + stack);
        }
        return result;
    };
    VocabContextError.prototype.countLevels = function () {
        var result = 1;
        var current = this;
        while (current && current._wrappedException) {
            if (!(current._wrappedException instanceof VocabContextError)) {
                // If we have wrapped a standard exception, then the unwrapping stops,
                //  because standard errors can't wrap other errors.
                current = undefined;
                result++;
            }
            else {
                current = current._wrappedException;
                result++;
            }
        }
        return result;
    };
    VocabContextError.prototype.unwrapException = function () {
        var totalLevels = this.countLevels();
        var level = 1;
        var result = "";
        var current = this;
        while (current !== undefined) {
            result += "\n\n" + this.report(level++, totalLevels, current);
            if (!(current._wrappedException instanceof VocabContextError) &&
                current._wrappedException) {
                result +=
                    "\n\n" + this.report(level++, totalLevels, current._wrappedException);
                // When reaching a plain Error, the unwrapping stops
                current = undefined;
            }
            else {
                // Unwraps the exception until _wrappedException is undefined
                current = current._wrappedException;
            }
        }
        return result;
    };
    VocabContextError.prototype.toString = function () {
        return this.unwrapException();
    };
    VocabContextError.prototype.contains = function (elements) {
        if (!elements) {
            return true;
        }
        var message = this.unwrapException();
        return elements
            .map(function (element) { return message.includes(element); })
            .reduce(function (acc, current) { return acc && current; }, true);
    };
    return VocabContextError;
}(Error));

/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
var NO_LANGUAGE_TAG = "<No Language>";
// Typically, these would come from a LIT-generated artifact,
// but since those generated artifacts depend on this current, it's
// just much easier to define the constants we need manually here.
var XSD_STRING = "http://www.w3.org/2001/XMLSchema#string";
var RDF_LANGSTRING = "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString";
function isLiteral(term) {
    return term.language !== undefined;
}
/**
 * Class that defines the concept of a multi-lingual literal (as in an RDF
 * String literal). We can add multiple values in different languages, and
 * look them up again.
 * Also supports parameterized string values (using {{0}} placeholders), for
 * which we can provide values when looking them up.
 */
var VocabMultiLingualLiteral = /** @class */ (function () {
    /**
     *
     * @param rdfFactory Expected to provide RDF primitives (e.g. named nodes,
     * literals, etc.).
     * @param iri The IRI for this instance
     * @param values The values (if any) to initialise this instance
     * @param contextMessage Context information (helpful for debugging)
     * @returns {VocabMultiLingualLiteral|*}
     */
    function VocabMultiLingualLiteral(rdfFactory, iri, values, contextMessage) {
        // Implementing the RDFJS Literal interface
        this.termType = "Literal";
        this._rdfFactory = rdfFactory;
        this._iri = iri;
        this._values = values ? values : new Map();
        this._contextMessage = contextMessage ? contextMessage : "<None provided>";
        this._language = undefined;
        this._expandedMessage = undefined;
    }
    Object.defineProperty(VocabMultiLingualLiteral.prototype, "setToEnglish", {
        get: function () {
            this.asLanguage("en");
            return this;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabMultiLingualLiteral.prototype, "value", {
        get: function () {
            var _a, _b;
            return (_b = (_a = this.lookup(false)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "";
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabMultiLingualLiteral.prototype, "language", {
        get: function () {
            if (!this._language || this._language === NO_LANGUAGE_TAG) {
                return "";
            }
            else {
                return this._language;
            }
        },
        enumerable: false,
        configurable: true
    });
    VocabMultiLingualLiteral.prototype.equals = function (other) {
        if (isLiteral(other)) {
            return (this._values.get(other.language || NO_LANGUAGE_TAG) === other.value);
        }
        else {
            return false;
        }
    };
    Object.defineProperty(VocabMultiLingualLiteral.prototype, "datatype", {
        get: function () {
            if (!this.language || this.language == NO_LANGUAGE_TAG) {
                return this._rdfFactory.namedNode(XSD_STRING);
            }
            else {
                return this._rdfFactory.namedNode(RDF_LANGSTRING);
            }
        },
        enumerable: false,
        configurable: true
    });
    VocabMultiLingualLiteral.prototype.getIri = function () {
        return this._iri;
    };
    VocabMultiLingualLiteral.prototype.asLanguage = function (tag) {
        this._language = tag;
        return this;
    };
    VocabMultiLingualLiteral.prototype.addValue = function (value, locale) {
        if (!this._language) {
            this._language = locale;
        }
        this._values.set(locale, value);
        return this;
    };
    VocabMultiLingualLiteral.prototype.lookupEnglish = function (mandatory) {
        return this.asLanguage("en").lookup(mandatory);
    };
    /**
     * Looks up a message in the currently set language, but if none found we
     * use the English message (which code-generators can enforce, so they should
     * always ensure at least an English message for vocab terms).
     *
     * NOTE: If we do use the English default, then we also reset our language
     * tag so that if we are returning an RDF literal it will contain the correct
     * language tag (i.e. 'en'), and not the requested language that didn't exist!
     *
     * @param mandatory Flag - if true, we'll Throw an error if no value found.
     * @returns {*}
     */
    VocabMultiLingualLiteral.prototype.lookup = function (mandatory) {
        var message = this.lookupButDefaultToEnglishOrNoLanguage(mandatory);
        if (message === undefined) {
            return undefined;
        }
        this._expandedMessage = message;
        return this._rdfFactory.literal(message, this.handleNoLanguageTag());
    };
    /**
     * Private method that only looks up the string itself (i.e. will not attempt
     * to wrap in an RDF literal).
     *
     * @param mandatory Flag - if true, we'll Throw an error if no value found.
     * @returns {*}
     */
    VocabMultiLingualLiteral.prototype.lookupButDefaultToEnglishOrNoLanguage = function (mandatory) {
        if (!this._language) {
            if (mandatory) {
                throw new Error("No value has been added to the literal");
            }
            else {
                return undefined;
            }
        }
        var message = this._values.get(this._language);
        if (message) {
            return message;
        }
        else if (mandatory) {
            // NOTE: we explicitly throw here, regardless of our 'throw' parameter.
            throw new Error("MultiLingualLiteral message with IRI [" + this._iri.value + "] required value in language [" + this._language + "], but none found (Context: [" + this._contextMessage + "]).");
        }
        else {
            message = this._values.get("en");
            if (message) {
                this._language = "en";
            }
            else {
                message = this._values.get(NO_LANGUAGE_TAG);
                this._language = NO_LANGUAGE_TAG;
            }
        }
        return message;
    };
    /**
     * TODO: Won't yet handle replacing multiple uses of say {{1}} in a single
     *  string, which I guess it should...!?
     *
     * @param mandatory Flag - if true, we'll Throw an error if no value found.
     * @param rest array of values to be used to replace placeholders in
     * the looked-up message.
     * @returns {*}
     */
    VocabMultiLingualLiteral.prototype.params = function (mandatory) {
        var rest = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            rest[_i - 1] = arguments[_i];
        }
        var message = this.lookupButDefaultToEnglishOrNoLanguage(mandatory);
        // If we failed to find a value at all (and didn't throw!), then return
        // 'undefined'.
        if (message === undefined) {
            return undefined;
        }
        var paramsRequired = message.split("{{").length - 1;
        if (paramsRequired !== rest.length) {
            throw new Error("Setting parameters on LitMultiLingualLiteral with IRI [" + this._iri.value + "] and value [" + message + "] in language [" + this._language + "], but it requires [" + paramsRequired + "] params and we received [" + rest.length + "] (Context: [" + this._contextMessage + "]).");
        }
        for (var i = 0; i < rest.length; i++) {
            var marker = "{{" + i + "}}";
            message = message.replace(marker, rest[i]);
        }
        this._expandedMessage = message;
        return this._rdfFactory.literal(message, this.handleNoLanguageTag());
    };
    /**
     * We use a marker for no-language literals, so this handles that marker
     * and returns the correct RDF tag for 'no-language'.
     *
     * @returns {string}
     */
    VocabMultiLingualLiteral.prototype.handleNoLanguageTag = function () {
        return this._language === NO_LANGUAGE_TAG ? "" : this._language;
    };
    return VocabMultiLingualLiteral;
}());

/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
/**
 * Simple registry of terms (and their associated meta-data (like labels,
 * comment, message)) in multiple languages.
 *
 * We use localStorage to store all term meta-data, which can only store
 * strings (so we need to expand out the meta-data for each term).
 */
var VocabTermRegistry = /** @class */ (function () {
    function VocabTermRegistry(store) {
        this.store = store;
    }
    VocabTermRegistry.prototype.lookupLabel = function (termIri, language) {
        return this.lookupItem(termIri, language, "label");
    };
    VocabTermRegistry.prototype.updateLabel = function (termIri, language, label) {
        this.updateItem(termIri, language, label, "label");
    };
    VocabTermRegistry.prototype.lookupComment = function (termIri, language) {
        return this.lookupItem(termIri, language, "comment");
    };
    VocabTermRegistry.prototype.updateComment = function (termIri, language, label) {
        this.updateItem(termIri, language, label, "comment");
    };
    VocabTermRegistry.prototype.lookupMessage = function (termIri, language) {
        return this.lookupItem(termIri, language, "message");
    };
    VocabTermRegistry.prototype.updateMessage = function (termIri, language, label) {
        this.updateItem(termIri, language, label, "message");
    };
    VocabTermRegistry.prototype.updateItem = function (termIri, language, label, item) {
        this.store.setItem(termIri + "-" + item + "-" + language, label);
    };
    VocabTermRegistry.prototype.lookupItem = function (termIri, language, item) {
        return this.lookupFullTerm(termIri + "-" + item + "-", language);
    };
    /**
     * Looks up the specified vocabulary term in the specified language. If no
     * value found, will lookup again using the fallback language (as set in our
     * context). If not found again, will fallback to looking up the term in
     * English.
     *
     * @param term
     * @param language
     * @returns {string}
     */
    VocabTermRegistry.prototype.lookupFullTerm = function (term, language) {
        var _a;
        var fallbackLanguage = (_a = this.store.getItem(CONTEXT_KEY_PREFERRED_FALLBACK_LANGUAGE)) !== null && _a !== void 0 ? _a : "en";
        return this.lookupFullTermFallback(term, language, [
            fallbackLanguage,
            "en",
            NO_LANGUAGE_TAG,
        ]);
    };
    /**
     * Looks up the specified vocabulary term in the specified language. If no
     * value found, will lookup again using the provided fallback values one by
     * one until a value is found or there are no additional fallbacks.
     *
     * @param term {string}
     * @param language {string}
     * @param fallback {string[]}
     *
     * @returns {string | undefined}
     */
    VocabTermRegistry.prototype.lookupFullTermFallback = function (term, language, fallback) {
        var result = this.store.getItem("" + term + language);
        if (result) {
            return result;
        }
        else if (fallback.length > 0) {
            return this.lookupFullTermFallback(term, fallback[0], fallback.slice(1));
        }
        else {
            return undefined;
        }
    };
    return VocabTermRegistry;
}());

/**
 * Begin license text.
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * End license text.Source Distributions
 */
var DEFAULT_LOCALE = "en";
/**
 * Class to represent vocabulary terms. We expect derived classes to extend
 * an IRI (e.g. a NamedNode in RDFJS), but we just provide effectively an
 * abstract base class providing meta-data associated with terms in a
 * vocabulary, like labels and comments (in multiple-languages).
 *
 * We can also take a reference to a context storage instance, which can
 * contain various contextual information, such as the current locale, or
 * language settings for an interaction that can be used to lookup context at
 * runtime (e.g. to look up the locale for a term's label at runtime if one is
 * not explicitly asked for).
 *
 * This Turtle snippet may help illustrate what this class supports:
 *
 *   prefix rdf:  <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
 *   prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#>
 *   prefix skos:     <http://www.w3.org/2004/02/skos/core#>
 *   prefix ex:   <http://example.com/>
 *
 *   ex:name a rdf:Property ;
 *     rdfs:label "Name" ;
 *     rdfs:label "First name"@en ;
 *     rdfs:label "Nombre"@es ;
 *     rdfs:comment "A person's first name"@en .
 *
 *   ex:errNameTooLong a rdfs:Literal ;
 *     skos:definition "Name must be less than {{0}}, but we got {{1}}"@en .
 *
 * NOTE: Since this class does NOT actually store the IRI value for the vocab
 * term (since we expect derived classes to provide that), testing this
 * class in isolation will result in strange looking (i.e. 'undefined-'
 * prefixed) key values in 'localStorage' since we create those keys based on
 * the term IRI (that we don't store!). Currently this doesn't cause any
 * problems, but it's just something to be aware of!
 */
var VocabTerm = /** @class */ (function () {
    /**
     * Constructor.
     *
     * @param iri the IRI for this vocabulary term
     * @param rdfFactory an underlying RDF library that can create IRI's
     * @param contextStorage context for this term
     * @param strict flag if we should be strict. If not strict, we can use the
     * path component of the term's IRI as the English label if no explicit
     * English label (or no-language label) is provided, e.g. 'name' for the
     * term 'http://example.com/vocab#name'.
     */
    function VocabTerm(iri, rdfFactory, contextStorage, strict) {
        // Implementation of the NamedNode interface.
        this.termType = "NamedNode";
        if (typeof iri === "string") {
            this.iri = rdfFactory.namedNode(iri);
        }
        else {
            this.iri = iri;
        }
        this.rdfFactory = rdfFactory;
        if (strict !== undefined) {
            this.strict = strict;
        }
        else {
            this.strict = false;
        }
        this._litSessionContext = new VocabContext(DEFAULT_LOCALE, contextStorage);
        this._registry = new VocabTermRegistry(contextStorage);
        // Create holders for meta-data on this vocabulary term (we could probably
        // lazily create these only if values are actually provided!).
        this._label = new VocabMultiLingualLiteral(rdfFactory, this.iri, undefined, "rdfs:label");
        this._comment = new VocabMultiLingualLiteral(rdfFactory, this.iri, undefined, "rdfs:comment");
        this._message = new VocabMultiLingualLiteral(rdfFactory, this.iri, undefined, "message (should be defined in RDF vocab using: skos:definition)");
        if (!strict) {
            // This can be overwritten if we get an actual no-language label later,
            // which would be perfectly fine.
            this._label.addValue(VocabTerm.extractIriLocalName(iri), NO_LANGUAGE_TAG);
        }
        // Stateful variables defaults.
        this._mandatory = true;
        this._languageOverride = undefined;
        this._isDefinedBy = undefined;
        this._seeAlso = undefined;
        this.resetState();
    }
    Object.defineProperty(VocabTerm.prototype, "value", {
        get: function () {
            return this.iri.value;
        },
        enumerable: false,
        configurable: true
    });
    VocabTerm.prototype.equals = function (other) {
        return this.iri.equals(other);
    };
    Object.defineProperty(VocabTerm.prototype, "mandatory", {
        // Set our mandatory flag - i.e. throws if not as expected.
        get: function () {
            this._mandatory = true;
            return this;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "seeAlso", {
        get: function () {
            return this._seeAlso;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "isDefinedBy", {
        get: function () {
            return this._isDefinedBy;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "asEnglish", {
        // Simple convenience accessor for requesting English.
        get: function () {
            return this.asLanguage("en");
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "iriAsString", {
        // Explicitly named alias for getting the IRI of this term as a String.
        get: function () {
            return this.value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "labelLiteral", {
        // Accessor for label that uses our LitSessionContext instance.
        get: function () {
            try {
                var language = this.useLanguageOverrideOrGetFromContext();
                return this._label.asLanguage(language).lookup(this._mandatory);
            }
            finally {
                this.resetState();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "label", {
        get: function () {
            var label = this.labelLiteral;
            return label && label.value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "commentLiteral", {
        // Accessor for comment that uses our LitSessionContext instance.
        get: function () {
            try {
                var language = this.useLanguageOverrideOrGetFromContext();
                return this._comment.asLanguage(language).lookup(this._mandatory);
            }
            finally {
                this.resetState();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "comment", {
        get: function () {
            var comment = this.commentLiteral;
            return comment && comment.value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "messageLiteral", {
        // Accessor for message that uses our LitSessionContext instance.
        get: function () {
            try {
                var language = this.useLanguageOverrideOrGetFromContext();
                return this._message.asLanguage(language).lookup(this._mandatory);
            }
            finally {
                this.resetState();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(VocabTerm.prototype, "message", {
        get: function () {
            var message = this.messageLiteral;
            return message && message.value;
        },
        enumerable: false,
        configurable: true
    });
    // Get the IRI of this term as a String (means we can treat this object
    // instance as a string more easily).
    // NOTE: This is *NOT* an accessor, but deliberately overriding the
    // 'toString()' method on the base Object.
    VocabTerm.prototype.toString = function () {
        return this.value;
    };
    VocabTerm.prototype.messageParamsLiteral = function () {
        var _a;
        var rest = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            rest[_i] = arguments[_i];
        }
        var language = this.useLanguageOverrideOrGetFromContext();
        try {
            return (_a = this._message
                .asLanguage(language)).params.apply(_a, __spreadArrays([this._mandatory], rest));
        }
        finally {
            this.resetState();
        }
    };
    VocabTerm.prototype.messageParams = function () {
        var rest = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            rest[_i] = arguments[_i];
        }
        var messageParams = this.messageParamsLiteral.apply(this, rest);
        return messageParams && messageParams.value;
    };
    VocabTerm.prototype.resetState = function () {
        this._languageOverride = undefined;
        this._mandatory = false;
    };
    VocabTerm.prototype.addSeeAlso = function (value) {
        if (!this._seeAlso) {
            this._seeAlso = new Set();
        }
        this._seeAlso.add(value);
        return this;
    };
    VocabTerm.prototype.addIsDefinedBy = function (value) {
        this._isDefinedBy = value;
        return this;
    };
    VocabTerm.prototype.addLabelNoLanguage = function (value) {
        return this.addLabel(value, NO_LANGUAGE_TAG);
    };
    VocabTerm.prototype.addLabel = function (value, language) {
        this.validateAddParams(value, language, "label");
        this._label.addValue(value, language);
        this._registry.updateLabel(this.value, language, value);
        return this;
    };
    VocabTerm.prototype.addCommentNoLanguage = function (value) {
        return this.addComment(value, NO_LANGUAGE_TAG);
    };
    VocabTerm.prototype.addComment = function (value, language) {
        this.validateAddParams(value, language, "comment");
        this._comment.addValue(value, language);
        this._registry.updateComment(this.value, language, value);
        return this;
    };
    VocabTerm.prototype.addMessageNoLanguage = function (value) {
        return this.addMessage(value, NO_LANGUAGE_TAG);
    };
    VocabTerm.prototype.addMessage = function (value, language) {
        this.validateAddParams(value, language, "message");
        this._message.addValue(value, language);
        this._registry.updateMessage(this.value, language, value);
        return this;
    };
    /**
     * Ensure we always provide both a value and a lnaguage tag for that value.
     *
     * @param value the test of the value
     * @param language the language tag for the value
     * @param what what kind of value we are adding
     */
    VocabTerm.prototype.validateAddParams = function (value, language, what) {
        if (value === undefined || value === null) {
            throw new Error("Attempted to add a non-existent [" + what + "] value to vocab term");
        }
        if (!language) {
            throw new Error("Attempted to add the [" + what + "] value [" + value + "], but without specifying a language");
        }
        return this;
    };
    VocabTerm.prototype.useLanguageOverrideOrGetFromContext = function () {
        return this._languageOverride === undefined
            ? this._litSessionContext.getLocale()
            : this._languageOverride;
    };
    VocabTerm.prototype.asLanguage = function (language) {
        // An empty string is converted to the NO_LANGUAGE_TAG.
        this._languageOverride = language || NO_LANGUAGE_TAG;
        return this;
    };
    /**
     * Extract the local name from the specified IRI (can be a primitive string or
     * a NamedNode).
     *
     * @param stringOrNamedNode The IRI to extract from.
     * @returns {string}
     */
    VocabTerm.extractIriLocalName = function (stringOrNamedNode) {
        var iri = this.isString(stringOrNamedNode)
            ? stringOrNamedNode
            : stringOrNamedNode.value;
        var hashPos = iri.lastIndexOf("#");
        if (hashPos > -1) {
            return iri.substring(hashPos + 1);
        }
        var lastSlashPos = iri.lastIndexOf("/");
        if (lastSlashPos === -1 ||
            (iri.toLowerCase().startsWith("http") &&
                lastSlashPos < (iri.toLowerCase().startsWith("https") ? 8 : 7))) {
            throw Error("Expected hash fragment ('#') or slash ('/') (other than 'https://...') in IRI [" + iri + "]");
        }
        else {
            return iri.substring(lastSlashPos + 1);
        }
    };
    /**
     * Simple method to determine if the specified value is a primitive String.
  
     * @param value The value to evaluate.
     * @returns {boolean} true if String, else false.
     */
    VocabTerm.isString = function (value) {
        return typeof value === "string" || value instanceof String;
    };
    /**
     * Simply treat the value as an IRI if it starts with 'http://' or 'https://'
     * (case-insensitive).
     *
     * @param value
     * @returns {boolean}
     */
    VocabTerm.isStringIri = function (value) {
        if (!this.isString(value)) {
            return false;
        }
        var valueLower = value.toLowerCase();
        return (valueLower.startsWith("http://") || valueLower.startsWith("https://"));
    };
    return VocabTerm;
}());

function BlankNode (id) {
  this.value = id || ('b' + (++BlankNode.nextId));
}

BlankNode.prototype.equals = function (other) {
  return !!other && other.termType === this.termType && other.value === this.value
};

BlankNode.prototype.termType = 'BlankNode';

BlankNode.nextId = 0;

var blankNode = BlankNode;

function DefaultGraph () {
  this.value = '';
}

DefaultGraph.prototype.equals = function (other) {
  return !!other && other.termType === this.termType
};

DefaultGraph.prototype.termType = 'DefaultGraph';

var defaultGraph = DefaultGraph;

function NamedNode (iri) {
  this.value = iri;
}

NamedNode.prototype.equals = function (other) {
  return !!other && other.termType === this.termType && other.value === this.value
};

NamedNode.prototype.termType = 'NamedNode';

var namedNode = NamedNode;

function Literal (value, language, datatype) {
  this.value = value;
  this.datatype = Literal.stringDatatype;
  this.language = '';

  if (language) {
    this.language = language;
    this.datatype = Literal.langStringDatatype;
  } else if (datatype) {
    this.datatype = datatype;
  }
}

Literal.prototype.equals = function (other) {
  return !!other && other.termType === this.termType && other.value === this.value &&
    other.language === this.language && other.datatype.equals(this.datatype)
};

Literal.prototype.termType = 'Literal';
Literal.langStringDatatype = new namedNode('http://www.w3.org/1999/02/22-rdf-syntax-ns#langString');
Literal.stringDatatype = new namedNode('http://www.w3.org/2001/XMLSchema#string');

var literal = Literal;

function Quad (subject, predicate, object, graph) {
  this.subject = subject;
  this.predicate = predicate;
  this.object = object;

  if (graph) {
    this.graph = graph;
  } else {
    this.graph = new defaultGraph();
  }
}

Quad.prototype.equals = function (other) {
  // `|| !other.termType` is for backwards-compatibility with old factories without RDF* support.
  return !!other && (other.termType === 'Quad' || !other.termType) &&
    other.subject.equals(this.subject) && other.predicate.equals(this.predicate) &&
    other.object.equals(this.object) && other.graph.equals(this.graph)
};

Quad.prototype.termType = 'Quad';
Quad.prototype.value = '';

var quad = Quad;

function Variable (name) {
  this.value = name;
}

Variable.prototype.equals = function (other) {
  return !!other && other.termType === this.termType && other.value === this.value
};

Variable.prototype.termType = 'Variable';

var variable = Variable;

function DataFactory () {}

DataFactory.namedNode = function (value) {
  return new namedNode(value)
};

DataFactory.blankNode = function (value) {
  return new blankNode(value)
};

DataFactory.literal = function (value, languageOrDatatype) {
  if (typeof languageOrDatatype === 'string') {
    if (languageOrDatatype.indexOf(':') === -1) {
      return new literal(value, languageOrDatatype)
    }

    return new literal(value, null, DataFactory.namedNode(languageOrDatatype))
  }

  return new literal(value, null, languageOrDatatype)
};

DataFactory.defaultGraph = function () {
  return DataFactory.defaultGraphInstance
};

DataFactory.variable = function (value) {
  return new variable(value)
};

DataFactory.triple = function (subject, predicate, object) {
  return DataFactory.quad(subject, predicate, object)
};

DataFactory.quad = function (subject, predicate, object, graph) {
  return new quad(subject, predicate, object, graph || DataFactory.defaultGraphInstance)
};

DataFactory.defaultGraphInstance = new defaultGraph();

var dataFactory = DataFactory;

var dataModel = dataFactory;

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$1 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  Extension to Schema.org terms providing multilingual alternative names and translations for comments (e.g. for use directly as labels or tool-tips in user interfaces or error messages)
 */
function _NS(localName) {
    return namedNode$1("http://schema.org/" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var SCHEMA_INRUPT_EXT = {
    PREFIX: "schema-inrupt-ext",
    NAMESPACE: "http://schema.org/",
    PREFIX_AND_NAMESPACE: { "schema-inrupt-ext": "http://schema.org/" },
    NS: _NS,
    // *****************
    // All the Classes.
    // *****************
    /**
     * A person (alive, dead, undead, or fictional).
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    Person: new VocabTerm(_NS("Person"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Person", "en")
        .addLabel("La personne", "fr")
        .addLabel("Person", "de")
        .addLabel("Persona", "es")
        .addLabel("Persona", "it")
        .addLabelNoLanguage("Person")
        .addComment("Une personne (vivante, morte, mort-vivant ou fictive).", "fr")
        .addComment("Eine Person (lebendig, tot, untot oder fiktiv).", "de")
        .addComment("Una persona (viva, muerta, no muerta o ficticia).", "es")
        .addComment("Una persona (viva, morta, non morta o immaginaria).", "it")
        .addCommentNoLanguage("A person (alive, dead, undead, or fictional)."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * An alias for the item.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    alternateName: new VocabTerm(_NS("alternateName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Alternate Name", "en")
        .addLabel("Nom alternatif", "fr")
        .addLabel("Anderer Name", "de")
        .addLabel("Nombre alternativo", "es")
        .addLabel("Nome alternativo", "it")
        .addLabelNoLanguage("alternateName")
        .addComment("Un alias pour l'article.", "fr")
        .addComment("Ein Alias f\u00FCr den Artikel.", "de")
        .addComment("Un alias para el elemento.", "es")
        .addComment("Un alias per l'elemento.", "it")
        .addCommentNoLanguage("An alias for the item."),
    /**
     * Given name. In the U.S., the first name of a Person.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    givenName: new VocabTerm(_NS("givenName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Given Name", "en")
        .addLabel("Pr\u00E9nom", "fr")
        .addLabel("Vorname", "de")
        .addLabel("Nombre de pila", "es")
        .addLabel("Nome di battesimo", "it")
        .addLabelNoLanguage("givenName")
        .addComment("Pr\u00E9nom. Aux \u00C9tats-Unis, le pr\u00E9nom d\u2019une personne. Ceci peut \u00EAtre utilis\u00E9 avec familyName au lieu de la propri\u00E9t\u00E9 name.", "fr")
        .addComment("Vorname. In den USA der Vorname einer Person. Dies kann zusammen mit familyName anstelle der Eigenschaft name verwendet werden.", "de")
        .addComment("Nombre de pila. En los EE. UU., El primer nombre de una persona. Esto se puede usar junto con familyName en lugar de la propiedad name.", "es")
        .addComment("Nome di battesimo. Negli Stati Uniti, il primo nome di una persona. Questo pu\u00F2 essere usato insieme a familyName al posto della propriet\u00E0 name.", "it")
        .addCommentNoLanguage("Given name. In the U.S., the first name of a Person."),
    /**
     * Family name. In the U.S., the last name of a Person.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    familyName: new VocabTerm(_NS("familyName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Family Name", "en")
        .addLabel("Nom de famille", "fr")
        .addLabel("Nachname", "de")
        .addLabel("Apellido", "es")
        .addLabel("Cognome", "it")
        .addLabelNoLanguage("familyName")
        .addComment("Nom de famille. Aux \u00C9tats-Unis, le nom de famille d\u2019une personne. Ceci peut \u00EAtre utilis\u00E9 avec GivenName au lieu de la propri\u00E9t\u00E9 name.", "fr")
        .addComment("Nachname. In den USA der Nachname einer Person. Dies kann zusammen mit givenName anstelle der Eigenschaft name verwendet werden.", "de")
        .addComment("Apellido. En los EE.UU., el apellido de una persona. Esto se puede usar junto con givenName en lugar de la propiedad name.", "es")
        .addComment("Cognome. Negli Stati Uniti, il cognome di una persona. Questo pu\u00F2 essere usato insieme a givenName al posto della propriet\u00E0 name.", "it")
        .addCommentNoLanguage("Family name. In the U.S., the last name of a Person."),
    /**
     * An additional name for a Person, can be used for a middle name.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    additionalName: new VocabTerm(_NS("additionalName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Additional Name", "en")
        .addLabel("Nom additionnel", "fr")
        .addLabel("Zus\u00E4tzlicher Name", "de")
        .addLabel("Nombre adicional", "es")
        .addLabel("Nome aggiuntivo", "it")
        .addLabelNoLanguage("additionalName")
        .addComment("Un nom suppl\u00E9mentaire pour une personne peut \u00EAtre utilis\u00E9 pour un deuxi\u00E8me pr\u00E9nom.", "fr")
        .addComment("Ein zus\u00E4tzlicher Name f\u00FCr eine Person kann f\u00FCr einen zweiten Vornamen verwendet werden.", "de")
        .addComment("Un nombre adicional para una persona, se puede utilizar para un segundo nombre.", "es")
        .addComment("Un nome aggiuntivo per una persona pu\u00F2 essere usato per un secondo nome.", "it")
        .addCommentNoLanguage("An additional name for a Person, can be used for a middle name."),
    /**
     * A license document that applies to this content, typically indicated by URL.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    license: new VocabTerm(_NS("license"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("License", "en")
        .addLabel("License", "fr")
        .addLabel("Lizenz", "de")
        .addLabel("Licencia", "es")
        .addLabel("Licenza", "it")
        .addLabelNoLanguage("license")
        .addComment("Un document de licence qui s'applique \u00E0 ce contenu, g\u00E9n\u00E9ralement indiqu\u00E9 par une URL.", "fr")
        .addComment("Ein Lizenzdokument, das f\u00FCr diesen Inhalt gilt und in der Regel durch eine URL angegeben wird.", "de")
        .addComment("Un documento de licencia que se aplica a este contenido, normalmente indicado por URL.", "es")
        .addComment("Un documento di licenza che si applica a questo contenuto, generalmente indicato dall'URL.", "it")
        .addCommentNoLanguage("A license document that applies to this content, typically indicated by URL."),
    /**
     * Must have comment too!
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    newTerm: new VocabTerm(_NS("newTerm"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabelNoLanguage("newTerm")
        .addComment("Must have comment too!", "en"),
    /**
     * The name of the item.
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/schema#
     */
    name: new VocabTerm(_NS("name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$1("https://w3id.org/inrupt/vocab/extension/schema#"))
        .addLabel("Name", "en")
        .addLabel("Nom", "fr")
        .addLabel("Name", "de")
        .addLabel("Nombre", "es")
        .addLabel("Nome", "it")
        .addLabelNoLanguage("name")
        .addComment("Le nom de l'objet.", "fr")
        .addComment("Der Name des Artikels.", "de")
        .addComment("El nombre del art\u00EDculo.", "es")
        .addComment("Il nome dell'articolo.", "it")
        .addCommentNoLanguage("The name of the item."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$2 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$1(localName) {
    return namedNode$2("http://www.w3.org/2006/vcard/ns#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var VCARD = {
    PREFIX: "vcard",
    NAMESPACE: "http://www.w3.org/2006/vcard/ns#",
    PREFIX_AND_NAMESPACE: { "vcard": "http://www.w3.org/2006/vcard/ns#" },
    NS: _NS$1,
    // *****************
    // All the Classes.
    // *****************
    /**
     * Acquaintance
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Acquaintance: new VocabTerm(_NS$1("Acquaintance"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Acquaintance", "en"),
    /**
     * Used for relation type codes. The URI of the relation type code must be used as the value for the Relation Type.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    RelatedType: new VocabTerm(_NS$1("RelatedType"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Relation Type", "en")
        .addComment("Used for relation type codes. The URI of the relation type code must be used as the value for the Relation Type.", "en"),
    /**
     * Agent
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Agent: new VocabTerm(_NS$1("Agent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Agent", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    BBS: new VocabTerm(_NS$1("BBS"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("BBS", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Used for telephone type codes. The URI of the telephone type code must be used as the value for the Telephone Type.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    TelephoneType: new VocabTerm(_NS$1("TelephoneType"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Phone", "en")
        .addComment("Used for telephone type codes. The URI of the telephone type code must be used as the value for the Telephone Type.", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Car: new VocabTerm(_NS$1("Car"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Car", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Also called mobile telephone
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Cell: new VocabTerm(_NS$1("Cell"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Cell", "en")
        .addComment("Also called mobile telephone", "en"),
    /**
     * Child
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Child: new VocabTerm(_NS$1("Child"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Child", "en"),
    /**
     * Colleague
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Colleague: new VocabTerm(_NS$1("Colleague"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Colleague", "en"),
    /**
     * Contact
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Contact: new VocabTerm(_NS$1("Contact"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Contact", "en"),
    /**
     * Coresident
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Coresident: new VocabTerm(_NS$1("Coresident"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Coresident", "en"),
    /**
     * Coworker
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Coworker: new VocabTerm(_NS$1("Coworker"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Coworker", "en"),
    /**
     * Crush
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Crush: new VocabTerm(_NS$1("Crush"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Crush", "en"),
    /**
     * Date
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Date: new VocabTerm(_NS$1("Date"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Date", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Dom: new VocabTerm(_NS$1("Dom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Dom", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Used for type codes. The URI of the type code must be used as the value for Type.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Type: new VocabTerm(_NS$1("Type"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Type", "en")
        .addComment("Used for type codes. The URI of the type code must be used as the value for Type.", "en"),
    /**
     * Emergency
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Emergency: new VocabTerm(_NS$1("Emergency"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Emergency", "en"),
    /**
     * Fax
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Fax: new VocabTerm(_NS$1("Fax"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Fax", "en"),
    /**
     * Female
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Female: new VocabTerm(_NS$1("Female"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Female", "en"),
    /**
     * Used for gender codes. The URI of the gender code must be used as the value for Gender.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Gender: new VocabTerm(_NS$1("Gender"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Gender", "en")
        .addComment("Used for gender codes. The URI of the gender code must be used as the value for Gender.", "en"),
    /**
     * Friend
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Friend: new VocabTerm(_NS$1("Friend"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Friend", "en"),
    /**
     * This implies that the property is related to an individual's personal life
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Home: new VocabTerm(_NS$1("Home"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Home", "en")
        .addComment("This implies that the property is related to an individual's personal life", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    ISDN: new VocabTerm(_NS$1("ISDN"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("ISDN", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Internet: new VocabTerm(_NS$1("Internet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Internet", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Intl: new VocabTerm(_NS$1("Intl"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Intl", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Kin
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Kin: new VocabTerm(_NS$1("Kin"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Kin", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Label: new VocabTerm(_NS$1("Label"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Label", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Male
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Male: new VocabTerm(_NS$1("Male"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Male", "en"),
    /**
     * Me
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Me: new VocabTerm(_NS$1("Me"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Me", "en"),
    /**
     * Met
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Met: new VocabTerm(_NS$1("Met"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Met", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Modem: new VocabTerm(_NS$1("Modem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Modem", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Msg: new VocabTerm(_NS$1("Msg"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Msg", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Muse
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Muse: new VocabTerm(_NS$1("Muse"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Muse", "en"),
    /**
     * Neighbor
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Neighbor: new VocabTerm(_NS$1("Neighbor"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Neighbor", "en"),
    /**
     * None
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    None: new VocabTerm(_NS$1("None"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("None", "en"),
    /**
     * Other
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Other: new VocabTerm(_NS$1("Other"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Other", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    PCS: new VocabTerm(_NS$1("PCS"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("PCS", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Pager
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Pager: new VocabTerm(_NS$1("Pager"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Pager", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Parcel: new VocabTerm(_NS$1("Parcel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Parcel", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Parent
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Parent: new VocabTerm(_NS$1("Parent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Parent", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Postal: new VocabTerm(_NS$1("Postal"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Postal", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Pref: new VocabTerm(_NS$1("Pref"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Pref", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * Sibling
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Sibling: new VocabTerm(_NS$1("Sibling"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Sibling", "en"),
    /**
     * Spouse
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Spouse: new VocabTerm(_NS$1("Spouse"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Spouse", "en"),
    /**
     * Sweetheart
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Sweetheart: new VocabTerm(_NS$1("Sweetheart"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Sweetheart", "en"),
    /**
     * This class is deprecated. Use the hasTelephone object property.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Tel: new VocabTerm(_NS$1("Tel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Tel", "en")
        .addComment("This class is deprecated. Use the hasTelephone object property.", "en"),
    /**
     * Also called sms telephone
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Text: new VocabTerm(_NS$1("Text"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Text", "en")
        .addComment("Also called sms telephone", "en"),
    /**
     * Text phone
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    TextPhone: new VocabTerm(_NS$1("TextPhone"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Text phone", "en"),
    /**
     * Unknown
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Unknown: new VocabTerm(_NS$1("Unknown"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Unknown", "en"),
    /**
     * Video
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Video: new VocabTerm(_NS$1("Video"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Video", "en"),
    /**
     * Voice
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Voice: new VocabTerm(_NS$1("Voice"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Voice", "en"),
    /**
     * This implies that the property is related to an individual's work place
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Work: new VocabTerm(_NS$1("Work"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Work", "en")
        .addComment("This implies that the property is related to an individual's work place", "en"),
    /**
     * This class is deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    X400: new VocabTerm(_NS$1("X400"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("X400", "en")
        .addComment("This class is deprecated", "en"),
    /**
     * To specify the components of the delivery address for the  object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Address: new VocabTerm(_NS$1("Address"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Address", "en")
        .addComment("To specify the components of the delivery address for the  object", "en"),
    /**
     * To specify the electronic mail address for communication with the object the vCard represents. Use the hasEmail object property.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Email: new VocabTerm(_NS$1("Email"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Email", "en")
        .addComment("To specify the electronic mail address for communication with the object the vCard represents. Use the hasEmail object property.", "en"),
    /**
     * Object representing a group of persons or entities.  A group object will usually contain hasMember properties to specify the members of the group.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Group: new VocabTerm(_NS$1("Group"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Group", "en")
        .addComment("Object representing a group of persons or entities.  A group object will usually contain hasMember properties to specify the members of the group.", "en"),
    /**
     * The parent class for all objects
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Kind: new VocabTerm(_NS$1("Kind"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Kind", "en")
        .addComment("The parent class for all objects", "en"),
    /**
     * An object representing a single person or entity
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Individual: new VocabTerm(_NS$1("Individual"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Individual", "en")
        .addComment("An object representing a single person or entity", "en"),
    /**
     * An object representing a named geographical place
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Location: new VocabTerm(_NS$1("Location"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Location", "en")
        .addComment("An object representing a named geographical place", "en"),
    /**
     * An object representing an organization.  An organization is a single entity, and might represent a business or government, a department or division within a business or government, a club, an association, or the like.
  
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Organization: new VocabTerm(_NS$1("Organization"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Organization", "en")
        .addComment("An object representing an organization.  An organization is a single entity, and might represent a business or government, a department or division within a business or government, a club, an association, or the like.\n", "en"),
    /**
     * To specify the components of the name of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    Name: new VocabTerm(_NS$1("Name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("Name", "en")
        .addComment("To specify the components of the name of the object", "en"),
    /**
     * The vCard class is  equivalent to the new Kind class, which is the parent for the four explicit types of vCards (Individual, Organization, Location, Group)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    VCard: new VocabTerm(_NS$1("VCard"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("VCard", "en")
        .addComment("The vCard class is  equivalent to the new Kind class, which is the parent for the four explicit types of vCards (Individual, Organization, Location, Group)", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    adr: new VocabTerm(_NS$1("adr"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("address", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify the components of the delivery address for the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasAddress: new VocabTerm(_NS$1("hasAddress"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has address", "en")
        .addComment("To specify the components of the delivery address for the object", "en"),
    /**
     * This object property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    agent: new VocabTerm(_NS$1("agent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("agent", "en")
        .addComment("This object property has been deprecated", "en"),
    /**
     * The date of marriage, or equivalent, of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    anniversary: new VocabTerm(_NS$1("anniversary"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("anniversary", "en")
        .addComment("The date of marriage, or equivalent, of the object", "en"),
    /**
     * To specify the birth date of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    bday: new VocabTerm(_NS$1("bday"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("birth date", "en")
        .addComment("To specify the birth date of the object", "en"),
    /**
     * The category information about the object, also known as tags
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    category: new VocabTerm(_NS$1("category"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("category", "en")
        .addComment("The category information about the object, also known as tags", "en"),
    /**
     * This data property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    class: new VocabTerm(_NS$1("class"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("class", "en")
        .addComment("This data property has been deprecated", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    email: new VocabTerm(_NS$1("email"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("email", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify the electronic mail address for communication with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasEmail: new VocabTerm(_NS$1("hasEmail"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has email", "en")
        .addComment("To specify the electronic mail address for communication with the object", "en"),
    /**
     * This data property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    extended_address: new VocabTerm(_NS$1("extended-address"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("extended address", "en")
        .addComment("This data property has been deprecated", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    geo: new VocabTerm(_NS$1("geo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("geo", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify information related to the global positioning of the object. May also be used as a property parameter.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasGeo: new VocabTerm(_NS$1("hasGeo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has geo", "en")
        .addComment("To specify information related to the global positioning of the object. May also be used as a property parameter.", "en"),
    /**
     * Used to support property parameters for the additional name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasAdditionalName: new VocabTerm(_NS$1("hasAdditionalName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has additional name", "en")
        .addComment("Used to support property parameters for the additional name data property", "en"),
    /**
     * To specify the busy time associated with the object. (Was called FBURL in RFC6350)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasCalendarBusy: new VocabTerm(_NS$1("hasCalendarBusy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has calendar busy", "en")
        .addComment("To specify the busy time associated with the object. (Was called FBURL in RFC6350)", "en"),
    /**
     * To specify the calendar associated with the object. (Was called CALURI in RFC6350)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasCalendarLink: new VocabTerm(_NS$1("hasCalendarLink"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has calendar link", "en")
        .addComment("To specify the calendar associated with the object. (Was called CALURI in RFC6350)", "en"),
    /**
     * To specify the calendar user address to which a scheduling request be sent for the object. (Was called CALADRURI in RFC6350)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasCalendarRequest: new VocabTerm(_NS$1("hasCalendarRequest"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has calendar request", "en")
        .addComment("To specify the calendar user address to which a scheduling request be sent for the object. (Was called CALADRURI in RFC6350)", "en"),
    /**
     * Used to support property parameters for the category data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasCategory: new VocabTerm(_NS$1("hasCategory"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has category", "en")
        .addComment("Used to support property parameters for the category data property", "en"),
    /**
     * Used to support property parameters for the country name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasCountryName: new VocabTerm(_NS$1("hasCountryName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has country name", "en")
        .addComment("Used to support property parameters for the country name data property", "en"),
    /**
     * Used to support property parameters for the formatted name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasFN: new VocabTerm(_NS$1("hasFN"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has formatted name", "en")
        .addComment("Used to support property parameters for the formatted name data property", "en"),
    /**
     * Used to support property parameters for the family name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasFamilyName: new VocabTerm(_NS$1("hasFamilyName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has family name", "en")
        .addComment("Used to support property parameters for the family name data property", "en"),
    /**
     * To specify  the sex or gender identity of the object. URIs are recommended to enable interoperable sex and gender codes to be used.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasGender: new VocabTerm(_NS$1("hasGender"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has gender", "en")
        .addComment("To specify  the sex or gender identity of the object. URIs are recommended to enable interoperable sex and gender codes to be used.", "en"),
    /**
     * Used to support property parameters for the given name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasGivenName: new VocabTerm(_NS$1("hasGivenName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has given name", "en")
        .addComment("Used to support property parameters for the given name data property", "en"),
    /**
     * Used to support property parameters for the honorific prefix data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasHonorificPrefix: new VocabTerm(_NS$1("hasHonorificPrefix"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has honorific prefix", "en")
        .addComment("Used to support property parameters for the honorific prefix data property", "en"),
    /**
     * Used to support property parameters for the honorific suffix data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasHonorificSuffix: new VocabTerm(_NS$1("hasHonorificSuffix"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has honorific suffix", "en")
        .addComment("Used to support property parameters for the honorific suffix data property", "en"),
    /**
     * To specify the instant messaging and presence protocol communications with the object. (Was called IMPP in RFC6350)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasInstantMessage: new VocabTerm(_NS$1("hasInstantMessage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has messaging", "en")
        .addComment("To specify the instant messaging and presence protocol communications with the object. (Was called IMPP in RFC6350)", "en"),
    /**
     * Used to support property parameters for the language data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasLanguage: new VocabTerm(_NS$1("hasLanguage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has language", "en")
        .addComment("Used to support property parameters for the language data property", "en"),
    /**
     * Used to support property parameters for the locality data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasLocality: new VocabTerm(_NS$1("hasLocality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has locality", "en")
        .addComment("Used to support property parameters for the locality data property", "en"),
    /**
     * Used to support property parameters for the nickname data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasNickname: new VocabTerm(_NS$1("hasNickname"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addSeeAlso(namedNode$2("http://www.w3.org/2006/vcard/ns#nickname"))
        .addLabel("has nickname", "en")
        .addComment("Used to support property parameters for the nickname data property", "en"),
    /**
     * The nick name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    nickname: new VocabTerm(_NS$1("nickname"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("nickname", "en")
        .addComment("The nick name associated with the object", "en"),
    /**
     * Used to support property parameters for the note data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasNote: new VocabTerm(_NS$1("hasNote"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has note", "en")
        .addComment("Used to support property parameters for the note data property", "en"),
    /**
     * Used to support property parameters for the organization name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasOrganizationName: new VocabTerm(_NS$1("hasOrganizationName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has organization name", "en")
        .addComment("Used to support property parameters for the organization name data property", "en"),
    /**
     * Used to support property parameters for the organization unit name data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasOrganizationUnit: new VocabTerm(_NS$1("hasOrganizationUnit"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has organization unit name", "en")
        .addComment("Used to support property parameters for the organization unit name data property", "en"),
    /**
     * Used to support property parameters for the postal code data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasPostalCode: new VocabTerm(_NS$1("hasPostalCode"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has postal code", "en")
        .addComment("Used to support property parameters for the postal code data property", "en"),
    /**
     * Used to support property parameters for the region data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasRegion: new VocabTerm(_NS$1("hasRegion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has region", "en")
        .addComment("Used to support property parameters for the region data property", "en"),
    /**
     * To specify a relationship between another entity and the entity represented by this object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasRelated: new VocabTerm(_NS$1("hasRelated"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has related", "en")
        .addComment("To specify a relationship between another entity and the entity represented by this object", "en"),
    /**
     * Used to support property parameters for the role data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasRole: new VocabTerm(_NS$1("hasRole"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has role", "en")
        .addComment("Used to support property parameters for the role data property", "en"),
    /**
     * To identify the source of directory information of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasSource: new VocabTerm(_NS$1("hasSource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has source", "en")
        .addComment("To identify the source of directory information of the object", "en"),
    /**
     * Used to support property parameters for the street address data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasStreetAddress: new VocabTerm(_NS$1("hasStreetAddress"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has street address", "en")
        .addComment("Used to support property parameters for the street address data property", "en"),
    /**
     * Used to support property parameters for the title data property
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasTitle: new VocabTerm(_NS$1("hasTitle"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has title", "en")
        .addComment("Used to support property parameters for the title data property", "en"),
    /**
     * To specify a value that represents a globally unique identifier corresponding to the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasUID: new VocabTerm(_NS$1("hasUID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has uid", "en")
        .addComment("To specify a value that represents a globally unique identifier corresponding to the object", "en"),
    /**
     * Used to indicate the resource value of an object property that requires property parameters
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasValue: new VocabTerm(_NS$1("hasValue"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has value", "en")
        .addComment("Used to indicate the resource value of an object property that requires property parameters", "en"),
    /**
     * This data property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    label: new VocabTerm(_NS$1("label"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("label", "en")
        .addComment("This data property has been deprecated", "en"),
    /**
     * To specify the language that may be used for contacting the object. May also be used as a property parameter.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    language: new VocabTerm(_NS$1("language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("language", "en")
        .addComment("To specify the language that may be used for contacting the object. May also be used as a property parameter.", "en"),
    /**
     * This data property has been deprecated. See hasGeo
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    latitude: new VocabTerm(_NS$1("latitude"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("latitude", "en")
        .addComment("This data property has been deprecated. See hasGeo", "en"),
    /**
     * This data property has been deprecated. See hasGeo
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    longitude: new VocabTerm(_NS$1("longitude"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("longitude", "en")
        .addComment("This data property has been deprecated. See hasGeo", "en"),
    /**
     * This data property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    mailer: new VocabTerm(_NS$1("mailer"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("mailer", "en")
        .addComment("This data property has been deprecated", "en"),
    /**
     * A note associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    note: new VocabTerm(_NS$1("note"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("note", "en")
        .addComment("A note associated with the object", "en"),
    /**
     * This object property has been mapped. Use the organization-name data property.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    org: new VocabTerm(_NS$1("org"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("organization", "en")
        .addComment("This object property has been mapped. Use the organization-name data property.", "en"),
    /**
     * To specify the organizational name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    organization_name: new VocabTerm(_NS$1("organization-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("organization name", "en")
        .addComment("To specify the organizational name associated with the object", "en"),
    /**
     * To specify the organizational unit name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    organization_unit: new VocabTerm(_NS$1("organization-unit"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("organizational unit name", "en")
        .addComment("To specify the organizational unit name associated with the object", "en"),
    /**
     * This data property has been deprecated
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    post_office_box: new VocabTerm(_NS$1("post-office-box"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("post office box", "en")
        .addComment("This data property has been deprecated", "en"),
    /**
     * To specify the identifier for the product that created the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    prodid: new VocabTerm(_NS$1("prodid"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("product id", "en")
        .addComment("To specify the identifier for the product that created the object", "en"),
    /**
     * To specify revision information about the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    rev: new VocabTerm(_NS$1("rev"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("revision", "en")
        .addComment("To specify revision information about the object", "en"),
    /**
     * To specify the function or part played in a particular situation by the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    role: new VocabTerm(_NS$1("role"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("role", "en")
        .addComment("To specify the function or part played in a particular situation by the object", "en"),
    /**
     * To specify the string to be used for national-language-specific sorting. Used as a property parameter only.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    sort_string: new VocabTerm(_NS$1("sort-string"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("sort as", "en")
        .addComment("To specify the string to be used for national-language-specific sorting. Used as a property parameter only.", "en"),
    /**
     * To specify the position or job of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    title: new VocabTerm(_NS$1("title"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("title", "en")
        .addComment("To specify the position or job of the object", "en"),
    /**
     * To indicate time zone information that is specific to the object. May also be used as a property parameter.
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    tz: new VocabTerm(_NS$1("tz"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("time zone", "en")
        .addComment("To indicate time zone information that is specific to the object. May also be used as a property parameter.", "en"),
    /**
     * Used to indicate the literal value of a data property that requires property parameters
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    value: new VocabTerm(_NS$1("value"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("value", "en")
        .addComment("Used to indicate the literal value of a data property that requires property parameters", "en"),
    /**
     * The country name associated with the address of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    country_name: new VocabTerm(_NS$1("country-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("country name", "en")
        .addComment("The country name associated with the address of the object", "en"),
    /**
     * The locality (e.g. city or town) associated with the address of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    locality: new VocabTerm(_NS$1("locality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("locality", "en")
        .addComment("The locality (e.g. city or town) associated with the address of the object", "en"),
    /**
     * The postal code associated with the address of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    postal_code: new VocabTerm(_NS$1("postal-code"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("postal code", "en")
        .addComment("The postal code associated with the address of the object", "en"),
    /**
     * The region (e.g. state or province) associated with the address of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    region: new VocabTerm(_NS$1("region"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("region", "en")
        .addComment("The region (e.g. state or province) associated with the address of the object", "en"),
    /**
     * The street address associated with the address of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    street_address: new VocabTerm(_NS$1("street-address"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("street address", "en")
        .addComment("The street address associated with the address of the object", "en"),
    /**
     * To include a member in the group this object represents. (This property can only be used by Group individuals)
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasMember: new VocabTerm(_NS$1("hasMember"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has member", "en")
        .addComment("To include a member in the group this object represents. (This property can only be used by Group individuals)", "en"),
    /**
     * The additional name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    additional_name: new VocabTerm(_NS$1("additional-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("additional name", "en")
        .addComment("The additional name associated with the object", "en"),
    /**
     * The family name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    family_name: new VocabTerm(_NS$1("family-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("family name", "en")
        .addComment("The family name associated with the object", "en"),
    /**
     * The given name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    given_name: new VocabTerm(_NS$1("given-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("given name", "en")
        .addComment("The given name associated with the object", "en"),
    /**
     * The honorific prefix of the name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    honorific_prefix: new VocabTerm(_NS$1("honorific-prefix"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("honorific prefix", "en")
        .addComment("The honorific prefix of the name associated with the object", "en"),
    /**
     * The honorific suffix of the name associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    honorific_suffix: new VocabTerm(_NS$1("honorific-suffix"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("honorific suffix", "en")
        .addComment("The honorific suffix of the name associated with the object", "en"),
    /**
     * The formatted text corresponding to the name of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    fn: new VocabTerm(_NS$1("fn"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("formatted name", "en")
        .addComment("The formatted text corresponding to the name of the object", "en"),
    /**
     * To specify a public key or authentication certificate associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasKey: new VocabTerm(_NS$1("hasKey"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has key", "en")
        .addComment("To specify a public key or authentication certificate associated with the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    key: new VocabTerm(_NS$1("key"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("key", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify a graphic image of a logo associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasLogo: new VocabTerm(_NS$1("hasLogo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has logo", "en")
        .addComment("To specify a graphic image of a logo associated with the object ", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    logo: new VocabTerm(_NS$1("logo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("logo", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify the components of the name of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasName: new VocabTerm(_NS$1("hasName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has name", "en")
        .addComment("To specify the components of the name of the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    n: new VocabTerm(_NS$1("n"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("name", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify an image or photograph information that annotates some aspect of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasPhoto: new VocabTerm(_NS$1("hasPhoto"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has photo", "en")
        .addComment("To specify an image or photograph information that annotates some aspect of the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    photo: new VocabTerm(_NS$1("photo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("photo", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify a digital sound content information that annotates some aspect of the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasSound: new VocabTerm(_NS$1("hasSound"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has sound", "en")
        .addComment("To specify a digital sound content information that annotates some aspect of the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    sound: new VocabTerm(_NS$1("sound"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("sound", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify the telephone number for telephony communication with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasTelephone: new VocabTerm(_NS$1("hasTelephone"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has telephone", "en")
        .addComment("To specify the telephone number for telephony communication with the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    tel: new VocabTerm(_NS$1("tel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("telephone", "en")
        .addComment("This object property has been mapped", "en"),
    /**
     * To specify a uniform resource locator associated with the object
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    hasURL: new VocabTerm(_NS$1("hasURL"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("has url", "en")
        .addComment("To specify a uniform resource locator associated with the object", "en"),
    /**
     * This object property has been mapped
     *
     * Defined by the vocabulary: http://www.w3.org/2006/vcard/ns
     */
    url: new VocabTerm(_NS$1("url"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$2("http://www.w3.org/2006/vcard/ns"))
        .addLabel("url", "en")
        .addComment("This object property has been mapped", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$3 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$2(localName) {
    return namedNode$3("http://www.w3.org/2006/vcard/ns#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var VCARD_INRUPT_EXT = {
    PREFIX: "vcard-inrupt-ext",
    NAMESPACE: "http://www.w3.org/2006/vcard/ns#",
    PREFIX_AND_NAMESPACE: { "vcard-inrupt-ext": "http://www.w3.org/2006/vcard/ns#" },
    NS: _NS$2,
    // *******************
    // All the Properties.
    // *******************
    /**
     * To specify the components of the delivery address for the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    hasAddress: new VocabTerm(_NS$2("hasAddress"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Address", "en")
        .addLabel("Direcci\u00F3n", "es")
        .addComment("To specify the components of the delivery address for the object", "en"),
    /**
     * To specify the electronic mail address for communication with the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    hasEmail: new VocabTerm(_NS$2("hasEmail"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("EMail", "en")
        .addLabel("Correo electr\u00F3nico", "es")
        .addComment("To specify the electronic mail address for communication with the object", "en"),
    /**
     * To specify the telephone number for telephony communication with the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    hasTelephone: new VocabTerm(_NS$2("hasTelephone"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Telephone", "en")
        .addLabel("Tel\u00E9fono", "es")
        .addComment("To specify the telephone number for telephony communication with the object", "en"),
    /**
     * The formatted text corresponding to the name of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    fn: new VocabTerm(_NS$2("fn"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Name", "en")
        .addLabel("Nombre", "es")
        .addComment("The formatted text corresponding to the name of the object", "en"),
    /**
     * To specify the function or part played in a particular situation by the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    role: new VocabTerm(_NS$2("role"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Role", "en")
        .addLabel("Rol", "es")
        .addComment("To specify the function or part played in a particular situation by the object", "en"),
    /**
     * To specify the organizational name associated with the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    organization_name: new VocabTerm(_NS$2("organization-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Company", "en")
        .addLabel("Compa\u00F1\u00EDa", "es")
        .addComment("To specify the organizational name associated with the object", "en"),
    /**
     * The street address associated with the address of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    street_address: new VocabTerm(_NS$2("street-address"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Direcci\u00F3n", "es")
        .addLabel("Street Address", "en")
        .addComment("The street address associated with the address of the object", "en"),
    /**
     * The locality (e.g. city or town) associated with the address of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    locality: new VocabTerm(_NS$2("locality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Locality", "en")
        .addLabel("Localidad", "es")
        .addComment("The locality (e.g. city or town) associated with the address of the object", "en"),
    /**
     * The postal code associated with the address of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    postal_code: new VocabTerm(_NS$2("postal-code"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Postal Code", "en")
        .addLabel("C\u00F3digo Postal", "es")
        .addComment("The postal code associated with the address of the object", "en"),
    /**
     * The region (e.g. state or province) associated with the address of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    region: new VocabTerm(_NS$2("region"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Region", "en")
        .addLabel("Regi\u00F3n", "es")
        .addComment("The region (e.g. state or province) associated with the address of the object", "en"),
    /**
     * The country name associated with the address of the object
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/vcard#
     */
    country_name: new VocabTerm(_NS$2("country-name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$3("https://w3id.org/inrupt/vocab/extension/vcard#"))
        .addLabel("Country", "en")
        .addLabel("Pais", "es")
        .addComment("The country name associated with the address of the object", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$4 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$3(localName) {
    return namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var RDF = {
    PREFIX: "rdf",
    NAMESPACE: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
    PREFIX_AND_NAMESPACE: { "rdf": "http://www.w3.org/1999/02/22-rdf-syntax-ns#" },
    NS: _NS$3,
    // *****************
    // All the Classes.
    // *****************
    /**
     * The datatype of RDF literals storing fragments of HTML content
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    HTML: new VocabTerm(_NS$3("HTML"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("http://www.w3.org/TR/rdf11-concepts/#section-html"))
        .addLabelNoLanguage("HTML")
        .addCommentNoLanguage("The datatype of RDF literals storing fragments of HTML content"),
    /**
     * The datatype of language-tagged string values
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    langString: new VocabTerm(_NS$3("langString"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("http://www.w3.org/TR/rdf11-concepts/#section-Graph-Literal"))
        .addLabelNoLanguage("langString")
        .addCommentNoLanguage("The datatype of language-tagged string values"),
    /**
     * The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    PlainLiteral: new VocabTerm(_NS$3("PlainLiteral"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("http://www.w3.org/TR/rdf-plain-literal/"))
        .addLabelNoLanguage("PlainLiteral")
        .addCommentNoLanguage("The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2"),
    /**
     * The class of RDF properties.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    Property: new VocabTerm(_NS$3("Property"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("Property")
        .addCommentNoLanguage("The class of RDF properties."),
    /**
     * The class of RDF statements.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    Statement: new VocabTerm(_NS$3("Statement"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("Statement")
        .addCommentNoLanguage("The class of RDF statements."),
    /**
     * The class of unordered containers.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    Bag: new VocabTerm(_NS$3("Bag"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("Bag")
        .addCommentNoLanguage("The class of unordered containers."),
    /**
     * The class of ordered containers.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    Seq: new VocabTerm(_NS$3("Seq"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("Seq")
        .addCommentNoLanguage("The class of ordered containers."),
    /**
     * The class of containers of alternatives.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    Alt: new VocabTerm(_NS$3("Alt"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("Alt")
        .addCommentNoLanguage("The class of containers of alternatives."),
    /**
     * The class of RDF Lists.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    List: new VocabTerm(_NS$3("List"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("List")
        .addCommentNoLanguage("The class of RDF Lists."),
    /**
     * The datatype of XML literal values.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    XMLLiteral: new VocabTerm(_NS$3("XMLLiteral"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("XMLLiteral")
        .addCommentNoLanguage("The datatype of XML literal values."),
    /**
     * The datatype of RDF literals storing JSON content.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    JSON: new VocabTerm(_NS$3("JSON"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("https://www.w3.org/TR/json-ld11/#the-rdf-json-datatype"))
        .addLabelNoLanguage("JSON")
        .addCommentNoLanguage("The datatype of RDF literals storing JSON content."),
    /**
     * A class representing a compound literal.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    CompoundLiteral: new VocabTerm(_NS$3("CompoundLiteral"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("https://www.w3.org/TR/json-ld11/#the-rdf-compoundliteral-class-and-the-rdf-language-and-rdf-direction-properties"))
        .addLabelNoLanguage("CompoundLiteral")
        .addCommentNoLanguage("A class representing a compound literal."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The subject is an instance of a class.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    type: new VocabTerm(_NS$3("type"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("type")
        .addCommentNoLanguage("The subject is an instance of a class."),
    /**
     * The subject of the subject RDF statement.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    subject: new VocabTerm(_NS$3("subject"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("subject")
        .addCommentNoLanguage("The subject of the subject RDF statement."),
    /**
     * The predicate of the subject RDF statement.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    predicate: new VocabTerm(_NS$3("predicate"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("predicate")
        .addCommentNoLanguage("The predicate of the subject RDF statement."),
    /**
     * The object of the subject RDF statement.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    object: new VocabTerm(_NS$3("object"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("object")
        .addCommentNoLanguage("The object of the subject RDF statement."),
    /**
     * Idiomatic property used for structured values.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    value: new VocabTerm(_NS$3("value"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("value")
        .addCommentNoLanguage("Idiomatic property used for structured values."),
    /**
     * The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    nil: new VocabTerm(_NS$3("nil"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("nil")
        .addCommentNoLanguage("The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it."),
    /**
     * The first item in the subject RDF list.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    first: new VocabTerm(_NS$3("first"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("first")
        .addCommentNoLanguage("The first item in the subject RDF list."),
    /**
     * The rest of the subject RDF list after the first item.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    rest: new VocabTerm(_NS$3("rest"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabelNoLanguage("rest")
        .addCommentNoLanguage("The rest of the subject RDF list after the first item."),
    /**
     * The language component of a CompoundLiteral.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    language: new VocabTerm(_NS$3("language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("https://www.w3.org/TR/json-ld11/#the-rdf-compoundliteral-class-and-the-rdf-language-and-rdf-direction-properties"))
        .addLabelNoLanguage("language")
        .addCommentNoLanguage("The language component of a CompoundLiteral."),
    /**
     * The base direction component of a CompoundLiteral.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    direction: new VocabTerm(_NS$3("direction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$4("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addSeeAlso(namedNode$4("https://www.w3.org/TR/json-ld11/#the-rdf-compoundliteral-class-and-the-rdf-language-and-rdf-direction-properties"))
        .addLabelNoLanguage("direction")
        .addCommentNoLanguage("The base direction component of a CompoundLiteral."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$5 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$4(localName) {
    return namedNode$5("http://www.w3.org/1999/02/22-rdf-syntax-ns#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var RDF_INRUPT_EXT = {
    PREFIX: "rdf-inrupt-ext",
    NAMESPACE: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
    PREFIX_AND_NAMESPACE: { "rdf-inrupt-ext": "http://www.w3.org/1999/02/22-rdf-syntax-ns#" },
    NS: _NS$4,
    // *******************
    // All the Properties.
    // *******************
    /**
     * The subject is an instance of a class.
     *
     * Defined by the vocabulary: http://www.w3.org/1999/02/22-rdf-syntax-ns#
     */
    type: new VocabTerm(_NS$4("type"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$5("http://www.w3.org/1999/02/22-rdf-syntax-ns#"))
        .addLabel("Tipo", "es")
        .addLabelNoLanguage("type")
        .addCommentNoLanguage("The subject is an instance of a class."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$6 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$5(localName) {
    return namedNode$6("http://www.w3.org/2000/01/rdf-schema#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var RDFS = {
    PREFIX: "rdfs",
    NAMESPACE: "http://www.w3.org/2000/01/rdf-schema#",
    PREFIX_AND_NAMESPACE: { "rdfs": "http://www.w3.org/2000/01/rdf-schema#" },
    NS: _NS$5,
    // *****************
    // All the Classes.
    // *****************
    /**
     * The class resource, everything.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    Resource: new VocabTerm(_NS$5("Resource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("Resource")
        .addCommentNoLanguage("The class resource, everything."),
    /**
     * The class of classes.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    Class: new VocabTerm(_NS$5("Class"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("Class")
        .addCommentNoLanguage("The class of classes."),
    /**
     * The class of literal values, eg. textual strings and integers.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    Literal: new VocabTerm(_NS$5("Literal"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("Literal")
        .addCommentNoLanguage("The class of literal values, eg. textual strings and integers."),
    /**
     * The class of RDF containers.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    Container: new VocabTerm(_NS$5("Container"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("Container")
        .addCommentNoLanguage("The class of RDF containers."),
    /**
     * The class of container membership properties, rdf:_1, rdf:_2, ...,
                      all of which are sub-properties of 'member'.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    ContainerMembershipProperty: new VocabTerm(_NS$5("ContainerMembershipProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("ContainerMembershipProperty")
        .addCommentNoLanguage("The class of container membership properties, rdf:_1, rdf:_2, ...,\n                    all of which are sub-properties of 'member'."),
    /**
     * The class of RDF datatypes.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    Datatype: new VocabTerm(_NS$5("Datatype"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("Datatype")
        .addCommentNoLanguage("The class of RDF datatypes."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The defininition of the subject resource.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    isDefinedBy: new VocabTerm(_NS$5("isDefinedBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("isDefinedBy")
        .addCommentNoLanguage("The defininition of the subject resource."),
    /**
     * A human-readable name for the subject.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    label: new VocabTerm(_NS$5("label"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("label")
        .addCommentNoLanguage("A human-readable name for the subject."),
    /**
     * A description of the subject resource.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    comment: new VocabTerm(_NS$5("comment"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("comment")
        .addCommentNoLanguage("A description of the subject resource."),
    /**
     * The subject is a subclass of a class.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    subClassOf: new VocabTerm(_NS$5("subClassOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("subClassOf")
        .addCommentNoLanguage("The subject is a subclass of a class."),
    /**
     * A range of the subject property.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    range: new VocabTerm(_NS$5("range"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("range")
        .addCommentNoLanguage("A range of the subject property."),
    /**
     * A domain of the subject property.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    domain: new VocabTerm(_NS$5("domain"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("domain")
        .addCommentNoLanguage("A domain of the subject property."),
    /**
     * The subject is a subproperty of a property.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    subPropertyOf: new VocabTerm(_NS$5("subPropertyOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("subPropertyOf")
        .addCommentNoLanguage("The subject is a subproperty of a property."),
    /**
     * Further information about the subject resource.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    seeAlso: new VocabTerm(_NS$5("seeAlso"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("seeAlso")
        .addCommentNoLanguage("Further information about the subject resource."),
    /**
     * A member of the subject resource.
     *
     * Defined by the vocabulary: http://www.w3.org/2000/01/rdf-schema#
     */
    member: new VocabTerm(_NS$5("member"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$6("http://www.w3.org/2000/01/rdf-schema#"))
        .addLabelNoLanguage("member")
        .addCommentNoLanguage("A member of the subject resource."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$7 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  A vocabulary for describing XML Schema datatypes.
 */
function _NS$6(localName) {
    return namedNode$7("http://www.w3.org/2001/XMLSchema#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var XSD = {
    PREFIX: "xsd",
    NAMESPACE: "http://www.w3.org/2001/XMLSchema#",
    PREFIX_AND_NAMESPACE: { "xsd": "http://www.w3.org/2001/XMLSchema#" },
    NS: _NS$6,
    // *******************
    // All the Properties.
    // *******************
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    changes: new VocabTerm(_NS$6("changes"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Changes", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    string: new VocabTerm(_NS$6("string"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("String", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    boolean: new VocabTerm(_NS$6("boolean"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Boolean", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    float: new VocabTerm(_NS$6("float"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Float", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    double: new VocabTerm(_NS$6("double"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Double", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    decimal: new VocabTerm(_NS$6("decimal"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Decimal", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    dateTime: new VocabTerm(_NS$6("dateTime"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Date and time", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    duration: new VocabTerm(_NS$6("duration"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Duration", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    hexBinary: new VocabTerm(_NS$6("hexBinary"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Hexadecimal binary", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    base64Binary: new VocabTerm(_NS$6("base64Binary"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Base64 binary", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    anyURI: new VocabTerm(_NS$6("anyURI"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("URI", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    ID: new VocabTerm(_NS$6("ID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("ID", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    IDREF: new VocabTerm(_NS$6("IDREF"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("ID reference", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    ENTITY: new VocabTerm(_NS$6("ENTITY"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("ENTITY", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    NOTATION: new VocabTerm(_NS$6("NOTATION"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("NOTATION", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    normalizedString: new VocabTerm(_NS$6("normalizedString"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Normalized string", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    token: new VocabTerm(_NS$6("token"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Token", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    language: new VocabTerm(_NS$6("language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Language", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    IDREFS: new VocabTerm(_NS$6("IDREFS"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("IDREFS", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    ENTITIES: new VocabTerm(_NS$6("ENTITIES"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("ENTITIES", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    NMTOKEN: new VocabTerm(_NS$6("NMTOKEN"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("NMTOKEN", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    NMTOKENS: new VocabTerm(_NS$6("NMTOKENS"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("NMTOKENS", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    Name: new VocabTerm(_NS$6("Name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Name", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    QName: new VocabTerm(_NS$6("QName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("QName", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    NCName: new VocabTerm(_NS$6("NCName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("NCName", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    integer: new VocabTerm(_NS$6("integer"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Integer", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    nonNegativeInteger: new VocabTerm(_NS$6("nonNegativeInteger"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Non-negative integer", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    positiveInteger: new VocabTerm(_NS$6("positiveInteger"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Positive integer", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    nonPositiveInteger: new VocabTerm(_NS$6("nonPositiveInteger"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Non-positive integer", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    negativeInteger: new VocabTerm(_NS$6("negativeInteger"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Negative integer", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    byte: new VocabTerm(_NS$6("byte"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Byte", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    int: new VocabTerm(_NS$6("int"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Int", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    long: new VocabTerm(_NS$6("long"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Long", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    short: new VocabTerm(_NS$6("short"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Short", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    unsignedByte: new VocabTerm(_NS$6("unsignedByte"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Unsigned byte", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    unsignedInt: new VocabTerm(_NS$6("unsignedInt"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Unsigned int", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    unsignedLong: new VocabTerm(_NS$6("unsignedLong"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Unsigned long", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    unsignedShort: new VocabTerm(_NS$6("unsignedShort"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Unsigned short", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    date: new VocabTerm(_NS$6("date"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Date", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    time: new VocabTerm(_NS$6("time"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Time", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    gYearMonth: new VocabTerm(_NS$6("gYearMonth"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Year month", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    gYear: new VocabTerm(_NS$6("gYear"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Year", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    gMonthDay: new VocabTerm(_NS$6("gMonthDay"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Month day", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    gDay: new VocabTerm(_NS$6("gDay"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Day", "en")
        .addComment(".", "en"),
    /**
     * .
     *
     * Defined by the vocabulary: http://www.w3.org/2001/XMLSchema#
     */
    gMonth: new VocabTerm(_NS$6("gMonth"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$7("http://www.w3.org/2001/XMLSchema#"))
        .addLabel("Month", "en")
        .addComment(".", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$8 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$7(localName) {
    return namedNode$8("http://www.w3.org/2002/07/owl#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var OWL = {
    PREFIX: "owl",
    NAMESPACE: "http://www.w3.org/2002/07/owl#",
    PREFIX_AND_NAMESPACE: { "owl": "http://www.w3.org/2002/07/owl#" },
    NS: _NS$7,
    // *****************
    // All the Classes.
    // *****************
    /**
     * The class of ontologies.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Ontology: new VocabTerm(_NS$7("Ontology"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Ontology")
        .addCommentNoLanguage("The class of ontologies."),
    /**
     * The class of collections of pairwise different individuals.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    AllDifferent: new VocabTerm(_NS$7("AllDifferent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("AllDifferent")
        .addCommentNoLanguage("The class of collections of pairwise different individuals."),
    /**
     * The class of collections of pairwise disjoint classes.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    AllDisjointClasses: new VocabTerm(_NS$7("AllDisjointClasses"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("AllDisjointClasses")
        .addCommentNoLanguage("The class of collections of pairwise disjoint classes."),
    /**
     * The class of collections of pairwise disjoint properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    AllDisjointProperties: new VocabTerm(_NS$7("AllDisjointProperties"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("AllDisjointProperties")
        .addCommentNoLanguage("The class of collections of pairwise disjoint properties."),
    /**
     * The class of annotated annotations for which the RDF serialization consists of an annotated subject, predicate and object.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Annotation: new VocabTerm(_NS$7("Annotation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Annotation")
        .addCommentNoLanguage("The class of annotated annotations for which the RDF serialization consists of an annotated subject, predicate and object."),
    /**
     * The class of annotation properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    AnnotationProperty: new VocabTerm(_NS$7("AnnotationProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("AnnotationProperty")
        .addCommentNoLanguage("The class of annotation properties."),
    /**
     * The class of asymmetric properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    AsymmetricProperty: new VocabTerm(_NS$7("AsymmetricProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("AsymmetricProperty")
        .addCommentNoLanguage("The class of asymmetric properties."),
    /**
     * The class of object properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    ObjectProperty: new VocabTerm(_NS$7("ObjectProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("ObjectProperty")
        .addCommentNoLanguage("The class of object properties."),
    /**
     * The class of annotated axioms for which the RDF serialization consists of an annotated subject, predicate and object.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Axiom: new VocabTerm(_NS$7("Axiom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Axiom")
        .addCommentNoLanguage("The class of annotated axioms for which the RDF serialization consists of an annotated subject, predicate and object."),
    /**
     * The class of OWL classes.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Class: new VocabTerm(_NS$7("Class"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Class")
        .addCommentNoLanguage("The class of OWL classes."),
    /**
     * The class of OWL data ranges, which are special kinds of datatypes. Note: The use of the IRI owl:DataRange has been deprecated as of OWL 2. The IRI rdfs:Datatype SHOULD be used instead.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    DataRange: new VocabTerm(_NS$7("DataRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("DataRange")
        .addCommentNoLanguage("The class of OWL data ranges, which are special kinds of datatypes. Note: The use of the IRI owl:DataRange has been deprecated as of OWL 2. The IRI rdfs:Datatype SHOULD be used instead."),
    /**
     * The class of data properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    DatatypeProperty: new VocabTerm(_NS$7("DatatypeProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("DatatypeProperty")
        .addCommentNoLanguage("The class of data properties."),
    /**
     * The class of deprecated classes.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    DeprecatedClass: new VocabTerm(_NS$7("DeprecatedClass"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("DeprecatedClass")
        .addCommentNoLanguage("The class of deprecated classes."),
    /**
     * The class of deprecated properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    DeprecatedProperty: new VocabTerm(_NS$7("DeprecatedProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("DeprecatedProperty")
        .addCommentNoLanguage("The class of deprecated properties."),
    /**
     * The class of functional properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    FunctionalProperty: new VocabTerm(_NS$7("FunctionalProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("FunctionalProperty")
        .addCommentNoLanguage("The class of functional properties."),
    /**
     * The class of inverse-functional properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    InverseFunctionalProperty: new VocabTerm(_NS$7("InverseFunctionalProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("InverseFunctionalProperty")
        .addCommentNoLanguage("The class of inverse-functional properties."),
    /**
     * The class of irreflexive properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    IrreflexiveProperty: new VocabTerm(_NS$7("IrreflexiveProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("IrreflexiveProperty")
        .addCommentNoLanguage("The class of irreflexive properties."),
    /**
     * The class of named individuals.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    NamedIndividual: new VocabTerm(_NS$7("NamedIndividual"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("NamedIndividual")
        .addCommentNoLanguage("The class of named individuals."),
    /**
     * The class of OWL individuals.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Thing: new VocabTerm(_NS$7("Thing"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Thing")
        .addCommentNoLanguage("The class of OWL individuals."),
    /**
     * The class of negative property assertions.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    NegativePropertyAssertion: new VocabTerm(_NS$7("NegativePropertyAssertion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("NegativePropertyAssertion")
        .addCommentNoLanguage("The class of negative property assertions."),
    /**
     * This is the empty class.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Nothing: new VocabTerm(_NS$7("Nothing"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Nothing")
        .addCommentNoLanguage("This is the empty class."),
    /**
     * The class of ontology properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    OntologyProperty: new VocabTerm(_NS$7("OntologyProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("OntologyProperty")
        .addCommentNoLanguage("The class of ontology properties."),
    /**
     * The class of reflexive properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    ReflexiveProperty: new VocabTerm(_NS$7("ReflexiveProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("ReflexiveProperty")
        .addCommentNoLanguage("The class of reflexive properties."),
    /**
     * The class of property restrictions.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    Restriction: new VocabTerm(_NS$7("Restriction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("Restriction")
        .addCommentNoLanguage("The class of property restrictions."),
    /**
     * The class of symmetric properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    SymmetricProperty: new VocabTerm(_NS$7("SymmetricProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("SymmetricProperty")
        .addCommentNoLanguage("The class of symmetric properties."),
    /**
     * The class of transitive properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    TransitiveProperty: new VocabTerm(_NS$7("TransitiveProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("TransitiveProperty")
        .addCommentNoLanguage("The class of transitive properties."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The annotation property that provides version information for an ontology or another OWL construct.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    versionInfo: new VocabTerm(_NS$7("versionInfo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("versionInfo")
        .addCommentNoLanguage("The annotation property that provides version information for an ontology or another OWL construct."),
    /**
     * The property that determines the class that a universal property restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    allValuesFrom: new VocabTerm(_NS$7("allValuesFrom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("allValuesFrom")
        .addCommentNoLanguage("The property that determines the class that a universal property restriction refers to."),
    /**
     * The property that determines the predicate of an annotated axiom or annotated annotation.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    annotatedProperty: new VocabTerm(_NS$7("annotatedProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("annotatedProperty")
        .addCommentNoLanguage("The property that determines the predicate of an annotated axiom or annotated annotation."),
    /**
     * The property that determines the subject of an annotated axiom or annotated annotation.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    annotatedSource: new VocabTerm(_NS$7("annotatedSource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("annotatedSource")
        .addCommentNoLanguage("The property that determines the subject of an annotated axiom or annotated annotation."),
    /**
     * The property that determines the object of an annotated axiom or annotated annotation.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    annotatedTarget: new VocabTerm(_NS$7("annotatedTarget"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("annotatedTarget")
        .addCommentNoLanguage("The property that determines the object of an annotated axiom or annotated annotation."),
    /**
     * The property that determines the predicate of a negative property assertion.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    assertionProperty: new VocabTerm(_NS$7("assertionProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("assertionProperty")
        .addCommentNoLanguage("The property that determines the predicate of a negative property assertion."),
    /**
     * The annotation property that indicates that a given ontology is backward compatible with another ontology.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    backwardCompatibleWith: new VocabTerm(_NS$7("backwardCompatibleWith"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("backwardCompatibleWith")
        .addCommentNoLanguage("The annotation property that indicates that a given ontology is backward compatible with another ontology."),
    /**
     * The data property that does not relate any individual to any data value.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    bottomDataProperty: new VocabTerm(_NS$7("bottomDataProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("bottomDataProperty")
        .addCommentNoLanguage("The data property that does not relate any individual to any data value."),
    /**
     * The object property that does not relate any two individuals.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    bottomObjectProperty: new VocabTerm(_NS$7("bottomObjectProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("bottomObjectProperty")
        .addCommentNoLanguage("The object property that does not relate any two individuals."),
    /**
     * The property that determines the cardinality of an exact cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    cardinality: new VocabTerm(_NS$7("cardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("cardinality")
        .addCommentNoLanguage("The property that determines the cardinality of an exact cardinality restriction."),
    /**
     * The property that determines that a given class is the complement of another class.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    complementOf: new VocabTerm(_NS$7("complementOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("complementOf")
        .addCommentNoLanguage("The property that determines that a given class is the complement of another class."),
    /**
     * The property that determines that a given data range is the complement of another data range with respect to the data domain.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    datatypeComplementOf: new VocabTerm(_NS$7("datatypeComplementOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("datatypeComplementOf")
        .addCommentNoLanguage("The property that determines that a given data range is the complement of another data range with respect to the data domain."),
    /**
     * The annotation property that indicates that a given entity has been deprecated.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    deprecated: new VocabTerm(_NS$7("deprecated"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("deprecated")
        .addCommentNoLanguage("The annotation property that indicates that a given entity has been deprecated."),
    /**
     * The property that determines that two given individuals are different.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    differentFrom: new VocabTerm(_NS$7("differentFrom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("differentFrom")
        .addCommentNoLanguage("The property that determines that two given individuals are different."),
    /**
     * The property that determines that a given class is equivalent to the disjoint union of a collection of other classes.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    disjointUnionOf: new VocabTerm(_NS$7("disjointUnionOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("disjointUnionOf")
        .addCommentNoLanguage("The property that determines that a given class is equivalent to the disjoint union of a collection of other classes."),
    /**
     * The property that determines that two given classes are disjoint.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    disjointWith: new VocabTerm(_NS$7("disjointWith"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("disjointWith")
        .addCommentNoLanguage("The property that determines that two given classes are disjoint."),
    /**
     * The property that determines the collection of pairwise different individuals in a owl:AllDifferent axiom.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    distinctMembers: new VocabTerm(_NS$7("distinctMembers"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("distinctMembers")
        .addCommentNoLanguage("The property that determines the collection of pairwise different individuals in a owl:AllDifferent axiom."),
    /**
     * The property that determines that two given classes are equivalent, and that is used to specify datatype definitions.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    equivalentClass: new VocabTerm(_NS$7("equivalentClass"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("equivalentClass")
        .addCommentNoLanguage("The property that determines that two given classes are equivalent, and that is used to specify datatype definitions."),
    /**
     * The property that determines that two given properties are equivalent.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    equivalentProperty: new VocabTerm(_NS$7("equivalentProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("equivalentProperty")
        .addCommentNoLanguage("The property that determines that two given properties are equivalent."),
    /**
     * The property that determines the collection of properties that jointly build a key.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    hasKey: new VocabTerm(_NS$7("hasKey"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("hasKey")
        .addCommentNoLanguage("The property that determines the collection of properties that jointly build a key."),
    /**
     * The property that determines the property that a self restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    hasSelf: new VocabTerm(_NS$7("hasSelf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("hasSelf")
        .addCommentNoLanguage("The property that determines the property that a self restriction refers to."),
    /**
     * The property that determines the individual that a has-value restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    hasValue: new VocabTerm(_NS$7("hasValue"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("hasValue")
        .addCommentNoLanguage("The property that determines the individual that a has-value restriction refers to."),
    /**
     * The annotation property that indicates that a given ontology is incompatible with another ontology.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    incompatibleWith: new VocabTerm(_NS$7("incompatibleWith"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("incompatibleWith")
        .addCommentNoLanguage("The annotation property that indicates that a given ontology is incompatible with another ontology."),
    /**
     * The property that determines the collection of classes or data ranges that build an intersection.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    intersectionOf: new VocabTerm(_NS$7("intersectionOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("intersectionOf")
        .addCommentNoLanguage("The property that determines the collection of classes or data ranges that build an intersection."),
    /**
     * The property that determines that two given properties are inverse.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    inverseOf: new VocabTerm(_NS$7("inverseOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("inverseOf")
        .addCommentNoLanguage("The property that determines that two given properties are inverse."),
    /**
     * The property that determines the cardinality of a maximum cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    maxCardinality: new VocabTerm(_NS$7("maxCardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("maxCardinality")
        .addCommentNoLanguage("The property that determines the cardinality of a maximum cardinality restriction."),
    /**
     * The property that determines the cardinality of a maximum qualified cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    maxQualifiedCardinality: new VocabTerm(_NS$7("maxQualifiedCardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("maxQualifiedCardinality")
        .addCommentNoLanguage("The property that determines the cardinality of a maximum qualified cardinality restriction."),
    /**
     * The property that determines the collection of members in either a owl:AllDifferent, owl:AllDisjointClasses or owl:AllDisjointProperties axiom.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    members: new VocabTerm(_NS$7("members"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("members")
        .addCommentNoLanguage("The property that determines the collection of members in either a owl:AllDifferent, owl:AllDisjointClasses or owl:AllDisjointProperties axiom."),
    /**
     * The property that determines the cardinality of a minimum cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    minCardinality: new VocabTerm(_NS$7("minCardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("minCardinality")
        .addCommentNoLanguage("The property that determines the cardinality of a minimum cardinality restriction."),
    /**
     * The property that determines the cardinality of a minimum qualified cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    minQualifiedCardinality: new VocabTerm(_NS$7("minQualifiedCardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("minQualifiedCardinality")
        .addCommentNoLanguage("The property that determines the cardinality of a minimum qualified cardinality restriction."),
    /**
     * The property that determines the class that a qualified object cardinality restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    onClass: new VocabTerm(_NS$7("onClass"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("onClass")
        .addCommentNoLanguage("The property that determines the class that a qualified object cardinality restriction refers to."),
    /**
     * The property that determines the data range that a qualified data cardinality restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    onDataRange: new VocabTerm(_NS$7("onDataRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("onDataRange")
        .addCommentNoLanguage("The property that determines the data range that a qualified data cardinality restriction refers to."),
    /**
     * The property that determines the datatype that a datatype restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    onDatatype: new VocabTerm(_NS$7("onDatatype"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("onDatatype")
        .addCommentNoLanguage("The property that determines the datatype that a datatype restriction refers to."),
    /**
     * The property that determines the collection of individuals or data values that build an enumeration.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    oneOf: new VocabTerm(_NS$7("oneOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("oneOf")
        .addCommentNoLanguage("The property that determines the collection of individuals or data values that build an enumeration."),
    /**
     * The property that determines the n-tuple of properties that a property restriction on an n-ary data range refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    onProperties: new VocabTerm(_NS$7("onProperties"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("onProperties")
        .addCommentNoLanguage("The property that determines the n-tuple of properties that a property restriction on an n-ary data range refers to."),
    /**
     * The property that determines the property that a property restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    onProperty: new VocabTerm(_NS$7("onProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("onProperty")
        .addCommentNoLanguage("The property that determines the property that a property restriction refers to."),
    /**
     * The annotation property that indicates the predecessor ontology of a given ontology.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    priorVersion: new VocabTerm(_NS$7("priorVersion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("priorVersion")
        .addCommentNoLanguage("The annotation property that indicates the predecessor ontology of a given ontology."),
    /**
     * The property that determines the n-tuple of properties that build a sub property chain of a given property.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    propertyChainAxiom: new VocabTerm(_NS$7("propertyChainAxiom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("propertyChainAxiom")
        .addCommentNoLanguage("The property that determines the n-tuple of properties that build a sub property chain of a given property."),
    /**
     * The property that determines that two given properties are disjoint.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    propertyDisjointWith: new VocabTerm(_NS$7("propertyDisjointWith"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("propertyDisjointWith")
        .addCommentNoLanguage("The property that determines that two given properties are disjoint."),
    /**
     * The property that determines the cardinality of an exact qualified cardinality restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    qualifiedCardinality: new VocabTerm(_NS$7("qualifiedCardinality"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("qualifiedCardinality")
        .addCommentNoLanguage("The property that determines the cardinality of an exact qualified cardinality restriction."),
    /**
     * The property that determines that two given individuals are equal.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    sameAs: new VocabTerm(_NS$7("sameAs"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("sameAs")
        .addCommentNoLanguage("The property that determines that two given individuals are equal."),
    /**
     * The property that determines the class that an existential property restriction refers to.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    someValuesFrom: new VocabTerm(_NS$7("someValuesFrom"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("someValuesFrom")
        .addCommentNoLanguage("The property that determines the class that an existential property restriction refers to."),
    /**
     * The property that determines the subject of a negative property assertion.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    sourceIndividual: new VocabTerm(_NS$7("sourceIndividual"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("sourceIndividual")
        .addCommentNoLanguage("The property that determines the subject of a negative property assertion."),
    /**
     * The property that determines the object of a negative object property assertion.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    targetIndividual: new VocabTerm(_NS$7("targetIndividual"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("targetIndividual")
        .addCommentNoLanguage("The property that determines the object of a negative object property assertion."),
    /**
     * The property that determines the value of a negative data property assertion.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    targetValue: new VocabTerm(_NS$7("targetValue"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("targetValue")
        .addCommentNoLanguage("The property that determines the value of a negative data property assertion."),
    /**
     * The data property that relates every individual to every data value.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    topDataProperty: new VocabTerm(_NS$7("topDataProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("topDataProperty")
        .addCommentNoLanguage("The data property that relates every individual to every data value."),
    /**
     * The object property that relates every two individuals.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    topObjectProperty: new VocabTerm(_NS$7("topObjectProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("topObjectProperty")
        .addCommentNoLanguage("The object property that relates every two individuals."),
    /**
     * The property that determines the collection of classes or data ranges that build a union.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    unionOf: new VocabTerm(_NS$7("unionOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("unionOf")
        .addCommentNoLanguage("The property that determines the collection of classes or data ranges that build a union."),
    /**
     * The property that determines the collection of facet-value pairs that define a datatype restriction.
     *
     * Defined by the vocabulary: http://www.w3.org/2002/07/owl#
     */
    withRestrictions: new VocabTerm(_NS$7("withRestrictions"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$8("http://www.w3.org/2002/07/owl#"))
        .addLabelNoLanguage("withRestrictions")
        .addCommentNoLanguage("The property that determines the collection of facet-value pairs that define a datatype restriction."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$9 = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  Vocabulary URIs defined in the Linked Data Platform (LDP) namespace.
 */
function _NS$8(localName) {
    return namedNode$9("http://www.w3.org/ns/ldp#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var LDP = {
    PREFIX: "ldp",
    NAMESPACE: "http://www.w3.org/ns/ldp#",
    PREFIX_AND_NAMESPACE: { "ldp": "http://www.w3.org/ns/ldp#" },
    NS: _NS$8,
    // *****************
    // All the Classes.
    // *****************
    /**
     * A HTTP-addressable resource whose lifecycle is managed by a LDP server.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    Resource: new VocabTerm(_NS$8("Resource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("Resource")
        .addCommentNoLanguage("A HTTP-addressable resource whose lifecycle is managed by a LDP server."),
    /**
     * A Linked Data Platform Resource (LDPR) whose state is represented as RDF.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    RDFSource: new VocabTerm(_NS$8("RDFSource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("RDFSource")
        .addCommentNoLanguage("A Linked Data Platform Resource (LDPR) whose state is represented as RDF."),
    /**
     * A Linked Data Platform Resource (LDPR) whose state is NOT represented as RDF.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    NonRDFSource: new VocabTerm(_NS$8("NonRDFSource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("NonRDFSource")
        .addCommentNoLanguage("A Linked Data Platform Resource (LDPR) whose state is NOT represented as RDF."),
    /**
     * A Linked Data Platform RDF Source (LDP-RS) that also conforms to additional patterns and conventions for managing membership. Readers should refer to the specification defining this ontology for the list of behaviors associated with it.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    Container: new VocabTerm(_NS$8("Container"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("Container")
        .addLabel("Conteneur", "fr")
        .addCommentNoLanguage("A Linked Data Platform RDF Source (LDP-RS) that also conforms to additional patterns and conventions for managing membership. Readers should refer to the specification defining this ontology for the list of behaviors associated with it.")
        .addComment("Une source RDF de plateforme de donn\u00E9es li\u00E9es (LDP-RS) qui\n se conforme \u00E9galement \u00E0 des mod\u00E8les et des conventions suppl\u00E9mentaires pour la\n gestion de l'adh\u00E9sion. Les lecteurs doivent se r\u00E9f\u00E9rer \u00E0 la sp\u00E9cification\n d\u00E9finissant cette ontologie pour la liste des comportements qui lui sont\n associ\u00E9s.", "fr"),
    /**
     * An LDPC that uses a predefined predicate to simply link to its contained resources.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    BasicContainer: new VocabTerm(_NS$8("BasicContainer"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("BasicContainer")
        .addLabel("Basic container", "en")
        .addLabel("Conteneur de base", "fr")
        .addCommentNoLanguage("An LDPC that uses a predefined predicate to simply link to its contained resources.")
        .addComment("Un LDPC qui utilise un pr\u00E9dicat pr\u00E9d\u00E9fini pour simplement se lier aux ressources qu'il contient.", "fr"),
    /**
     * An LDPC that is similar to a LDP-DC but it allows an indirection with the ability to list as member a resource, such as a URI representing a real-world object, that is different from the resource that is created.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    DirectContainer: new VocabTerm(_NS$8("DirectContainer"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("DirectContainer")
        .addCommentNoLanguage("An LDPC that is similar to a LDP-DC but it allows an indirection with the ability to list as member a resource, such as a URI representing a real-world object, that is different from the resource that is created."),
    /**
     * An LDPC that has the flexibility of choosing what form the membership triples take.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    IndirectContainer: new VocabTerm(_NS$8("IndirectContainer"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("IndirectContainer")
        .addCommentNoLanguage("An LDPC that has the flexibility of choosing what form the membership triples take."),
    /**
     * URI signifying that the resource is an in-sequence page resource, as defined by LDP Paging.  Typically used on Link rel='type' response headers.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    Page: new VocabTerm(_NS$8("Page"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("Page")
        .addCommentNoLanguage("URI signifying that the resource is an in-sequence page resource, as defined by LDP Paging.  Typically used on Link rel='type' response headers."),
    /**
     * Element in the list of sorting criteria used by the server to assign container members to pages.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    PageSortCriterion: new VocabTerm(_NS$8("PageSortCriterion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("PageSortCriterion")
        .addCommentNoLanguage("Element in the list of sorting criteria used by the server to assign container members to pages."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * Indicates which predicate is used in membership triples, and that the membership triple pattern is < membership-constant-URI , object-of-hasMemberRelation, member-URI >.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    hasMemberRelation: new VocabTerm(_NS$8("hasMemberRelation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("hasMemberRelation")
        .addCommentNoLanguage("Indicates which predicate is used in membership triples, and that the membership triple pattern is < membership-constant-URI , object-of-hasMemberRelation, member-URI >."),
    /**
     * Indicates which predicate is used in membership triples, and that the membership triple pattern is < member-URI , object-of-isMemberOfRelation, membership-constant-URI >.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    isMemberOfRelation: new VocabTerm(_NS$8("isMemberOfRelation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("isMemmberOfRelation")
        .addCommentNoLanguage("Indicates which predicate is used in membership triples, and that the membership triple pattern is < member-URI , object-of-isMemberOfRelation, membership-constant-URI >."),
    /**
     * Indicates the membership-constant-URI in a membership triple.  Depending upon the membership triple pattern a container uses, as indicated by the presence of ldp:hasMemberRelation or ldp:isMemberOfRelation, the membership-constant-URI might occupy either the subject or object position in membership triples.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    membershipResource: new VocabTerm(_NS$8("membershipResource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("membershipResource")
        .addCommentNoLanguage("Indicates the membership-constant-URI in a membership triple.  Depending upon the membership triple pattern a container uses, as indicated by the presence of ldp:hasMemberRelation or ldp:isMemberOfRelation, the membership-constant-URI might occupy either the subject or object position in membership triples."),
    /**
     * Indicates which triple in a creation request should be used as the member-URI value in the membership triple added when the creation request is successful.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    insertedContentRelation: new VocabTerm(_NS$8("insertedContentRelation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("insertedContentRelation")
        .addCommentNoLanguage("Indicates which triple in a creation request should be used as the member-URI value in the membership triple added when the creation request is successful."),
    /**
     * LDP servers should use this predicate as the membership predicate if there is no obvious predicate from an application vocabulary to use.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    member: new VocabTerm(_NS$8("member"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("member")
        .addCommentNoLanguage("LDP servers should use this predicate as the membership predicate if there is no obvious predicate from an application vocabulary to use."),
    /**
     * Links a container with resources created through the container.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    contains: new VocabTerm(_NS$8("contains"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("contains")
        .addCommentNoLanguage("Links a container with resources created through the container."),
    /**
     * Links a resource with constraints that the server requires requests like creation and update to conform to.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    constrainedBy: new VocabTerm(_NS$8("constrainedBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("constrainedBy")
        .addCommentNoLanguage("Links a resource with constraints that the server requires requests like creation and update to conform to."),
    /**
     * Link to the list of sorting criteria used by the server in a representation.  Typically used on Link response headers as an extension link relation URI in the rel= parameter.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    pageSortCriteria: new VocabTerm(_NS$8("pageSortCriteria"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("pageSortCriteria")
        .addCommentNoLanguage("Link to the list of sorting criteria used by the server in a representation.  Typically used on Link response headers as an extension link relation URI in the rel= parameter."),
    /**
     * Predicate used to specify the order of the members across a page sequence's in-sequence page resources; it asserts nothing about the order of members in the representation of a single page.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    pageSortPredicate: new VocabTerm(_NS$8("pageSortPredicate"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("pageSortPredicate")
        .addCommentNoLanguage("Predicate used to specify the order of the members across a page sequence's in-sequence page resources; it asserts nothing about the order of members in the representation of a single page."),
    /**
     * The ascending/descending/etc order used to order the members across pages in a page sequence.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    pageSortOrder: new VocabTerm(_NS$8("pageSortOrder"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("pageSortOrder")
        .addCommentNoLanguage("The ascending/descending/etc order used to order the members across pages in a page sequence."),
    /**
     * The collation used to order the members across pages in a page sequence when comparing strings.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    pageSortCollation: new VocabTerm(_NS$8("pageSortCollation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("pageSortCollation")
        .addCommentNoLanguage("The collation used to order the members across pages in a page sequence when comparing strings."),
    /**
     * Link to a page sequence resource, as defined by LDP Paging.  Typically used to communicate the sorting criteria used to allocate LDPC members to pages.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/ldp#
     */
    pageSequence: new VocabTerm(_NS$8("pageSequence"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("http://www.w3.org/ns/ldp#"))
        .addLabelNoLanguage("Page")
        .addCommentNoLanguage("Link to a page sequence resource, as defined by LDP Paging.  Typically used to communicate the sorting criteria used to allocate LDPC members to pages."),
    /**
     * Links a resource to a container where notifications for the resource can be created and discovered.
     *
     * Defined by the vocabulary: https://www.w3.org/TR/ldn/
     */
    inbox: new VocabTerm(_NS$8("inbox"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$9("https://www.w3.org/TR/ldn/"))
        .addLabelNoLanguage("inbox")
        .addCommentNoLanguage("Links a resource to a container where notifications for the resource can be created and discovered."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$a = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$9(localName) {
    return namedNode$a("http://www.w3.org/2011/http#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var HTTP = {
    PREFIX: "http",
    NAMESPACE: "http://www.w3.org/2011/http#",
    PREFIX_AND_NAMESPACE: { "http": "http://www.w3.org/2011/http#" },
    NS: _NS$9,
    // *****************
    // All the Classes.
    // *****************
    /**
     * A connection used for HTTP transfer.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    Connection: new VocabTerm(_NS$9("Connection"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Connection", "en")
        .addComment("A connection used for HTTP transfer.", "en"),
    /**
     * An entity header in an HTTP message.
     */
    EntityHeader: new VocabTerm(_NS$9("EntityHeader"), dataModel, getLocalStore(), false)
        .addLabel("Entity Header", "en")
        .addComment("An entity header in an HTTP message.", "en"),
    /**
     * A header in an HTTP message.
     */
    MessageHeader: new VocabTerm(_NS$9("MessageHeader"), dataModel, getLocalStore(), false)
        .addLabel("Message Header", "en")
        .addComment("A header in an HTTP message.", "en"),
    /**
     * A general header in an HTTP message.
     */
    GeneralHeader: new VocabTerm(_NS$9("GeneralHeader"), dataModel, getLocalStore(), false)
        .addLabel("General Header", "en")
        .addComment("A general header in an HTTP message.", "en"),
    /**
     * A part of a deconstructed header value.
     */
    HeaderElement: new VocabTerm(_NS$9("HeaderElement"), dataModel, getLocalStore(), false)
        .addLabel("Header Element", "en")
        .addComment("A part of a deconstructed header value.", "en"),
    /**
     * A header name.
     */
    HeaderName: new VocabTerm(_NS$9("HeaderName"), dataModel, getLocalStore(), false)
        .addLabel("Header Name", "en")
        .addComment("A header name.", "en"),
    /**
     * An HTTP message.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    Message: new VocabTerm(_NS$9("Message"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Message", "en")
        .addComment("An HTTP message.", "en"),
    /**
     * The HTTP method used for the request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    Method: new VocabTerm(_NS$9("Method"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Method", "en")
        .addComment("The HTTP method used for the request.", "en"),
    /**
     * A parameter for a part of a header value.
     */
    Parameter: new VocabTerm(_NS$9("Parameter"), dataModel, getLocalStore(), false)
        .addLabel("Parameter", "en")
        .addComment("A parameter for a part of a header value.", "en"),
    /**
     * An HTTP request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    Request: new VocabTerm(_NS$9("Request"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Request", "en")
        .addComment("An HTTP request.", "en"),
    /**
     * A header in an HTTP request message.
     */
    RequestHeader: new VocabTerm(_NS$9("RequestHeader"), dataModel, getLocalStore(), false)
        .addLabel("Request Header", "en")
        .addComment("A header in an HTTP request message.", "en"),
    /**
     * An HTTP response.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    Response: new VocabTerm(_NS$9("Response"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Response", "en")
        .addComment("An HTTP response.", "en"),
    /**
     * A header in an HTTP response message.
     */
    ResponseHeader: new VocabTerm(_NS$9("ResponseHeader"), dataModel, getLocalStore(), false)
        .addLabel("Response Header", "en")
        .addComment("A header in an HTTP response message.", "en"),
    /**
     * The status code of an HTTP response.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    StatusCode: new VocabTerm(_NS$9("StatusCode"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Status code", "en")
        .addComment("The status code of an HTTP response.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The absolute request URI.
     */
    absoluteURI: new VocabTerm(_NS$9("absoluteURI"), dataModel, getLocalStore(), false)
        .addLabel("Absolute URI", "en")
        .addComment("The absolute request URI.", "en"),
    /**
     * The request URI of an HTTP request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    requestURI: new VocabTerm(_NS$9("requestURI"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Request URI", "en")
        .addComment("The request URI of an HTTP request.", "en"),
    /**
     * The absolute path sort of request URI.
     */
    absolutePath: new VocabTerm(_NS$9("absolutePath"), dataModel, getLocalStore(), false)
        .addLabel("Absolute path", "en")
        .addComment("The absolute path sort of request URI.", "en"),
    /**
     * The authority sort of request URI.
     */
    authority: new VocabTerm(_NS$9("authority"), dataModel, getLocalStore(), false)
        .addLabel("Authority", "en")
        .addComment("The authority sort of request URI.", "en"),
    /**
     * The entity body of an HTTP message.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    body: new VocabTerm(_NS$9("body"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Entity Body", "en")
        .addComment("The entity body of an HTTP message.", "en"),
    /**
     * The authority of a connection used for the HTTP transfer.
     */
    connectionAuthority: new VocabTerm(_NS$9("connectionAuthority"), dataModel, getLocalStore(), false)
        .addLabel("Connection authority", "en")
        .addComment("The authority of a connection used for the HTTP transfer.", "en"),
    /**
     * The name of a header element.
     */
    elementName: new VocabTerm(_NS$9("elementName"), dataModel, getLocalStore(), false)
        .addLabel("Header element name", "en")
        .addComment("The name of a header element.", "en"),
    /**
     * The value of a header element.
     */
    elementValue: new VocabTerm(_NS$9("elementValue"), dataModel, getLocalStore(), false)
        .addLabel("Header element value", "en")
        .addComment("The value of a header element.", "en"),
    /**
     * The name of an HTTP header field.
     */
    fieldName: new VocabTerm(_NS$9("fieldName"), dataModel, getLocalStore(), false)
        .addLabel("Field name", "en")
        .addComment("The name of an HTTP header field.", "en"),
    /**
     * The value of an HTTP header field.
     */
    fieldValue: new VocabTerm(_NS$9("fieldValue"), dataModel, getLocalStore(), false)
        .addLabel("Field value", "en")
        .addComment("The value of an HTTP header field.", "en"),
    /**
     * The deconstructed parts of an HTTP header value.
     */
    headerElements: new VocabTerm(_NS$9("headerElements"), dataModel, getLocalStore(), false)
        .addLabel("Header elements", "en")
        .addComment("The deconstructed parts of an HTTP header value.", "en"),
    /**
     * The name of an HTTP header.
     */
    hdrName: new VocabTerm(_NS$9("hdrName"), dataModel, getLocalStore(), false)
        .addLabel("Header name", "en")
        .addComment("The name of an HTTP header.", "en"),
    /**
     * The headers in an HTTP message.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    headers: new VocabTerm(_NS$9("headers"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Headers", "en")
        .addComment("The headers in an HTTP message.", "en"),
    /**
     * The HTTP version of an HTTP message.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    httpVersion: new VocabTerm(_NS$9("httpVersion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("HTTP version", "en")
        .addComment("The HTTP version of an HTTP message.", "en"),
    /**
     * The HTTP method used for the HTTP request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    mthd: new VocabTerm(_NS$9("mthd"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Method", "en")
        .addComment("The HTTP method used for the HTTP request.", "en"),
    /**
     * The HTTP method name used for the HTTP request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    methodName: new VocabTerm(_NS$9("methodName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Method name", "en")
        .addComment("The HTTP method name used for the HTTP request.", "en"),
    /**
     * The name of a parameter in a part of a deconstructed HTTP header value.
     */
    paramName: new VocabTerm(_NS$9("paramName"), dataModel, getLocalStore(), false)
        .addLabel("Parameter name", "en")
        .addComment("The name of a parameter in a part of a deconstructed HTTP header value.", "en"),
    /**
     * The parameters in a part of a deconstructed HTTP header value.
     */
    params: new VocabTerm(_NS$9("params"), dataModel, getLocalStore(), false)
        .addLabel("Header parameters", "en")
        .addComment("The parameters in a part of a deconstructed HTTP header value.", "en"),
    /**
     * The value of a parameter in a part of a deconstructed HTTP header value.
     */
    paramValue: new VocabTerm(_NS$9("paramValue"), dataModel, getLocalStore(), false)
        .addLabel("Parameter value", "en")
        .addComment("The value of a parameter in a part of a deconstructed HTTP header value.", "en"),
    /**
     * The reason phrase (status text) of an HTTP response.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    reasonPhrase: new VocabTerm(_NS$9("reasonPhrase"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Reason phrase", "en")
        .addComment("The reason phrase (status text) of an HTTP response.", "en"),
    /**
     * The HTTP requests made via a connection.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    requests: new VocabTerm(_NS$9("requests"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Requests", "en")
        .addComment("The HTTP requests made via a connection.", "en"),
    /**
     * The HTTP response sent in answer to an HTTP request.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    resp: new VocabTerm(_NS$9("resp"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Response", "en")
        .addComment("The HTTP response sent in answer to an HTTP request.", "en"),
    /**
     * The status code of an HTTP response.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    sc: new VocabTerm(_NS$9("sc"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Status code", "en")
        .addComment("The status code of an HTTP response.", "en"),
    /**
     * The status code number.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    statusCodeNumber: new VocabTerm(_NS$9("statusCodeNumber"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Status code", "en")
        .addComment("The status code number.", "en"),
    /**
     * The status code value of an HTTP response.
     *
     * Defined by the vocabulary: http://www.ietf.org/rfc/rfc2616.txt
     */
    statusCodeValue: new VocabTerm(_NS$9("statusCodeValue"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$a("http://www.ietf.org/rfc/rfc2616.txt"))
        .addLabel("Status code", "en")
        .addComment("The status code value of an HTTP response.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$b = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$a(localName) {
    return namedNode$b("http://www.w3.org/2007/ont/httph#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var HTTPH = {
    PREFIX: "httph",
    NAMESPACE: "http://www.w3.org/2007/ont/httph#",
    PREFIX_AND_NAMESPACE: { "httph": "http://www.w3.org/2007/ont/httph#" },
    NS: _NS$a,
    // *******************
    // All the Properties.
    // *******************
    /**
     * See the HTTP specification
     *
     * Defined by the vocabulary: http://www.w3.org/2007/ont/httph
     */
    content_type: new VocabTerm(_NS$a("content-type"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$b("http://www.w3.org/2007/ont/httph"))
        .addLabelNoLanguage("content type")
        .addCommentNoLanguage("See the HTTP specification"),
    /**
     * See the HTTP specification
     *
     * Defined by the vocabulary: http://www.w3.org/2007/ont/httph#
     */
    accept: new VocabTerm(_NS$a("accept"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$b("http://www.w3.org/2007/ont/httph#"))
        .addLabel("Accept", "en")
        .addComment("See the HTTP specification", "en"),
    /**
     * See the HTTP specification
     *
     * Defined by the vocabulary: http://www.w3.org/2007/ont/httph#
     */
    accept_language: new VocabTerm(_NS$a("accept-language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$b("http://www.w3.org/2007/ont/httph#"))
        .addLabel("Accept-Language", "en")
        .addComment("See the HTTP specification", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$c = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$b(localName) {
    return namedNode$c("http://purl.org/ontology/olo/core#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var OLO = {
    PREFIX: "olo",
    NAMESPACE: "http://purl.org/ontology/olo/core#",
    PREFIX_AND_NAMESPACE: { "olo": "http://purl.org/ontology/olo/core#" },
    NS: _NS$b,
    // *****************
    // All the Classes.
    // *****************
    /**
     * An ordered list with a given length an indexed items.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    OrderedList: new VocabTerm(_NS$b("OrderedList"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("Ordered List", "en")
        .addComment("An ordered list with a given length an indexed items.", "en"),
    /**
     * A slot in an ordered list with a fixed index.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    Slot: new VocabTerm(_NS$b("Slot"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("Slot", "en")
        .addComment("A slot in an ordered list with a fixed index.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The length of an ordered list.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    length: new VocabTerm(_NS$b("length"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has length", "en")
        .addComment("The length of an ordered list.", "en"),
    /**
     * Associates the next slot in an ordered list.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    next: new VocabTerm(_NS$b("next"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has next", "en")
        .addComment("Associates the next slot in an ordered list.", "en"),
    /**
     * An index of a slot in an ordered list.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    index: new VocabTerm(_NS$b("index"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has index", "en")
        .addComment("An index of a slot in an ordered list.", "en"),
    /**
     * An ordered list of an slot.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    ordered_list: new VocabTerm(_NS$b("ordered_list"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has ordered list", "en")
        .addComment("An ordered list of an slot.", "en"),
    /**
     * Associates the previous slot in an ordered list
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    previous: new VocabTerm(_NS$b("previous"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has previous", "en")
        .addComment("Associates the previous slot in an ordered list", "en"),
    /**
     * An item of a slot in an ordered list.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    item: new VocabTerm(_NS$b("item"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has item", "en")
        .addComment("An item of a slot in an ordered list.", "en"),
    /**
     * A slot in an ordered list.
     *
     * Defined by the vocabulary: http://purl.org/ontology/olo/core#
     */
    slot: new VocabTerm(_NS$b("slot"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$c("http://purl.org/ontology/olo/core#"))
        .addLabel("has slot", "en")
        .addComment("A slot in an ordered list.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$d = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  An RDF vocabulary for describing the basic structure and content of concept schemes such as thesauri, classification schemes, subject heading lists, taxonomies, 'folksonomies', other types of controlled vocabulary, and also concept schemes embedded in glossaries and terminologies.
 */
function _NS$c(localName) {
    return namedNode$d("http://www.w3.org/2004/02/skos/core#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var SKOS = {
    PREFIX: "skos",
    NAMESPACE: "http://www.w3.org/2004/02/skos/core#",
    PREFIX_AND_NAMESPACE: { "skos": "http://www.w3.org/2004/02/skos/core#" },
    NS: _NS$c,
    // *****************
    // All the Classes.
    // *****************
    /**
     * An idea or notion; a unit of thought.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    Concept: new VocabTerm(_NS$c("Concept"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("Concept", "en"),
    /**
     * A set of concepts, optionally including statements about semantic relationships between those concepts.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    ConceptScheme: new VocabTerm(_NS$c("ConceptScheme"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("Concept Scheme", "en"),
    /**
     * A meaningful collection of concepts.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    Collection: new VocabTerm(_NS$c("Collection"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("Collection", "en"),
    /**
     * An ordered collection of concepts, where both the grouping and the ordering are meaningful.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    OrderedCollection: new VocabTerm(_NS$c("OrderedCollection"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("Ordered Collection", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * A statement or formal explanation of the meaning of a concept.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    definition: new VocabTerm(_NS$c("definition"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("definition", "en"),
    /**
     * A note that helps to clarify the meaning and/or the use of a concept.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    scopeNote: new VocabTerm(_NS$c("scopeNote"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("scope note", "en"),
    /**
     * An example of the use of a concept.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    example: new VocabTerm(_NS$c("example"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("example", "en"),
    /**
     * Relates a resource (for example a concept) to a concept scheme in which it is included.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    inScheme: new VocabTerm(_NS$c("inScheme"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("is in scheme", "en"),
    /**
     * Relates, by convention, a concept scheme to a concept which is topmost in the broader/narrower concept hierarchies for that scheme, providing an entry point to these hierarchies.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    hasTopConcept: new VocabTerm(_NS$c("hasTopConcept"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has top concept", "en"),
    /**
     * Relates a concept to the concept scheme that it is a top level concept of.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    topConceptOf: new VocabTerm(_NS$c("topConceptOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("is top concept in scheme", "en"),
    /**
     * A resource has no more than one value of skos:prefLabel per language tag, and no more than one value of skos:prefLabel without language tag.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    prefLabel: new VocabTerm(_NS$c("prefLabel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("preferred label", "en")
        .addComment("A resource has no more than one value of skos:prefLabel per language tag, and no more than one value of skos:prefLabel without language tag.", "en"),
    /**
     * The range of skos:altLabel is the class of RDF plain literals.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    altLabel: new VocabTerm(_NS$c("altLabel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("alternative label", "en")
        .addComment("The range of skos:altLabel is the class of RDF plain literals.", "en"),
    /**
     * skos:prefLabel, skos:altLabel and skos:hiddenLabel are pairwise disjoint properties.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    hiddenLabel: new VocabTerm(_NS$c("hiddenLabel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("hidden label", "en")
        .addComment("skos:prefLabel, skos:altLabel and skos:hiddenLabel are pairwise disjoint properties.", "en"),
    /**
     * A notation, also known as classification code, is a string of characters such as "T58.5" or "303.4833" used to uniquely identify a concept within the scope of a given concept scheme.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    notation: new VocabTerm(_NS$c("notation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("notation", "en"),
    /**
     * A general note, for any purpose.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    note: new VocabTerm(_NS$c("note"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("note", "en"),
    /**
     * A note about a modification to a concept.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    changeNote: new VocabTerm(_NS$c("changeNote"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("change note", "en"),
    /**
     * A note for an editor, translator or maintainer of the vocabulary.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    editorialNote: new VocabTerm(_NS$c("editorialNote"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("editorial note", "en"),
    /**
     * A note about the past state/use/meaning of a concept.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    historyNote: new VocabTerm(_NS$c("historyNote"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("history note", "en"),
    /**
     * Links a concept to a concept related by meaning.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    semanticRelation: new VocabTerm(_NS$c("semanticRelation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("is in semantic relation with", "en"),
    /**
     * Broader concepts are typically rendered as parents in a concept hierarchy (tree).
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    broader: new VocabTerm(_NS$c("broader"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has broader", "en")
        .addComment("Broader concepts are typically rendered as parents in a concept hierarchy (tree).", "en"),
    /**
     * skos:broaderTransitive is a transitive superproperty of skos:broader.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    broaderTransitive: new VocabTerm(_NS$c("broaderTransitive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has broader transitive", "en"),
    /**
     * Narrower concepts are typically rendered as children in a concept hierarchy (tree).
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    narrower: new VocabTerm(_NS$c("narrower"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has narrower", "en")
        .addComment("Narrower concepts are typically rendered as children in a concept hierarchy (tree).", "en"),
    /**
     * skos:narrowerTransitive is a transitive superproperty of skos:narrower.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    narrowerTransitive: new VocabTerm(_NS$c("narrowerTransitive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has narrower transitive", "en"),
    /**
     * skos:related is disjoint with skos:broaderTransitive
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    related: new VocabTerm(_NS$c("related"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has related", "en")
        .addComment("skos:related is disjoint with skos:broaderTransitive", "en"),
    /**
     * Relates a collection to one of its members.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    member: new VocabTerm(_NS$c("member"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has member", "en"),
    /**
     * For any resource, every item in the list given as the value of the
        skos:memberList property is also a value of the skos:member property.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    memberList: new VocabTerm(_NS$c("memberList"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has member list", "en")
        .addComment("For any resource, every item in the list given as the value of the\n      skos:memberList property is also a value of the skos:member property.", "en"),
    /**
     * These concept mapping relations mirror semantic relations, and the data model defined below is similar (with the exception of skos:exactMatch) to the data model defined for semantic relations. A distinct vocabulary is provided for concept mapping relations, to provide a convenient way to differentiate links within a concept scheme from links between concept schemes. However, this pattern of usage is not a formal requirement of the SKOS data model, and relies on informal definitions of best practice.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    mappingRelation: new VocabTerm(_NS$c("mappingRelation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("is in mapping relation with", "en")
        .addComment("These concept mapping relations mirror semantic relations, and the data model defined below is similar (with the exception of skos:exactMatch) to the data model defined for semantic relations. A distinct vocabulary is provided for concept mapping relations, to provide a convenient way to differentiate links within a concept scheme from links between concept schemes. However, this pattern of usage is not a formal requirement of the SKOS data model, and relies on informal definitions of best practice.", "en"),
    /**
     * skos:broadMatch is used to state a hierarchical mapping link between two conceptual resources in different concept schemes.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    broadMatch: new VocabTerm(_NS$c("broadMatch"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has broader match", "en"),
    /**
     * skos:narrowMatch is used to state a hierarchical mapping link between two conceptual resources in different concept schemes.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    narrowMatch: new VocabTerm(_NS$c("narrowMatch"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has narrower match", "en"),
    /**
     * skos:relatedMatch is used to state an associative mapping link between two conceptual resources in different concept schemes.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    relatedMatch: new VocabTerm(_NS$c("relatedMatch"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has related match", "en"),
    /**
     * skos:exactMatch is disjoint with each of the properties skos:broadMatch and skos:relatedMatch.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    exactMatch: new VocabTerm(_NS$c("exactMatch"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has exact match", "en")
        .addComment("skos:exactMatch is disjoint with each of the properties skos:broadMatch and skos:relatedMatch.", "en"),
    /**
     * skos:closeMatch is used to link two concepts that are sufficiently similar that they can be used interchangeably in some information retrieval applications. In order to avoid the possibility of "compound errors" when combining mappings across more than two concept schemes, skos:closeMatch is not declared to be a transitive property.
     *
     * Defined by the vocabulary: http://www.w3.org/2004/02/skos/core
     */
    closeMatch: new VocabTerm(_NS$c("closeMatch"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$d("http://www.w3.org/2004/02/skos/core"))
        .addLabel("has close match", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$e = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  Extension to UI terms providing multilingual alternative names and translations for comments (e.g. for use directly as labels or tool-tips in user interfaces or error messages)
 */
function _NS$d(localName) {
    return namedNode$e("http://www.w3.org/ns/ui#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var UI = {
    PREFIX: "ui",
    NAMESPACE: "http://www.w3.org/ns/ui#",
    PREFIX_AND_NAMESPACE: { "ui": "http://www.w3.org/ns/ui#" },
    NS: _NS$d,
    // *****************
    // All the Classes.
    // *****************
    /**
     *
     */
    BooleanField: new VocabTerm(_NS$d("BooleanField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    NumericField: new VocabTerm(_NS$d("NumericField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    ValueField: new VocabTerm(_NS$d("ValueField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    Group: new VocabTerm(_NS$d("Group"), dataModel, getLocalStore(), false),
    /**
     * A form can be any type of single field, or typically a Group of several fields,
      including interspersed headings and comments.
     */
    Form: new VocabTerm(_NS$d("Form"), dataModel, getLocalStore(), false)
        .addCommentNoLanguage("A form can be any type of single field, or typically a Group of several fields,\n    including interspersed headings and comments.  "),
    /**
     *
     */
    Single: new VocabTerm(_NS$d("Single"), dataModel, getLocalStore(), false),
    /**
     *
     */
    Choice: new VocabTerm(_NS$d("Choice"), dataModel, getLocalStore(), false),
    /**
     * A classifier allows the user to select the type of an object.
      The possible types must be subclasses of some overall class, the "category".
      (Ideally, the superclass is also set up as the disjoint union of the subclasses,
      if they are disjoint.)
  
      The form normally stores the resulting classes using an rdf:type triple,
      but a different predicate can be used if required, so the classifier field
      needs is 'property' defined too.
  
      If the subclass selected itself is has subclasses defined, the user can
      recursively select from them in turn, as many levels as needed.
     */
    Classifier: new VocabTerm(_NS$d("Classifier"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("classifier")
        .addCommentNoLanguage("A classifier allows the user to select the type of an object.\n    The possible types must be subclasses of some overall class, the \"category\".\n    (Ideally, the superclass is also set up as the disjoint union of the subclasses,\n    if they are disjoint.)\n\n    The form normally stores the resulting classes using an rdf:type triple,\n    but a different predicate can be used if required, so the classifier field\n    needs is 'property' defined too.\n\n    If the subclass selected itself is has subclasses defined, the user can\n    recursively select from them in turn, as many levels as needed."),
    /**
     *
     */
    ColorField: new VocabTerm(_NS$d("ColorField"), dataModel, getLocalStore(), false),
    /**
     * Comment
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    Comment: new VocabTerm(_NS$d("Comment"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Comment", "en")
        .addLabel("Comentario", "es"),
    /**
     *
     */
    DateField: new VocabTerm(_NS$d("DateField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    DateTimeField: new VocabTerm(_NS$d("DateTimeField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    DecimalField: new VocabTerm(_NS$d("DecimalField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    EmailField: new VocabTerm(_NS$d("EmailField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    Heading: new VocabTerm(_NS$d("Heading"), dataModel, getLocalStore(), false),
    /**
     *
     */
    SingleLineTextField: new VocabTerm(_NS$d("SingleLineTextField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    TextField: new VocabTerm(_NS$d("TextField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    Options: new VocabTerm(_NS$d("Options"), dataModel, getLocalStore(), false),
    /**
     *
     */
    IntegerField: new VocabTerm(_NS$d("IntegerField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    FloatField: new VocabTerm(_NS$d("FloatField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    PhoneField: new VocabTerm(_NS$d("PhoneField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    Multiple: new VocabTerm(_NS$d("Multiple"), dataModel, getLocalStore(), false),
    /**
     *
     */
    MultiLineTextField: new VocabTerm(_NS$d("MultiLineTextField"), dataModel, getLocalStore(), false),
    /**
     *
     */
    TriStateField: new VocabTerm(_NS$d("TriStateField"), dataModel, getLocalStore(), false),
    // *******************
    // All the Properties.
    // *******************
    /**
     * parts
     */
    parts: new VocabTerm(_NS$d("parts"), dataModel, getLocalStore(), false)
        .addLabel("parts", "en"),
    /**
     * from
     */
    from: new VocabTerm(_NS$d("from"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("from"),
    /**
     * Label
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    label: new VocabTerm(_NS$d("label"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Label", "en")
        .addLabel("Etiqueta", "es"),
    /**
     * Many fields prompt for information about a given property of the subject.
      When field is filled in, this gives which property is written into the data.
     */
    property: new VocabTerm(_NS$d("property"), dataModel, getLocalStore(), false)
        .addLabel("property to be stored", "en")
        .addCommentNoLanguage("Many fields prompt for information about a given property of the subject.\n    When field is filled in, this gives which property is written into the data."),
    /**
     * The value for which this case is selected.
     */
    for: new VocabTerm(_NS$d("for"), dataModel, getLocalStore(), false)
        .addLabel("for", "en")
        .addCommentNoLanguage("The value for which this case is selected."),
    /**
     * A string for the UI to use if the user needs a longer
          prompts than just a field name, the rdfs:label.
     */
    prompt: new VocabTerm(_NS$d("prompt"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("user prompt")
        .addCommentNoLanguage("A string for the UI to use if the user needs a longer\n        prompts than just a field name, the rdfs:label. "),
    /**
     *
     */
    use: new VocabTerm(_NS$d("use"), dataModel, getLocalStore(), false),
    /**
     * A form which may be used to collect information about a
      hitherto locally undocumented instance instance of this class.
     */
    creationForm: new VocabTerm(_NS$d("creationForm"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("creation form")
        .addCommentNoLanguage("A form which may be used to collect information about a\n    hitherto locally undocumented instance instance of this class."),
    /**
     * Contents
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    contents: new VocabTerm(_NS$d("contents"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Contents", "en")
        .addLabel("Contenido", "es"),
    /**
     * size of field
     */
    size: new VocabTerm(_NS$d("size"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("size of field"),
    /**
     * Must be a valid CSS style string such as one could put in
          an HTML style attribute.  Depending on the user interface system, this can
          by given to individuals, classes or properties. It is up to a user interface
          which wants to draw on them to pick how it uses styles from which parts
          of the data it has.  For example, the style of a class may be picked
          to distinguish information about things in that class.
     */
    style: new VocabTerm(_NS$d("style"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("style")
        .addCommentNoLanguage("Must be a valid CSS style string such as one could put in\n        an HTML style attribute.  Depending on the user interface system, this can\n        by given to individuals, classes or properties. It is up to a user interface\n        which wants to draw on them to pick how it uses styles from which parts\n        of the data it has.  For example, the style of a class may be picked\n        to distinguish information about things in that class."),
    /**
     * The superclass subclasses of which will be selected.
     */
    category: new VocabTerm(_NS$d("category"), dataModel, getLocalStore(), false)
        .addLabel("overall superclass", "en")
        .addCommentNoLanguage("The superclass subclasses of which will be selected."),
    /**
     * Many fields prompt for information about a given property of the subject
     */
    dependingOn: new VocabTerm(_NS$d("dependingOn"), dataModel, getLocalStore(), false)
        .addLabel("depending on", "en")
        .addCommentNoLanguage("Many fields prompt for information about a given property of the subject"),
    /**
     * Max length
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    maxLength: new VocabTerm(_NS$d("maxLength"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabelNoLanguage("max length of value")
        .addLabel("Max length", "en")
        .addLabel("Longitud m\u00E1xima", "es"),
    /**
     * part
     */
    part: new VocabTerm(_NS$d("part"), dataModel, getLocalStore(), false)
        .addLabel("part", "en"),
    /**
     * A form which may be used to add more infromation to an
  instance of this class which we know something about.  Anything from
  adding just add one more fact, to adding a whole lot of information about a specific
  facet of the thing.
  
     */
    annotationForm: new VocabTerm(_NS$d("annotationForm"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("annotation form")
        .addCommentNoLanguage("A form which may be used to add more infromation to an\ninstance of this class which we know something about.  Anything from\nadding just add one more fact, to adding a whole lot of information about a specific\nfacet of the thing.\n"),
    /**
     * Must be a valid CSS color string such as one could put in
          an HTML style attribute.  This should be in the #xxxxxx form,
          (with 6 digits of hex)  so that it
          can work with Graphviz.
     */
    backgroundColor: new VocabTerm(_NS$d("backgroundColor"), dataModel, getLocalStore(), false)
        .addLabel("background color", "en")
        .addCommentNoLanguage("Must be a valid CSS color string such as one could put in\n        an HTML style attribute.  This should be in the #xxxxxx form,\n        (with 6 digits of hex)  so that it\n        can work with Graphviz."),
    /**
     * URI or base64 representation of an image
     */
    backgroundImage: new VocabTerm(_NS$d("backgroundImage"), dataModel, getLocalStore(), false)
        .addLabel("background image", "en")
        .addCommentNoLanguage("URI or base64 representation of an image"),
    /**
     * Must be a valid CSS color string such as one could put in
          an HTML style attribute.  This should be in the #xxxxxx form,
          (with 6 digits of hex)  so that it
          can work with Graphviz.
     */
    color: new VocabTerm(_NS$d("color"), dataModel, getLocalStore(), false)
        .addLabel("color", "en")
        .addCommentNoLanguage("Must be a valid CSS color string such as one could put in\n        an HTML style attribute.  This should be in the #xxxxxx form,\n        (with 6 digits of hex)  so that it\n        can work with Graphviz."),
    /**
     * A really simple way of enabling user interfaces to
              create new information about a class of things is to make a define of properties
              to be specified when a information about a new item
              ("New item" here means an item which the system
              does not have prvious information about yet,
              not an items which has just been created,
              like new friend as opposed to new baby)
     */
    initialProperties: new VocabTerm(_NS$d("initialProperties"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("initial properties")
        .addCommentNoLanguage("A really simple way of enabling user interfaces to\n            create new information about a class of things is to make a define of properties\n            to be specified when a information about a new item\n            (\"New item\" here means an item which the system\n            does not have prvious information about yet,\n            not an items which has just been created,\n            like new friend as opposed to new baby)"),
    /**
     * max
     */
    maxValue: new VocabTerm(_NS$d("maxValue"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("max"),
    /**
     * min
     */
    minValue: new VocabTerm(_NS$d("minValue"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("min"),
    /**
     * The sequence in which this item is arranged with repect to other parts.
     */
    seqeunce: new VocabTerm(_NS$d("seqeunce"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("sequence number")
        .addCommentNoLanguage("The sequence in which this item is arranged with repect to other parts."),
    /**
     * A property which typically is used to sort
          members of a given class.
     */
    sortBy: new VocabTerm(_NS$d("sortBy"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("sort by")
        .addCommentNoLanguage("A property which typically is used to sort\n        members of a given class."),
    /**
     * When individuals or classes must be sorted, then
          if they are given different values of sortPriority a user agent can
          use this as a hint to how to present information.
     */
    sortPriority: new VocabTerm(_NS$d("sortPriority"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("sort priority")
        .addCommentNoLanguage("When individuals or classes must be sorted, then\n        if they are given different values of sortPriority a user agent can\n        use this as a hint to how to present information."),
    /**
     * This  is a crude way of specifying a table-based
              view for objects of this class.
     */
    tableProperties: new VocabTerm(_NS$d("tableProperties"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("table properties")
        .addCommentNoLanguage("This  is a crude way of specifying a table-based\n            view for objects of this class."),
    /**
     * Pattern
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    pattern: new VocabTerm(_NS$d("pattern"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Pattern", "en")
        .addLabel("Modelo", "es"),
    /**
     * Value
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    value: new VocabTerm(_NS$d("value"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Value", "en")
        .addLabel("Valor", "es"),
    /**
     * Values
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    values: new VocabTerm(_NS$d("values"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Values", "en")
        .addLabel("Valores", "es"),
    /**
     * Minimum date offset
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    minDateOffset: new VocabTerm(_NS$d("minDateOffset"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Minimum date offset", "en")
        .addLabel("Fecha m\u00EDnima compensada", "es"),
    /**
     * Maximum date offset
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    maxDateOffset: new VocabTerm(_NS$d("maxDateOffset"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Maximum date offset", "en")
        .addLabel("Fecha m\u00E1xima de desplazamiento", "es"),
    /**
     * Minimum datetime offset
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    minDatetimeOffset: new VocabTerm(_NS$d("minDatetimeOffset"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Minimum datetime offset", "en")
        .addLabel("Desplazamiento m\u00EDnimo de fecha y hora", "es"),
    /**
     * Maximum datetime offset
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    maxDatetimeOffset: new VocabTerm(_NS$d("maxDatetimeOffset"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Maximum datetime offset", "en")
        .addLabel("Desplazamiento m\u00E1ximo de fecha y hora", "es"),
    /**
     * Name
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    name: new VocabTerm(_NS$d("name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Name", "en")
        .addLabel("Nombre", "es"),
    /**
     * Time field
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    TimeField: new VocabTerm(_NS$d("TimeField"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Time field", "en")
        .addLabel("Campo de tiempo", "es"),
    /**
     * Old value
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    oldValue: new VocabTerm(_NS$d("oldValue"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Old value", "en")
        .addLabel("Valor antiguo", "es"),
    /**
     * Base
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    base: new VocabTerm(_NS$d("base"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Base", "en")
        .addLabel("Base", "es"),
    /**
     * Parent property
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    parentProperty: new VocabTerm(_NS$d("parentProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Parent property", "en")
        .addLabel("Propiedad principal", "es"),
    /**
     * Reference
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    reference: new VocabTerm(_NS$d("reference"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Reference", "en")
        .addLabel("Referencia", "es"),
    /**
     * Valid
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    valid: new VocabTerm(_NS$d("valid"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Valid", "en")
        .addLabel("V\u00E1lida", "es"),
    /**
     * Default error
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    defaultError: new VocabTerm(_NS$d("defaultError"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Default error", "en")
        .addLabel("Error predeterminado", "es"),
    /**
     * Parts clone
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    partsClone: new VocabTerm(_NS$d("partsClone"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Parts clone", "en")
        .addLabel("Clon de piezas", "es"),
    /**
     * Min length
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    minLength: new VocabTerm(_NS$d("minLength"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Min length", "en")
        .addLabel("Longitud m\u00EDnima", "es"),
    /**
     * Validation error
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    validationError: new VocabTerm(_NS$d("validationError"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Validation error", "en")
        .addLabel("Error de validacion", "es"),
    /**
     * Required
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    required: new VocabTerm(_NS$d("required"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Required", "en")
        .addLabel("Requerido", "es"),
    /**
     * Required error
     *
     * Defined by the vocabulary: https://w3id.org/inrupt/vocab/extension/ui#
     */
    requiredError: new VocabTerm(_NS$d("requiredError"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$e("https://w3id.org/inrupt/vocab/extension/ui#"))
        .addLabel("Required error", "en")
        .addLabel("Error requerido", "es"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$f = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  A vocabulary for annotating vocabulary descriptions (VANN).
 */
function _NS$e(localName) {
    return namedNode$f("http://purl.org/vocab/vann/" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var VANN = {
    PREFIX: "vann",
    NAMESPACE: "http://purl.org/vocab/vann/",
    PREFIX_AND_NAMESPACE: { "vann": "http://purl.org/vocab/vann/" },
    NS: _NS$e,
    // *******************
    // All the Properties.
    // *******************
    /**
     * The preferred namespace prefix to ue when using terms from this vocabulary in an XML document.
     */
    preferredNamespacePrefix: new VocabTerm(_NS$e("preferredNamespacePrefix"), dataModel, getLocalStore(), false)
        .addLabel("Preferred namespace prefix", "en")
        .addComment("The preferred namespace prefix to ue when using terms from this vocabulary in an XML document.", "en"),
    /**
     * The preferred namespace URI to use when using terms from this vocabulary in an XML document.
     */
    preferredNamespaceUri: new VocabTerm(_NS$e("preferredNamespaceUri"), dataModel, getLocalStore(), false)
        .addLabel("Preferred namespace URI", "en")
        .addComment("The preferred namespace URI to use when using terms from this vocabulary in an XML document.", "en"),
    /**
     * A reference to a resource that describes changes between this version of a vocabulary and the previous.
     */
    changes: new VocabTerm(_NS$e("changes"), dataModel, getLocalStore(), false)
        .addLabel("changes", "en")
        .addComment("A reference to a resource that describes changes between this version of a vocabulary and the previous.", "en"),
    /**
     * A reference to a resource that provides an example of how this resource can be used.
     */
    example: new VocabTerm(_NS$e("example"), dataModel, getLocalStore(), false)
        .addLabel("Example", "en")
        .addComment("A reference to a resource that provides an example of how this resource can be used.", "en"),
    /**
     * A group of related terms in a vocabulary.
     */
    termGroup: new VocabTerm(_NS$e("termGroup"), dataModel, getLocalStore(), false)
        .addLabel("Term group", "en")
        .addComment("A group of related terms in a vocabulary.", "en"),
    /**
     * A reference to a resource that provides information on how this resource is to be used.
     */
    usageNote: new VocabTerm(_NS$e("usageNote"), dataModel, getLocalStore(), false)
        .addLabel("Usage note", "en")
        .addComment("A reference to a resource that provides information on how this resource is to be used.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$g = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$f(localName) {
    return namedNode$g("http://purl.org/dc/terms/" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var DCTERMS = {
    PREFIX: "dcterms",
    NAMESPACE: "http://purl.org/dc/terms/",
    PREFIX_AND_NAMESPACE: { "dcterms": "http://purl.org/dc/terms/" },
    NS: _NS$f,
    // *****************
    // All the Classes.
    // *****************
    /**
     * A resource that acts or has the power to act.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Agent: new VocabTerm(_NS$f("Agent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Agent", "en")
        .addComment("A resource that acts or has the power to act.", "en"),
    /**
     * A group of agents.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    AgentClass: new VocabTerm(_NS$f("AgentClass"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Agent Class", "en")
        .addComment("A group of agents.", "en"),
    /**
     * A book, article, or other documentary resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    BibliographicResource: new VocabTerm(_NS$f("BibliographicResource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Bibliographic Resource", "en")
        .addComment("A book, article, or other documentary resource.", "en"),
    /**
     * A digital resource format.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    FileFormat: new VocabTerm(_NS$f("FileFormat"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("File Format", "en")
        .addComment("A digital resource format.", "en"),
    /**
     * A file format or physical medium.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    MediaType: new VocabTerm(_NS$f("MediaType"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Media Type", "en")
        .addComment("A file format or physical medium.", "en"),
    /**
     * A rate at which something recurs.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Frequency: new VocabTerm(_NS$f("Frequency"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Frequency", "en")
        .addComment("A rate at which something recurs.", "en"),
    /**
     * The extent or range of judicial, law enforcement, or other authority.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Jurisdiction: new VocabTerm(_NS$f("Jurisdiction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Jurisdiction", "en")
        .addComment("The extent or range of judicial, law enforcement, or other authority.", "en"),
    /**
     * A location, period of time, or jurisdiction.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    LocationPeriodOrJurisdiction: new VocabTerm(_NS$f("LocationPeriodOrJurisdiction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Location, Period, or Jurisdiction", "en")
        .addComment("A location, period of time, or jurisdiction.", "en"),
    /**
     * A legal document giving official permission to do something with a resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    LicenseDocument: new VocabTerm(_NS$f("LicenseDocument"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("License Document", "en")
        .addComment("A legal document giving official permission to do something with a resource.", "en"),
    /**
     * A statement about the intellectual property rights (IPR) held in or over a resource, a legal document giving official permission to do something with a resource, or a statement about access rights.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    RightsStatement: new VocabTerm(_NS$f("RightsStatement"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Rights Statement", "en")
        .addComment("A statement about the intellectual property rights (IPR) held in or over a resource, a legal document giving official permission to do something with a resource, or a statement about access rights.", "en"),
    /**
     * A system of signs, symbols, sounds, gestures, or rules used in communication.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    LinguisticSystem: new VocabTerm(_NS$f("LinguisticSystem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Linguistic System", "en")
        .addComment("A system of signs, symbols, sounds, gestures, or rules used in communication.", "en"),
    /**
     * A spatial region or named place.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Location: new VocabTerm(_NS$f("Location"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Location", "en")
        .addComment("A spatial region or named place.", "en"),
    /**
     * A media type or extent.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    MediaTypeOrExtent: new VocabTerm(_NS$f("MediaTypeOrExtent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Media Type or Extent", "en")
        .addComment("A media type or extent.", "en"),
    /**
     * A method by which resources are added to a collection.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    MethodOfAccrual: new VocabTerm(_NS$f("MethodOfAccrual"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Method of Accrual", "en")
        .addComment("A method by which resources are added to a collection.", "en"),
    /**
     * A process that is used to engender knowledge, attitudes, and skills.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    MethodOfInstruction: new VocabTerm(_NS$f("MethodOfInstruction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Method of Instruction", "en")
        .addComment("A process that is used to engender knowledge, attitudes, and skills.", "en"),
    /**
     * An interval of time that is named or defined by its start and end dates.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    PeriodOfTime: new VocabTerm(_NS$f("PeriodOfTime"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Period of Time", "en")
        .addComment("An interval of time that is named or defined by its start and end dates.", "en"),
    /**
     * A physical material or carrier.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    PhysicalMedium: new VocabTerm(_NS$f("PhysicalMedium"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Physical Medium", "en")
        .addComment("A physical material or carrier.", "en"),
    /**
     * A material thing.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    PhysicalResource: new VocabTerm(_NS$f("PhysicalResource"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Physical Resource", "en")
        .addComment("A material thing.", "en"),
    /**
     * A plan or course of action by an authority, intended to influence and determine decisions, actions, and other matters.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Policy: new VocabTerm(_NS$f("Policy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Policy", "en")
        .addComment("A plan or course of action by an authority, intended to influence and determine decisions, actions, and other matters.", "en"),
    /**
     * Any changes in ownership and custody of a resource since its creation that are significant for its authenticity, integrity, and interpretation.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    ProvenanceStatement: new VocabTerm(_NS$f("ProvenanceStatement"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Provenance Statement", "en")
        .addComment("Any changes in ownership and custody of a resource since its creation that are significant for its authenticity, integrity, and interpretation.", "en"),
    /**
     * A dimension or extent, or a time taken to play or execute.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    SizeOrDuration: new VocabTerm(_NS$f("SizeOrDuration"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Size or Duration", "en")
        .addComment("A dimension or extent, or a time taken to play or execute.", "en"),
    /**
     * A reference point against which other things can be evaluated or compared.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Standard: new VocabTerm(_NS$f("Standard"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Standard", "en")
        .addComment("A reference point against which other things can be evaluated or compared.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * Date on which the resource was changed.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    modified: new VocabTerm(_NS$f("modified"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Modified", "en")
        .addComment("Date on which the resource was changed.", "en"),
    /**
     * An entity responsible for making the resource available.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    publisher: new VocabTerm(_NS$f("publisher"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Publisher", "en")
        .addComment("An entity responsible for making the resource available.", "en"),
    /**
     * A name given to the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    title: new VocabTerm(_NS$f("title"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Title", "en")
        .addComment("A name given to the resource.", "en"),
    /**
     * Date of formal issuance of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    issued: new VocabTerm(_NS$f("issued"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Issued", "en")
        .addComment("Date of formal issuance of the resource.", "en"),
    /**
     * The set of regions in space defined by their geographic coordinates according to the DCMI Box Encoding Scheme.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Box: new VocabTerm(_NS$f("Box"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("https://www.dublincore.org/specifications/dublin-core/dcmi-box/"))
        .addLabel("DCMI Box", "en")
        .addComment("The set of regions in space defined by their geographic coordinates according to the DCMI Box Encoding Scheme.", "en"),
    /**
     * The set of codes listed in ISO 3166-1 for the representation of names of countries.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    ISO3166: new VocabTerm(_NS$f("ISO3166"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.iso.org/iso/en/prods-services/iso3166ma/02iso-3166-code-lists/list-en1.html"))
        .addLabel("ISO 3166", "en")
        .addComment("The set of codes listed in ISO 3166-1 for the representation of names of countries.", "en"),
    /**
     * The three-letter alphabetic codes listed in ISO639-2 for the representation of names of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    ISO639_2: new VocabTerm(_NS$f("ISO639-2"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://lcweb.loc.gov/standards/iso639-2/langhome.html"))
        .addLabel("ISO 639-2", "en")
        .addComment("The three-letter alphabetic codes listed in ISO639-2 for the representation of names of languages.", "en"),
    /**
     * The set of three-letter codes listed in ISO 639-3 for the representation of names of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    ISO639_3: new VocabTerm(_NS$f("ISO639-3"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.sil.org/iso639-3/"))
        .addLabel("ISO 639-3", "en")
        .addComment("The set of three-letter codes listed in ISO 639-3 for the representation of names of languages.", "en"),
    /**
     * An account of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    description: new VocabTerm(_NS$f("description"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Description", "en")
        .addComment("An account of the resource.", "en"),
    /**
     * The set of time intervals defined by their limits according to the DCMI Period Encoding Scheme.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Period: new VocabTerm(_NS$f("Period"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("https://www.dublincore.org/specifications/dublin-core/dcmi-period/"))
        .addLabel("DCMI Period", "en")
        .addComment("The set of time intervals defined by their limits according to the DCMI Period Encoding Scheme.", "en"),
    /**
     * The set of points in space defined by their geographic coordinates according to the DCMI Point Encoding Scheme.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    Point: new VocabTerm(_NS$f("Point"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("https://www.dublincore.org/specifications/dublin-core/dcmi-point/"))
        .addLabel("DCMI Point", "en")
        .addComment("The set of points in space defined by their geographic coordinates according to the DCMI Point Encoding Scheme.", "en"),
    /**
     * The set of tags, constructed according to RFC 1766, for the identification of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    RFC1766: new VocabTerm(_NS$f("RFC1766"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.ietf.org/rfc/rfc1766.txt"))
        .addLabel("RFC 1766", "en")
        .addComment("The set of tags, constructed according to RFC 1766, for the identification of languages.", "en"),
    /**
     * The set of tags constructed according to RFC 3066 for the identification of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    RFC3066: new VocabTerm(_NS$f("RFC3066"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.ietf.org/rfc/rfc3066.txt"))
        .addLabel("RFC 3066", "en")
        .addComment("The set of tags constructed according to RFC 3066 for the identification of languages.", "en"),
    /**
     * The set of tags constructed according to RFC 4646 for the identification of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    RFC4646: new VocabTerm(_NS$f("RFC4646"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.ietf.org/rfc/rfc4646.txt"))
        .addLabel("RFC 4646", "en")
        .addComment("The set of tags constructed according to RFC 4646 for the identification of languages.", "en"),
    /**
     * The set of tags constructed according to RFC 5646 for the identification of languages.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    RFC5646: new VocabTerm(_NS$f("RFC5646"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.ietf.org/rfc/rfc5646.txt"))
        .addLabel("RFC 5646", "en")
        .addComment("The set of tags constructed according to RFC 5646 for the identification of languages.", "en"),
    /**
     * The set of identifiers constructed according to the generic syntax for Uniform Resource Identifiers as specified by the Internet Engineering Task Force.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    URI: new VocabTerm(_NS$f("URI"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.ietf.org/rfc/rfc3986.txt"))
        .addLabel("URI", "en")
        .addComment("The set of identifiers constructed according to the generic syntax for Uniform Resource Identifiers as specified by the Internet Engineering Task Force.", "en"),
    /**
     * The set of dates and times constructed according to the W3C Date and Time Formats Specification.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    W3CDTF: new VocabTerm(_NS$f("W3CDTF"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addSeeAlso(namedNode$g("http://www.w3.org/TR/NOTE-datetime"))
        .addLabel("W3C-DTF", "en")
        .addComment("The set of dates and times constructed according to the W3C Date and Time Formats Specification.", "en"),
    /**
     * A summary of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    abstract: new VocabTerm(_NS$f("abstract"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Abstract", "en")
        .addComment("A summary of the resource.", "en"),
    /**
     * Information about who access the resource or an indication of its security status.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    accessRights: new VocabTerm(_NS$f("accessRights"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Access Rights", "en")
        .addComment("Information about who access the resource or an indication of its security status.", "en"),
    /**
     * The method by which items are added to a collection.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    accrualMethod: new VocabTerm(_NS$f("accrualMethod"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Accrual Method", "en")
        .addComment("The method by which items are added to a collection.", "en"),
    /**
     * The frequency with which items are added to a collection.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    accrualPeriodicity: new VocabTerm(_NS$f("accrualPeriodicity"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Accrual Periodicity", "en")
        .addComment("The frequency with which items are added to a collection.", "en"),
    /**
     * The policy governing the addition of items to a collection.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    accrualPolicy: new VocabTerm(_NS$f("accrualPolicy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Accrual Policy", "en")
        .addComment("The policy governing the addition of items to a collection.", "en"),
    /**
     * An alternative name for the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    alternative: new VocabTerm(_NS$f("alternative"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Alternative Title", "en")
        .addComment("An alternative name for the resource.", "en"),
    /**
     * A class of agents for whom the resource is intended or useful.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    audience: new VocabTerm(_NS$f("audience"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Audience", "en")
        .addComment("A class of agents for whom the resource is intended or useful.", "en"),
    /**
     * Date that the resource became or will become available.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    available: new VocabTerm(_NS$f("available"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Available", "en")
        .addComment("Date that the resource became or will become available.", "en"),
    /**
     * A bibliographic reference for the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    bibliographicCitation: new VocabTerm(_NS$f("bibliographicCitation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Bibliographic Citation", "en")
        .addComment("A bibliographic reference for the resource.", "en"),
    /**
     * An established standard to which the described resource conforms.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    conformsTo: new VocabTerm(_NS$f("conformsTo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Conforms To", "en")
        .addComment("An established standard to which the described resource conforms.", "en"),
    /**
     * An entity responsible for making contributions to the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    contributor: new VocabTerm(_NS$f("contributor"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Contributor", "en")
        .addComment("An entity responsible for making contributions to the resource.", "en"),
    /**
     * The spatial or temporal topic of the resource, spatial applicability of the resource, or jurisdiction under which the resource is relevant.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    coverage: new VocabTerm(_NS$f("coverage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Coverage", "en")
        .addComment("The spatial or temporal topic of the resource, spatial applicability of the resource, or jurisdiction under which the resource is relevant.", "en"),
    /**
     * Date of creation of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    created: new VocabTerm(_NS$f("created"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Created", "en")
        .addComment("Date of creation of the resource.", "en"),
    /**
     * An entity responsible for making the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    creator: new VocabTerm(_NS$f("creator"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Creator", "en")
        .addComment("An entity responsible for making the resource.", "en"),
    /**
     * A point or period of time associated with an event in the lifecycle of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    date: new VocabTerm(_NS$f("date"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date", "en")
        .addComment("A point or period of time associated with an event in the lifecycle of the resource.", "en"),
    /**
     * Date of acceptance of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    dateAccepted: new VocabTerm(_NS$f("dateAccepted"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Accepted", "en")
        .addComment("Date of acceptance of the resource.", "en"),
    /**
     * Date of copyright of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    dateCopyrighted: new VocabTerm(_NS$f("dateCopyrighted"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Copyrighted", "en")
        .addComment("Date of copyright of the resource.", "en"),
    /**
     * Date of submission of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    dateSubmitted: new VocabTerm(_NS$f("dateSubmitted"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Submitted", "en")
        .addComment("Date of submission of the resource.", "en"),
    /**
     * A class of agents, defined in terms of progression through an educational or training context, for which the described resource is intended.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    educationLevel: new VocabTerm(_NS$f("educationLevel"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Audience Education Level", "en")
        .addComment("A class of agents, defined in terms of progression through an educational or training context, for which the described resource is intended.", "en"),
    /**
     * The size or duration of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    extent: new VocabTerm(_NS$f("extent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Extent", "en")
        .addComment("The size or duration of the resource.", "en"),
    /**
     * The file format, physical medium, or dimensions of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    format: new VocabTerm(_NS$f("format"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Format", "en")
        .addComment("The file format, physical medium, or dimensions of the resource.", "en"),
    /**
     * A related resource that is substantially the same as the pre-existing described resource, but in another format.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    hasFormat: new VocabTerm(_NS$f("hasFormat"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Has Format", "en")
        .addComment("A related resource that is substantially the same as the pre-existing described resource, but in another format.", "en"),
    /**
     * A related resource that is included either physically or logically in the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    hasPart: new VocabTerm(_NS$f("hasPart"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Has Part", "en")
        .addComment("A related resource that is included either physically or logically in the described resource.", "en"),
    /**
     * A related resource that is a version, edition, or adaptation of the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    hasVersion: new VocabTerm(_NS$f("hasVersion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Has Version", "en")
        .addComment("A related resource that is a version, edition, or adaptation of the described resource.", "en"),
    /**
     * An unambiguous reference to the resource within a given context.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    identifier: new VocabTerm(_NS$f("identifier"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Identifier", "en")
        .addComment("An unambiguous reference to the resource within a given context.", "en"),
    /**
     * A process, used to engender knowledge, attitudes and skills, that the described resource is designed to support.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    instructionalMethod: new VocabTerm(_NS$f("instructionalMethod"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Instructional Method", "en")
        .addComment("A process, used to engender knowledge, attitudes and skills, that the described resource is designed to support.", "en"),
    /**
     * A pre-existing related resource that is substantially the same as the described resource, but in another format.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isFormatOf: new VocabTerm(_NS$f("isFormatOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Format Of", "en")
        .addComment("A pre-existing related resource that is substantially the same as the described resource, but in another format.", "en"),
    /**
     * A related resource in which the described resource is physically or logically included.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isPartOf: new VocabTerm(_NS$f("isPartOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Part Of", "en")
        .addComment("A related resource in which the described resource is physically or logically included.", "en"),
    /**
     * A related resource that references, cites, or otherwise points to the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isReferencedBy: new VocabTerm(_NS$f("isReferencedBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Referenced By", "en")
        .addComment("A related resource that references, cites, or otherwise points to the described resource.", "en"),
    /**
     * A related resource that supplants, displaces, or supersedes the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isReplacedBy: new VocabTerm(_NS$f("isReplacedBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Replaced By", "en")
        .addComment("A related resource that supplants, displaces, or supersedes the described resource.", "en"),
    /**
     * A related resource that requires the described resource to support its function, delivery, or coherence.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isRequiredBy: new VocabTerm(_NS$f("isRequiredBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Required By", "en")
        .addComment("A related resource that requires the described resource to support its function, delivery, or coherence.", "en"),
    /**
     * A related resource of which the described resource is a version, edition, or adaptation.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    isVersionOf: new VocabTerm(_NS$f("isVersionOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Is Version Of", "en")
        .addComment("A related resource of which the described resource is a version, edition, or adaptation.", "en"),
    /**
     * A language of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    language: new VocabTerm(_NS$f("language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Language", "en")
        .addComment("A language of the resource.", "en"),
    /**
     * A legal document giving official permission to do something with the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    license: new VocabTerm(_NS$f("license"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("License", "en")
        .addComment("A legal document giving official permission to do something with the resource.", "en"),
    /**
     * An entity that mediates access to the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    mediator: new VocabTerm(_NS$f("mediator"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Mediator", "en")
        .addComment("An entity that mediates access to the resource.", "en"),
    /**
     * The material or physical carrier of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    medium: new VocabTerm(_NS$f("medium"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Medium", "en")
        .addComment("The material or physical carrier of the resource.", "en"),
    /**
     * A statement of any changes in ownership and custody of the resource since its creation that are significant for its authenticity, integrity, and interpretation.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    provenance: new VocabTerm(_NS$f("provenance"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Provenance", "en")
        .addComment("A statement of any changes in ownership and custody of the resource since its creation that are significant for its authenticity, integrity, and interpretation.", "en"),
    /**
     * A related resource that is referenced, cited, or otherwise pointed to by the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    references: new VocabTerm(_NS$f("references"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("References", "en")
        .addComment("A related resource that is referenced, cited, or otherwise pointed to by the described resource.", "en"),
    /**
     * A related resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    relation: new VocabTerm(_NS$f("relation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Relation", "en")
        .addComment("A related resource.", "en"),
    /**
     * A related resource that is supplanted, displaced, or superseded by the described resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    replaces: new VocabTerm(_NS$f("replaces"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Replaces", "en")
        .addComment("A related resource that is supplanted, displaced, or superseded by the described resource.", "en"),
    /**
     * A related resource that is required by the described resource to support its function, delivery, or coherence.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    requires: new VocabTerm(_NS$f("requires"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Requires", "en")
        .addComment("A related resource that is required by the described resource to support its function, delivery, or coherence.", "en"),
    /**
     * Information about rights held in and over the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    rights: new VocabTerm(_NS$f("rights"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Rights", "en")
        .addComment("Information about rights held in and over the resource.", "en"),
    /**
     * A person or organization owning or managing rights over the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    rightsHolder: new VocabTerm(_NS$f("rightsHolder"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Rights Holder", "en")
        .addComment("A person or organization owning or managing rights over the resource.", "en"),
    /**
     * A related resource from which the described resource is derived.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    source: new VocabTerm(_NS$f("source"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Source", "en")
        .addComment("A related resource from which the described resource is derived.", "en"),
    /**
     * Spatial characteristics of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    spatial: new VocabTerm(_NS$f("spatial"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Spatial Coverage", "en")
        .addComment("Spatial characteristics of the resource.", "en"),
    /**
     * A topic of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    subject: new VocabTerm(_NS$f("subject"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Subject", "en")
        .addComment("A topic of the resource.", "en"),
    /**
     * A list of subunits of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    tableOfContents: new VocabTerm(_NS$f("tableOfContents"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Table Of Contents", "en")
        .addComment("A list of subunits of the resource.", "en"),
    /**
     * Temporal characteristics of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    temporal: new VocabTerm(_NS$f("temporal"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Temporal Coverage", "en")
        .addComment("Temporal characteristics of the resource.", "en"),
    /**
     * The nature or genre of the resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    type: new VocabTerm(_NS$f("type"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Type", "en")
        .addComment("The nature or genre of the resource.", "en"),
    /**
     * Date (often a range) of validity of a resource.
     *
     * Defined by the vocabulary: http://purl.org/dc/terms/
     */
    valid: new VocabTerm(_NS$f("valid"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$g("http://purl.org/dc/terms/"))
        .addLabel("Date Valid", "en")
        .addComment("Date (often a range) of validity of a resource.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$h = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$g(localName) {
    return namedNode$h("https://www.w3.org/ns/activitystreams#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var AS = {
    PREFIX: "as",
    NAMESPACE: "https://www.w3.org/ns/activitystreams#",
    PREFIX_AND_NAMESPACE: { "as": "https://www.w3.org/ns/activitystreams#" },
    NS: _NS$g,
    // *****************
    // All the Classes.
    // *****************
    /**
     * An Object representing some form of Action that has been taken
     */
    Activity: new VocabTerm(_NS$g("Activity"), dataModel, getLocalStore(), false)
        .addLabel("Activity", "en")
        .addComment("An Object representing some form of Action that has been taken", "en"),
    /**
     * Object
     */
    Object: new VocabTerm(_NS$g("Object"), dataModel, getLocalStore(), false)
        .addLabel("Object", "en"),
    /**
     * Represents a qualified reference to another resource. Patterned after the RFC5988 Web Linking Model
     */
    Link: new VocabTerm(_NS$g("Link"), dataModel, getLocalStore(), false)
        .addLabel("Link", "en")
        .addComment("Represents a qualified reference to another resource. Patterned after the RFC5988 Web Linking Model", "en"),
    /**
     * An ordered or unordered collection of Objects or Links
     */
    Collection: new VocabTerm(_NS$g("Collection"), dataModel, getLocalStore(), false)
        .addLabel("Collection", "en")
        .addComment("An ordered or unordered collection of Objects or Links", "en"),
    /**
     * A subset of items from a Collection
     */
    CollectionPage: new VocabTerm(_NS$g("CollectionPage"), dataModel, getLocalStore(), false)
        .addLabel("CollectionPage", "en")
        .addComment("A subset of items from a Collection", "en"),
    /**
     * An Image file
     */
    Image: new VocabTerm(_NS$g("Image"), dataModel, getLocalStore(), false)
        .addLabel("Image", "en")
        .addComment("An Image file", "en"),
    /**
     * A rdf:List variant for Objects and Links
     */
    OrderedItems: new VocabTerm(_NS$g("OrderedItems"), dataModel, getLocalStore(), false)
        .addLabel("OrderedItems", "en")
        .addComment("A rdf:List variant for Objects and Links", "en"),
    /**
     * Represents a Social Graph relationship between two Individuals (indicated by the 'a' and 'b' properties)
     */
    Relationship: new VocabTerm(_NS$g("Relationship"), dataModel, getLocalStore(), false)
        .addLabel("Relationship", "en")
        .addComment("Represents a Social Graph relationship between two Individuals (indicated by the 'a' and 'b' properties)", "en"),
    /**
     * A question of any sort.
     */
    Question: new VocabTerm(_NS$g("Question"), dataModel, getLocalStore(), false)
        .addLabel("Question", "en")
        .addComment("A question of any sort.", "en"),
    /**
     * A Profile Document
     */
    Profile: new VocabTerm(_NS$g("Profile"), dataModel, getLocalStore(), false)
        .addLabel("Profile", "en")
        .addComment("A Profile Document", "en"),
    /**
     * A placeholder for a deleted object
     */
    Tombstone: new VocabTerm(_NS$g("Tombstone"), dataModel, getLocalStore(), false)
        .addLabel("Tombstone", "en")
        .addComment("A placeholder for a deleted object", "en"),
    /**
     * A physical or logical location
     */
    Place: new VocabTerm(_NS$g("Place"), dataModel, getLocalStore(), false)
        .addLabel("Place", "en")
        .addComment("A physical or logical location", "en"),
    /**
     * An ordered subset of items from an OrderedCollection
     */
    OrderedCollectionPage: new VocabTerm(_NS$g("OrderedCollectionPage"), dataModel, getLocalStore(), false)
        .addLabel("OrderedCollectionPage", "en")
        .addComment("An ordered subset of items from an OrderedCollection", "en"),
    /**
     * Actor accepts the Object
     */
    Accept: new VocabTerm(_NS$g("Accept"), dataModel, getLocalStore(), false)
        .addLabel("Accept", "en")
        .addComment("Actor accepts the Object", "en"),
    /**
     * Block
     */
    Block: new VocabTerm(_NS$g("Block"), dataModel, getLocalStore(), false)
        .addLabel("Block", "en"),
    /**
     * Actor is ignoring the Object
     */
    Ignore: new VocabTerm(_NS$g("Ignore"), dataModel, getLocalStore(), false)
        .addLabel("Ignore", "en")
        .addComment("Actor is ignoring the Object", "en"),
    /**
     * An Activity that has no direct object
     */
    IntransitiveActivity: new VocabTerm(_NS$g("IntransitiveActivity"), dataModel, getLocalStore(), false)
        .addLabel("IntransitiveActivity", "en")
        .addComment("An Activity that has no direct object", "en"),
    /**
     * To Add an Object or Link to Something
     */
    Add: new VocabTerm(_NS$g("Add"), dataModel, getLocalStore(), false)
        .addLabel("Add", "en")
        .addComment("To Add an Object or Link to Something", "en"),
    /**
     * Actor announces the object to the target
     */
    Announce: new VocabTerm(_NS$g("Announce"), dataModel, getLocalStore(), false)
        .addLabel("Announce", "en")
        .addComment("Actor announces the object to the target", "en"),
    /**
     * Represents a software application of any sort
     */
    Application: new VocabTerm(_NS$g("Application"), dataModel, getLocalStore(), false)
        .addLabel("Application", "en")
        .addComment("Represents a software application of any sort", "en"),
    /**
     * To Arrive Somewhere (can be used, for instance, to indicate that a particular entity is currently located somewhere, e.g. a "check-in")
     */
    Arrive: new VocabTerm(_NS$g("Arrive"), dataModel, getLocalStore(), false)
        .addLabel("Arrive", "en")
        .addComment("To Arrive Somewhere (can be used, for instance, to indicate that a particular entity is currently located somewhere, e.g. a \"check-in\")", "en"),
    /**
     * A written work. Typically several paragraphs long. For example, a blog post or a news article.
     */
    Article: new VocabTerm(_NS$g("Article"), dataModel, getLocalStore(), false)
        .addLabel("Article", "en")
        .addComment("A written work. Typically several paragraphs long. For example, a blog post or a news article.", "en"),
    /**
     * An audio file
     */
    Audio: new VocabTerm(_NS$g("Audio"), dataModel, getLocalStore(), false)
        .addLabel("Audio", "en")
        .addComment("An audio file", "en"),
    /**
     * Represents a digital document/file of any sort
     */
    Document: new VocabTerm(_NS$g("Document"), dataModel, getLocalStore(), false)
        .addLabel("Document", "en")
        .addComment("Represents a digital document/file of any sort", "en"),
    /**
     * A variation of Collection in which items are strictly ordered
     */
    OrderedCollection: new VocabTerm(_NS$g("OrderedCollection"), dataModel, getLocalStore(), false)
        .addLabel("OrderedCollection", "en")
        .addComment("A variation of Collection in which items are strictly ordered", "en"),
    /**
     * To Create Something
     */
    Create: new VocabTerm(_NS$g("Create"), dataModel, getLocalStore(), false)
        .addLabel("Create", "en")
        .addComment("To Create Something", "en"),
    /**
     * To Delete Something
     */
    Delete: new VocabTerm(_NS$g("Delete"), dataModel, getLocalStore(), false)
        .addLabel("Delete", "en")
        .addComment("To Delete Something", "en"),
    /**
     * The actor dislikes the object
     */
    Dislike: new VocabTerm(_NS$g("Dislike"), dataModel, getLocalStore(), false)
        .addLabel("Dislike", "en")
        .addComment("The actor dislikes the object", "en"),
    /**
     * An Event of any kind
     */
    Event: new VocabTerm(_NS$g("Event"), dataModel, getLocalStore(), false)
        .addLabel("Event", "en")
        .addComment("An Event of any kind", "en"),
    /**
     * To flag something (e.g. flag as inappropriate, flag as spam, etc)
     */
    Flag: new VocabTerm(_NS$g("Flag"), dataModel, getLocalStore(), false)
        .addLabel("Flag", "en")
        .addComment("To flag something (e.g. flag as inappropriate, flag as spam, etc)", "en"),
    /**
     * To Express Interest in Something
     */
    Follow: new VocabTerm(_NS$g("Follow"), dataModel, getLocalStore(), false)
        .addLabel("Follow", "en")
        .addComment("To Express Interest in Something", "en"),
    /**
     * A Group of any kind.
     */
    Group: new VocabTerm(_NS$g("Group"), dataModel, getLocalStore(), false)
        .addLabel("Group", "en")
        .addComment("A Group of any kind.", "en"),
    /**
     * To invite someone or something to something
     */
    Invite: new VocabTerm(_NS$g("Invite"), dataModel, getLocalStore(), false)
        .addLabel("Invite", "en")
        .addComment("To invite someone or something to something", "en"),
    /**
     * To Offer something to someone or something
     */
    Offer: new VocabTerm(_NS$g("Offer"), dataModel, getLocalStore(), false)
        .addLabel("Offer", "en")
        .addComment("To Offer something to someone or something", "en"),
    /**
     * To Join Something
     */
    Join: new VocabTerm(_NS$g("Join"), dataModel, getLocalStore(), false)
        .addLabel("Join", "en")
        .addComment("To Join Something", "en"),
    /**
     * To Leave Something
     */
    Leave: new VocabTerm(_NS$g("Leave"), dataModel, getLocalStore(), false)
        .addLabel("Leave", "en")
        .addComment("To Leave Something", "en"),
    /**
     * To Like Something
     */
    Like: new VocabTerm(_NS$g("Like"), dataModel, getLocalStore(), false)
        .addLabel("Like", "en")
        .addComment("To Like Something", "en"),
    /**
     * The actor viewed the object
     */
    View: new VocabTerm(_NS$g("View"), dataModel, getLocalStore(), false)
        .addLabel("View", "en")
        .addComment("The actor viewed the object", "en"),
    /**
     * The actor listened to the object
     */
    Listen: new VocabTerm(_NS$g("Listen"), dataModel, getLocalStore(), false)
        .addLabel("Listen", "en")
        .addComment("The actor listened to the object", "en"),
    /**
     * The actor read the object
     */
    Read: new VocabTerm(_NS$g("Read"), dataModel, getLocalStore(), false)
        .addLabel("Read", "en")
        .addComment("The actor read the object", "en"),
    /**
     * The actor is moving the object. The target specifies where the object is moving to. The origin specifies where the object is moving from.
     */
    Move: new VocabTerm(_NS$g("Move"), dataModel, getLocalStore(), false)
        .addLabel("Move", "en")
        .addCommentNoLanguage("The actor is moving the object. The target specifies where the object is moving to. The origin specifies where the object is moving from."),
    /**
     * The actor is traveling to the target. The origin specifies where the actor is traveling from.
     */
    Travel: new VocabTerm(_NS$g("Travel"), dataModel, getLocalStore(), false)
        .addLabel("Travel", "en")
        .addCommentNoLanguage("The actor is traveling to the target. The origin specifies where the actor is traveling from."),
    /**
     * A specialized Link that represents an @mention
     */
    Mention: new VocabTerm(_NS$g("Mention"), dataModel, getLocalStore(), false)
        .addLabel("Mention", "en")
        .addComment("A specialized Link that represents an @mention", "en"),
    /**
     * A Short note, typically less than a single paragraph. A "tweet" is an example, or a "status update"
     */
    Note: new VocabTerm(_NS$g("Note"), dataModel, getLocalStore(), false)
        .addLabel("Note", "en")
        .addComment("A Short note, typically less than a single paragraph. A \"tweet\" is an example, or a \"status update\"", "en"),
    /**
     * A Web Page
     */
    Page: new VocabTerm(_NS$g("Page"), dataModel, getLocalStore(), false)
        .addLabel("Page", "en")
        .addComment("A Web Page", "en"),
    /**
     * A Person
     */
    Person: new VocabTerm(_NS$g("Person"), dataModel, getLocalStore(), false)
        .addLabel("Person", "en")
        .addComment("A Person", "en"),
    /**
     * An Organization
     */
    Organization: new VocabTerm(_NS$g("Organization"), dataModel, getLocalStore(), false)
        .addLabel("Organization", "en")
        .addComment("An Organization", "en"),
    /**
     * Actor rejects the Object
     */
    Reject: new VocabTerm(_NS$g("Reject"), dataModel, getLocalStore(), false)
        .addLabel("Reject", "en")
        .addComment("Actor rejects the Object", "en"),
    /**
     * To Remove Something
     */
    Remove: new VocabTerm(_NS$g("Remove"), dataModel, getLocalStore(), false)
        .addLabel("Remove", "en")
        .addComment("To Remove Something", "en"),
    /**
     * A service provided by some entity
     */
    Service: new VocabTerm(_NS$g("Service"), dataModel, getLocalStore(), false)
        .addLabel("Service", "en")
        .addComment("A service provided by some entity", "en"),
    /**
     * Actor tentatively accepts the Object
     */
    TentativeAccept: new VocabTerm(_NS$g("TentativeAccept"), dataModel, getLocalStore(), false)
        .addLabel("TentativeAccept", "en")
        .addComment("Actor tentatively accepts the Object", "en"),
    /**
     * Actor tentatively rejects the object
     */
    TentativeReject: new VocabTerm(_NS$g("TentativeReject"), dataModel, getLocalStore(), false)
        .addLabel("TentativeReject", "en")
        .addComment("Actor tentatively rejects the object", "en"),
    /**
     * To Undo Something. This would typically be used to indicate that a previous Activity has been undone.
     */
    Undo: new VocabTerm(_NS$g("Undo"), dataModel, getLocalStore(), false)
        .addLabel("Undo", "en")
        .addComment("To Undo Something. This would typically be used to indicate that a previous Activity has been undone.", "en"),
    /**
     * To Update/Modify Something
     */
    Update: new VocabTerm(_NS$g("Update"), dataModel, getLocalStore(), false)
        .addLabel("Update", "en")
        .addComment("To Update/Modify Something", "en"),
    /**
     * A Video document of any kind.
     */
    Video: new VocabTerm(_NS$g("Video"), dataModel, getLocalStore(), false)
        .addLabel("Video", "en")
        .addComment("A Video document of any kind.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * Subproperty of as:attributedTo that identifies the primary actor
     */
    actor: new VocabTerm(_NS$g("actor"), dataModel, getLocalStore(), false)
        .addLabel("actor", "en")
        .addComment("Subproperty of as:attributedTo that identifies the primary actor", "en"),
    /**
     * Identifies an entity to which an object is attributed
     */
    attributedTo: new VocabTerm(_NS$g("attributedTo"), dataModel, getLocalStore(), false)
        .addLabel("attributedTo", "en")
        .addComment("Identifies an entity to which an object is attributed", "en"),
    /**
     * attachment
     */
    attachment: new VocabTerm(_NS$g("attachment"), dataModel, getLocalStore(), false)
        .addLabel("attachment", "en"),
    /**
     * attachments
     */
    attachments: new VocabTerm(_NS$g("attachments"), dataModel, getLocalStore(), false)
        .addLabel("attachments", "en"),
    /**
     * Identifies the author of an object. Deprecated. Use as:attributedTo instead
     */
    author: new VocabTerm(_NS$g("author"), dataModel, getLocalStore(), false)
        .addLabel("author", "en")
        .addComment("Identifies the author of an object. Deprecated. Use as:attributedTo instead", "en"),
    /**
     * bcc
     */
    bcc: new VocabTerm(_NS$g("bcc"), dataModel, getLocalStore(), false)
        .addLabel("bcc", "en"),
    /**
     * bto
     */
    bto: new VocabTerm(_NS$g("bto"), dataModel, getLocalStore(), false)
        .addLabel("bto", "en"),
    /**
     * cc
     */
    cc: new VocabTerm(_NS$g("cc"), dataModel, getLocalStore(), false)
        .addLabel("cc", "en"),
    /**
     * Specifies the context within which an object exists or an activity was performed
     */
    context: new VocabTerm(_NS$g("context"), dataModel, getLocalStore(), false)
        .addLabel("context", "en")
        .addComment("Specifies the context within which an object exists or an activity was performed", "en"),
    /**
     * current
     */
    current: new VocabTerm(_NS$g("current"), dataModel, getLocalStore(), false)
        .addLabel("current", "en"),
    /**
     * first
     */
    first: new VocabTerm(_NS$g("first"), dataModel, getLocalStore(), false)
        .addLabel("first", "en"),
    /**
     * generator
     */
    generator: new VocabTerm(_NS$g("generator"), dataModel, getLocalStore(), false)
        .addLabel("generator", "en"),
    /**
     * icon
     */
    icon: new VocabTerm(_NS$g("icon"), dataModel, getLocalStore(), false)
        .addLabel("icon", "en"),
    /**
     * image
     */
    image: new VocabTerm(_NS$g("image"), dataModel, getLocalStore(), false)
        .addLabel("image", "en"),
    /**
     * inReplyTo
     */
    inReplyTo: new VocabTerm(_NS$g("inReplyTo"), dataModel, getLocalStore(), false)
        .addLabel("inReplyTo", "en"),
    /**
     * items
     */
    items: new VocabTerm(_NS$g("items"), dataModel, getLocalStore(), false)
        .addLabel("items", "en"),
    /**
     * last
     */
    last: new VocabTerm(_NS$g("last"), dataModel, getLocalStore(), false)
        .addLabel("last", "en"),
    /**
     * location
     */
    location: new VocabTerm(_NS$g("location"), dataModel, getLocalStore(), false)
        .addLabel("location", "en"),
    /**
     * next
     */
    next: new VocabTerm(_NS$g("next"), dataModel, getLocalStore(), false)
        .addLabel("next", "en"),
    /**
     * object
     */
    object: new VocabTerm(_NS$g("object"), dataModel, getLocalStore(), false)
        .addLabel("object", "en"),
    /**
     * Describes a possible exclusive answer or option for a question.
     */
    oneOf: new VocabTerm(_NS$g("oneOf"), dataModel, getLocalStore(), false)
        .addLabel("oneOf", "en")
        .addComment("Describes a possible exclusive answer or option for a question.", "en"),
    /**
     * Describes a possible inclusive answer or option for a question.
     */
    anyOf: new VocabTerm(_NS$g("anyOf"), dataModel, getLocalStore(), false)
        .addLabel("oneOf", "en")
        .addComment("Describes a possible inclusive answer or option for a question.", "en"),
    /**
     * prev
     */
    prev: new VocabTerm(_NS$g("prev"), dataModel, getLocalStore(), false)
        .addLabel("prev", "en"),
    /**
     * preview
     */
    preview: new VocabTerm(_NS$g("preview"), dataModel, getLocalStore(), false)
        .addLabel("preview", "en"),
    /**
     * provider
     */
    provider: new VocabTerm(_NS$g("provider"), dataModel, getLocalStore(), false)
        .addLabel("provider", "en"),
    /**
     * replies
     */
    replies: new VocabTerm(_NS$g("replies"), dataModel, getLocalStore(), false)
        .addLabel("replies", "en"),
    /**
     * result
     */
    result: new VocabTerm(_NS$g("result"), dataModel, getLocalStore(), false)
        .addLabel("result", "en"),
    /**
     * audience
     */
    audience: new VocabTerm(_NS$g("audience"), dataModel, getLocalStore(), false)
        .addLabel("audience", "en"),
    /**
     * partOf
     */
    partOf: new VocabTerm(_NS$g("partOf"), dataModel, getLocalStore(), false)
        .addLabel("partOf", "en"),
    /**
     * tag
     */
    tag: new VocabTerm(_NS$g("tag"), dataModel, getLocalStore(), false)
        .addLabel("tag", "en"),
    /**
     * tags
     */
    tags: new VocabTerm(_NS$g("tags"), dataModel, getLocalStore(), false)
        .addLabel("tags", "en"),
    /**
     * target
     */
    target: new VocabTerm(_NS$g("target"), dataModel, getLocalStore(), false)
        .addLabel("target", "en"),
    /**
     * For certain activities, specifies the entity from which the action is directed.
     */
    origin: new VocabTerm(_NS$g("origin"), dataModel, getLocalStore(), false)
        .addLabel("origin", "en")
        .addComment("For certain activities, specifies the entity from which the action is directed.", "en"),
    /**
     * Indentifies an object used (or to be used) to complete an activity
     */
    instrument: new VocabTerm(_NS$g("instrument"), dataModel, getLocalStore(), false)
        .addLabel("instrument", "en")
        .addComment("Indentifies an object used (or to be used) to complete an activity", "en"),
    /**
     * to
     */
    to: new VocabTerm(_NS$g("to"), dataModel, getLocalStore(), false)
        .addLabel("to", "en"),
    /**
     * Specifies a link to a specific representation of the Object
     */
    url: new VocabTerm(_NS$g("url"), dataModel, getLocalStore(), false)
        .addLabel("url", "en")
        .addComment("Specifies a link to a specific representation of the Object", "en"),
    /**
     * On a Relationship object, identifies the subject. e.g. when saying "John is connected to Sally", 'subject' refers to 'John'
     */
    subject: new VocabTerm(_NS$g("subject"), dataModel, getLocalStore(), false)
        .addLabel("a", "en")
        .addComment("On a Relationship object, identifies the subject. e.g. when saying \"John is connected to Sally\", 'subject' refers to 'John'", "en"),
    /**
     * On a Relationship object, describes the type of relationship
     */
    relationship: new VocabTerm(_NS$g("relationship"), dataModel, getLocalStore(), false)
        .addLabel("relationship", "en")
        .addComment("On a Relationship object, describes the type of relationship", "en"),
    /**
     * On a Profile object, describes the object described by the profile
     */
    describes: new VocabTerm(_NS$g("describes"), dataModel, getLocalStore(), false)
        .addLabel("describes", "en")
        .addComment("On a Profile object, describes the object described by the profile", "en"),
    /**
     * On a Tombstone object, describes the former type of the deleted object
     */
    formerType: new VocabTerm(_NS$g("formerType"), dataModel, getLocalStore(), false)
        .addLabel("formerType", "en")
        .addComment("On a Tombstone object, describes the former type of the deleted object", "en"),
    /**
     * Specifies the accuracy around the point established by the longitude and latitude
     */
    accuracy: new VocabTerm(_NS$g("accuracy"), dataModel, getLocalStore(), false)
        .addLabel("accuracy", "en")
        .addComment("Specifies the accuracy around the point established by the longitude and latitude", "en"),
    /**
     * The altitude of a place
     */
    altitude: new VocabTerm(_NS$g("altitude"), dataModel, getLocalStore(), false)
        .addLabel("altitude", "en")
        .addComment("The altitude of a place", "en"),
    /**
     * The content of the object.
     */
    content: new VocabTerm(_NS$g("content"), dataModel, getLocalStore(), false)
        .addLabel("content", "en")
        .addComment("The content of the object.", "en"),
    /**
     * name
     */
    name: new VocabTerm(_NS$g("name"), dataModel, getLocalStore(), false)
        .addLabel("name", "en"),
    /**
     * downstreamDuplicates
     */
    downstreamDuplicates: new VocabTerm(_NS$g("downstreamDuplicates"), dataModel, getLocalStore(), false)
        .addLabel("downstreamDuplicates", "en"),
    /**
     * The duration of the object
     */
    duration: new VocabTerm(_NS$g("duration"), dataModel, getLocalStore(), false)
        .addLabel("duration", "en")
        .addComment("The duration of the object", "en"),
    /**
     * The ending time of the object
     */
    endTime: new VocabTerm(_NS$g("endTime"), dataModel, getLocalStore(), false)
        .addLabel("endTime", "en")
        .addComment("The ending time of the object", "en"),
    /**
     * The display height expressed as device independent pixels
     */
    height: new VocabTerm(_NS$g("height"), dataModel, getLocalStore(), false)
        .addLabel("height", "en")
        .addComment("The display height expressed as device independent pixels", "en"),
    /**
     * The target URI of the Link
     */
    href: new VocabTerm(_NS$g("href"), dataModel, getLocalStore(), false)
        .addLabel("href", "en")
        .addComment("The target URI of the Link", "en"),
    /**
     * A hint about the language of the referenced resource
     */
    hreflang: new VocabTerm(_NS$g("hreflang"), dataModel, getLocalStore(), false)
        .addLabel("hreflang", "en")
        .addComment("A hint about the language of the referenced resource", "en"),
    /**
     * id
     */
    id: new VocabTerm(_NS$g("id"), dataModel, getLocalStore(), false)
        .addLabel("id", "en"),
    /**
     * The latitude
     */
    latitude: new VocabTerm(_NS$g("latitude"), dataModel, getLocalStore(), false)
        .addLabel("latitude", "en")
        .addComment("The latitude", "en"),
    /**
     * The longitude
     */
    longitude: new VocabTerm(_NS$g("longitude"), dataModel, getLocalStore(), false)
        .addLabel("longitude", "en")
        .addComment("The longitude", "en"),
    /**
     * The MIME Media Type
     */
    mediaType: new VocabTerm(_NS$g("mediaType"), dataModel, getLocalStore(), false)
        .addLabel("mediaType", "en")
        .addComment("The MIME Media Type", "en"),
    /**
     * objectType
     */
    objectType: new VocabTerm(_NS$g("objectType"), dataModel, getLocalStore(), false)
        .addLabel("objectType", "en"),
    /**
     * Specifies the date and time the object was published
     */
    published: new VocabTerm(_NS$g("published"), dataModel, getLocalStore(), false)
        .addLabel("published", "en")
        .addComment("Specifies the date and time the object was published", "en"),
    /**
     * Specifies a radius around the point established by the longitude and latitude
     */
    radius: new VocabTerm(_NS$g("radius"), dataModel, getLocalStore(), false)
        .addLabel("radius", "en")
        .addComment("Specifies a radius around the point established by the longitude and latitude", "en"),
    /**
     * A numeric rating (>= 0.0, <= 5.0) for the object
     */
    rating: new VocabTerm(_NS$g("rating"), dataModel, getLocalStore(), false)
        .addLabel("rating", "en")
        .addComment("A numeric rating (>= 0.0, <= 5.0) for the object", "en"),
    /**
     * The RFC 5988 or HTML5 Link Relation associated with the Link
     */
    rel: new VocabTerm(_NS$g("rel"), dataModel, getLocalStore(), false)
        .addLabel("rel", "en")
        .addComment("The RFC 5988 or HTML5 Link Relation associated with the Link", "en"),
    /**
     * In a strictly ordered logical collection, specifies the index position of the first item in the items list
     */
    startIndex: new VocabTerm(_NS$g("startIndex"), dataModel, getLocalStore(), false)
        .addLabel("startIndex", "en")
        .addComment("In a strictly ordered logical collection, specifies the index position of the first item in the items list", "en"),
    /**
     * The starting time of the object
     */
    startTime: new VocabTerm(_NS$g("startTime"), dataModel, getLocalStore(), false)
        .addLabel("startTime", "en")
        .addComment("The starting time of the object", "en"),
    /**
     * A short summary of the object
     */
    summary: new VocabTerm(_NS$g("summary"), dataModel, getLocalStore(), false)
        .addLabel("summary", "en")
        .addComment("A short summary of the object", "en"),
    /**
     * The total number of items in a logical collection
     */
    totalItems: new VocabTerm(_NS$g("totalItems"), dataModel, getLocalStore(), false)
        .addLabel("totalItems", "en")
        .addComment("The total number of items in a logical collection", "en"),
    /**
     * Identifies the unit of measurement used by the radius, altitude and accuracy properties. The value can be expressed either as one of a set of predefined units or as a well-known common URI that identifies units.
     */
    units: new VocabTerm(_NS$g("units"), dataModel, getLocalStore(), false)
        .addLabel("units", "en")
        .addComment("Identifies the unit of measurement used by the radius, altitude and accuracy properties. The value can be expressed either as one of a set of predefined units or as a well-known common URI that identifies units.", "en"),
    /**
     * Specifies when the object was last updated
     */
    updated: new VocabTerm(_NS$g("updated"), dataModel, getLocalStore(), false)
        .addLabel("updated", "en")
        .addComment("Specifies when the object was last updated", "en"),
    /**
     * upstreamDuplicates
     */
    upstreamDuplicates: new VocabTerm(_NS$g("upstreamDuplicates"), dataModel, getLocalStore(), false)
        .addLabel("upstreamDuplicates", "en"),
    /**
     * verb
     */
    verb: new VocabTerm(_NS$g("verb"), dataModel, getLocalStore(), false)
        .addLabel("verb", "en"),
    /**
     * Specifies the preferred display width of the content, expressed in terms of device independent pixels.
     */
    width: new VocabTerm(_NS$g("width"), dataModel, getLocalStore(), false)
        .addLabel("width", "en")
        .addComment("Specifies the preferred display width of the content, expressed in terms of device independent pixels.", "en"),
    /**
     * Specifies the date and time the object was deleted
     */
    deleted: new VocabTerm(_NS$g("deleted"), dataModel, getLocalStore(), false)
        .addLabel("deleted", "en")
        .addComment("Specifies the date and time the object was deleted", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$i = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  Inrupt-created copy of RLOG, needed due to Server 500 errors resolving original uni-leipzig.org!
 */
function _NS$h(localName) {
    return namedNode$i("http://persistence.uni-leipzig.org/nlp2rdf/ontologies/rlog#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var RLOG = {
    PREFIX: "rlog",
    NAMESPACE: "http://persistence.uni-leipzig.org/nlp2rdf/ontologies/rlog#",
    PREFIX_AND_NAMESPACE: { "rlog": "http://persistence.uni-leipzig.org/nlp2rdf/ontologies/rlog#" },
    NS: _NS$h,
    // *****************
    // All the Classes.
    // *****************
    /**
     * look here: http://logging.apache.org/log4j/1.2/apidocs/org/apache/log4j/Level.html
     */
    Level: new VocabTerm(_NS$h("Level"), dataModel, getLocalStore(), false)
        .addLabel("Log Level", "en")
        .addComment("look here: http://logging.apache.org/log4j/1.2/apidocs/org/apache/log4j/Level.html ", "en"),
    /**
     * An entry in a log.
     */
    Entry: new VocabTerm(_NS$h("Entry"), dataModel, getLocalStore(), false)
        .addLabel("Log Entry", "en")
        .addComment("An entry in a log.", "en"),
    /**
     * A status code which can occur in an application. Instances of this class must put the name of the status as rdfs:label and describe it with rdfs:comment.
     */
    StatusCode: new VocabTerm(_NS$h("StatusCode"), dataModel, getLocalStore(), false)
        .addLabel("Status Code", "en")
        .addComment("A status code which can occur in an application. Instances of this class must put the name of the status as rdfs:label and describe it with rdfs:comment.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The ALL has the lowest possible rank and is intended to turn on all logging.
     */
    ALL: new VocabTerm(_NS$h("ALL"), dataModel, getLocalStore(), false)
        .addLabel("ALL", "en")
        .addComment("The ALL has the lowest possible rank and is intended to turn on all logging.", "en"),
    /**
     * Attention: the ids here can change, they are just used to define an order over the levels!
     */
    priority: new VocabTerm(_NS$h("priority"), dataModel, getLocalStore(), false)
        .addLabel("logLevel", "en")
        .addComment("Attention: the ids here can change, they are just used to define an order over the levels!", "en"),
    /**
     * The DEBUG Level designates fine-grained informational events that are most useful to debug an application.
     */
    DEBUG: new VocabTerm(_NS$h("DEBUG"), dataModel, getLocalStore(), false)
        .addLabel("DEBUG", "en")
        .addComment("The DEBUG Level designates fine-grained informational events that are most useful to debug an application.", "en"),
    /**
     * The ERROR level designates error events that might still allow the application to continue running.
     */
    ERROR: new VocabTerm(_NS$h("ERROR"), dataModel, getLocalStore(), false)
        .addLabel("ERROR", "en")
        .addComment("The ERROR level designates error events that might still allow the application to continue running.", "en"),
    /**
     * The FATAL level designates very severe error events that will presumably lead the application to abort.
     */
    FATAL: new VocabTerm(_NS$h("FATAL"), dataModel, getLocalStore(), false)
        .addLabel("FATAL", "en")
        .addComment("The FATAL level designates very severe error events that will presumably lead the application to abort.", "en"),
    /**
     * The INFO level designates informational messages that highlight the progress of the application at coarse-grained level.
     */
    INFO: new VocabTerm(_NS$h("INFO"), dataModel, getLocalStore(), false)
        .addLabel("INFO", "en")
        .addComment("The INFO level designates informational messages that highlight the progress of the application at coarse-grained level.", "en"),
    /**
     * The OFF has the highest possible rank and is intended to turn off logging.
     */
    OFF: new VocabTerm(_NS$h("OFF"), dataModel, getLocalStore(), false)
        .addLabel("OFF", "en")
        .addComment("The OFF has the highest possible rank and is intended to turn off logging.", "en"),
    /**
     * The TRACE Level designates finer-grained informational events than the DEBUG.
     */
    TRACE: new VocabTerm(_NS$h("TRACE"), dataModel, getLocalStore(), false)
        .addLabel("TRACE", "en")
        .addComment("The TRACE Level designates finer-grained informational events than the DEBUG.", "en"),
    /**
     * The WARN level designates potentially harmful situations.
     */
    WARN: new VocabTerm(_NS$h("WARN"), dataModel, getLocalStore(), false)
        .addLabel("WARN", "en")
        .addComment("The WARN level designates potentially harmful situations.", "en"),
    /**
     * Outputs the fully qualified class name of the caller issuing the logging request.
     */
    className: new VocabTerm(_NS$h("className"), dataModel, getLocalStore(), false)
        .addLabel("class name", "en")
        .addComment("Outputs the fully qualified class name of the caller issuing the logging request.", "en"),
    /**
     * The numerical value of the code, e.g. 200, 404 or 42.
     */
    codeId: new VocabTerm(_NS$h("codeId"), dataModel, getLocalStore(), false)
        .addLabel("code id", "en")
        .addComment("The numerical value of the code, e.g. 200, 404 or 42.", "en"),
    /**
     * Logging datetime (or just date)
     */
    date: new VocabTerm(_NS$h("date"), dataModel, getLocalStore(), false)
        .addLabel("Logging datetime (or just date)", "en"),
    /**
     * Links a log message to a status code.
     */
    hasCode: new VocabTerm(_NS$h("hasCode"), dataModel, getLocalStore(), false)
        .addLabel("has code", "en")
        .addComment("Links a log message to a status code.", "en"),
    /**
     * logLevel
     */
    level: new VocabTerm(_NS$h("level"), dataModel, getLocalStore(), false)
        .addLabel("logLevel", "en"),
    /**
     * Logging message
     */
    message: new VocabTerm(_NS$h("message"), dataModel, getLocalStore(), false)
        .addLabel("Logging message", "en"),
    /**
     * If we mix normal and log output, this can be used to refer to the resource in the RDF the error is connected to.
     */
    resource: new VocabTerm(_NS$h("resource"), dataModel, getLocalStore(), false)
        .addLabel("an associated resource", "en")
        .addComment("If we mix normal and log output, this can be used to refer to the resource in the RDF the error is connected to. ", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$j = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$i(localName) {
    return namedNode$j("http://xmlns.com/foaf/0.1/" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var FOAF = {
    PREFIX: "foaf",
    NAMESPACE: "http://xmlns.com/foaf/0.1/",
    PREFIX_AND_NAMESPACE: { "foaf": "http://xmlns.com/foaf/0.1/" },
    NS: _NS$i,
    // *****************
    // All the Classes.
    // *****************
    /**
     * An agent (eg. person, group, software or physical artifact).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Agent: new VocabTerm(_NS$i("Agent"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Agent")
        .addCommentNoLanguage("An agent (eg. person, group, software or physical artifact)."),
    /**
     * A document.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Document: new VocabTerm(_NS$i("Document"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Document")
        .addCommentNoLanguage("A document."),
    /**
     * An organization.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Organization: new VocabTerm(_NS$i("Organization"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Organization")
        .addCommentNoLanguage("An organization."),
    /**
     * A project (a collective endeavour of some kind).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Project: new VocabTerm(_NS$i("Project"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Project")
        .addCommentNoLanguage("A project (a collective endeavour of some kind)."),
    /**
     * A class of Agents.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Group: new VocabTerm(_NS$i("Group"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Group")
        .addCommentNoLanguage("A class of Agents."),
    /**
     * An image.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Image: new VocabTerm(_NS$i("Image"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Image")
        .addCommentNoLanguage("An image."),
    /**
     * A foaf:LabelProperty is any RDF property with texual values that serve as labels.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    LabelProperty: new VocabTerm(_NS$i("LabelProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Label Property")
        .addCommentNoLanguage("A foaf:LabelProperty is any RDF property with texual values that serve as labels."),
    /**
     * An online account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    OnlineAccount: new VocabTerm(_NS$i("OnlineAccount"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Online Account")
        .addCommentNoLanguage("An online account."),
    /**
     * An online chat account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    OnlineChatAccount: new VocabTerm(_NS$i("OnlineChatAccount"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Online Chat Account")
        .addCommentNoLanguage("An online chat account."),
    /**
     * An online e-commerce account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    OnlineEcommerceAccount: new VocabTerm(_NS$i("OnlineEcommerceAccount"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Online E-commerce Account")
        .addCommentNoLanguage("An online e-commerce account."),
    /**
     * An online gaming account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    OnlineGamingAccount: new VocabTerm(_NS$i("OnlineGamingAccount"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Online Gaming Account")
        .addCommentNoLanguage("An online gaming account."),
    /**
     * A person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    Person: new VocabTerm(_NS$i("Person"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Person")
        .addCommentNoLanguage("A person."),
    /**
     * A personal profile RDF document.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    PersonalProfileDocument: new VocabTerm(_NS$i("PersonalProfileDocument"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("PersonalProfileDocument")
        .addCommentNoLanguage("A personal profile RDF document."),
    // *******************
    // All the Properties.
    // *******************
    /**
     * Indicates an account held by this agent.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    account: new VocabTerm(_NS$i("account"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("account")
        .addCommentNoLanguage("Indicates an account held by this agent."),
    /**
     * Indicates the name (identifier) associated with this online account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    accountName: new VocabTerm(_NS$i("accountName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("account name")
        .addCommentNoLanguage("Indicates the name (identifier) associated with this online account."),
    /**
     * Indicates a homepage of the service provide for this online account.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    accountServiceHomepage: new VocabTerm(_NS$i("accountServiceHomepage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("account service homepage")
        .addCommentNoLanguage("Indicates a homepage of the service provide for this online account."),
    /**
     * The age in years of some agent.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    age: new VocabTerm(_NS$i("age"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("age")
        .addCommentNoLanguage("The age in years of some agent."),
    /**
     * An AIM chat ID
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    aimChatID: new VocabTerm(_NS$i("aimChatID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("AIM chat ID")
        .addCommentNoLanguage("An AIM chat ID"),
    /**
     * A short informal nickname characterising an agent (includes login identifiers, IRC and other chat nicknames).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    nick: new VocabTerm(_NS$i("nick"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("nickname")
        .addCommentNoLanguage("A short informal nickname characterising an agent (includes login identifiers, IRC and other chat nicknames)."),
    /**
     * A location that something is based near, for some broadly human notion of near.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    based_near: new VocabTerm(_NS$i("based_near"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("based near")
        .addCommentNoLanguage("A location that something is based near, for some broadly human notion of near."),
    /**
     * The birthday of this Agent, represented in mm-dd string form, eg. '12-31'.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    birthday: new VocabTerm(_NS$i("birthday"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("birthday")
        .addCommentNoLanguage("The birthday of this Agent, represented in mm-dd string form, eg. '12-31'."),
    /**
     * A current project this person works on.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    currentProject: new VocabTerm(_NS$i("currentProject"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("current project")
        .addCommentNoLanguage("A current project this person works on."),
    /**
     * A depiction of some thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    depiction: new VocabTerm(_NS$i("depiction"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("depiction")
        .addCommentNoLanguage("A depiction of some thing."),
    /**
     * A thing depicted in this representation.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    depicts: new VocabTerm(_NS$i("depicts"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("depicts")
        .addCommentNoLanguage("A thing depicted in this representation."),
    /**
     * A checksum for the DNA of some thing. Joke.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    dnaChecksum: new VocabTerm(_NS$i("dnaChecksum"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("DNA checksum")
        .addCommentNoLanguage("A checksum for the DNA of some thing. Joke."),
    /**
     * The family name of some person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    familyName: new VocabTerm(_NS$i("familyName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("familyName")
        .addCommentNoLanguage("The family name of some person."),
    /**
     * The family name of some person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    family_name: new VocabTerm(_NS$i("family_name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("family_name")
        .addCommentNoLanguage("The family name of some person."),
    /**
     * The first name of a person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    firstName: new VocabTerm(_NS$i("firstName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("firstName")
        .addCommentNoLanguage("The first name of a person."),
    /**
     * The underlying or 'focal' entity associated with some SKOS-described concept.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    focus: new VocabTerm(_NS$i("focus"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("focus")
        .addCommentNoLanguage("The underlying or 'focal' entity associated with some SKOS-described concept."),
    /**
     * An organization funding a project or person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    fundedBy: new VocabTerm(_NS$i("fundedBy"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("funded by")
        .addCommentNoLanguage("An organization funding a project or person."),
    /**
     * A textual geekcode for this person, see http://www.geekcode.com/geek.html
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    geekcode: new VocabTerm(_NS$i("geekcode"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("geekcode")
        .addCommentNoLanguage("A textual geekcode for this person, see http://www.geekcode.com/geek.html"),
    /**
     * The gender of this Agent (typically but not necessarily 'male' or 'female').
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    gender: new VocabTerm(_NS$i("gender"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("gender")
        .addCommentNoLanguage("The gender of this Agent (typically but not necessarily 'male' or 'female')."),
    /**
     * The given name of some person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    givenName: new VocabTerm(_NS$i("givenName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Given name")
        .addCommentNoLanguage("The given name of some person."),
    /**
     * The given name of some person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    givenname: new VocabTerm(_NS$i("givenname"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Given name")
        .addCommentNoLanguage("The given name of some person."),
    /**
     * Indicates an account held by this agent.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    holdsAccount: new VocabTerm(_NS$i("holdsAccount"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("account")
        .addCommentNoLanguage("Indicates an account held by this agent."),
    /**
     * A homepage for some thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    homepage: new VocabTerm(_NS$i("homepage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("homepage")
        .addCommentNoLanguage("A homepage for some thing."),
    /**
     * A page or document about this thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    page: new VocabTerm(_NS$i("page"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("page")
        .addCommentNoLanguage("A page or document about this thing."),
    /**
     * A document that this thing is the primary topic of.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    isPrimaryTopicOf: new VocabTerm(_NS$i("isPrimaryTopicOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("is primary topic of")
        .addCommentNoLanguage("A document that this thing is the primary topic of."),
    /**
     * An ICQ chat ID
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    icqChatID: new VocabTerm(_NS$i("icqChatID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("ICQ chat ID")
        .addCommentNoLanguage("An ICQ chat ID"),
    /**
     * An image that can be used to represent some thing (ie. those depictions which are particularly representative of something, eg. one's photo on a homepage).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    img: new VocabTerm(_NS$i("img"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("image")
        .addCommentNoLanguage("An image that can be used to represent some thing (ie. those depictions which are particularly representative of something, eg. one's photo on a homepage)."),
    /**
     * A page about a topic of interest to this person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    interest: new VocabTerm(_NS$i("interest"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("interest")
        .addCommentNoLanguage("A page about a topic of interest to this person."),
    /**
     * The primary topic of some page or document.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    primaryTopic: new VocabTerm(_NS$i("primaryTopic"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("primary topic")
        .addCommentNoLanguage("The primary topic of some page or document."),
    /**
     * A jabber ID for something.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    jabberID: new VocabTerm(_NS$i("jabberID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("jabber ID")
        .addCommentNoLanguage("A jabber ID for something."),
    /**
     * A person known by this person (indicating some level of reciprocated interaction between the parties).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    knows: new VocabTerm(_NS$i("knows"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("knows")
        .addCommentNoLanguage("A person known by this person (indicating some level of reciprocated interaction between the parties)."),
    /**
     * The last name of a person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    lastName: new VocabTerm(_NS$i("lastName"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("lastName")
        .addCommentNoLanguage("The last name of a person."),
    /**
     * A logo representing some thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    logo: new VocabTerm(_NS$i("logo"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("logo")
        .addCommentNoLanguage("A logo representing some thing."),
    /**
     * Something that was made by this agent.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    made: new VocabTerm(_NS$i("made"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("made")
        .addCommentNoLanguage("Something that was made by this agent."),
    /**
     * An agent that made this thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    maker: new VocabTerm(_NS$i("maker"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("maker")
        .addCommentNoLanguage("An agent that made this thing."),
    /**
     * A personal mailbox, ie. an Internet mailbox associated with exactly one owner, the first owner of this mailbox. This is a 'static inverse functional property', in that there is (across time and change) at most one individual that ever has any particular value for foaf:mbox.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    mbox: new VocabTerm(_NS$i("mbox"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("personal mailbox")
        .addCommentNoLanguage("A personal mailbox, ie. an Internet mailbox associated with exactly one owner, the first owner of this mailbox. This is a 'static inverse functional property', in that there is (across time and change) at most one individual that ever has any particular value for foaf:mbox."),
    /**
     * The sha1sum of the URI of an Internet mailbox associated with exactly one owner, the first owner of the mailbox.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    mbox_sha1sum: new VocabTerm(_NS$i("mbox_sha1sum"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("sha1sum of a personal mailbox URI name")
        .addCommentNoLanguage("The sha1sum of the URI of an Internet mailbox associated with exactly one owner, the first owner of the mailbox."),
    /**
     * Indicates a member of a Group
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    member: new VocabTerm(_NS$i("member"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("member")
        .addCommentNoLanguage("Indicates a member of a Group"),
    /**
     * Indicates the class of individuals that are a member of a Group
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    membershipClass: new VocabTerm(_NS$i("membershipClass"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("membershipClass")
        .addCommentNoLanguage("Indicates the class of individuals that are a member of a Group"),
    /**
     * An MSN chat ID
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    msnChatID: new VocabTerm(_NS$i("msnChatID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("MSN chat ID")
        .addCommentNoLanguage("An MSN chat ID"),
    /**
     * A Myers Briggs (MBTI) personality classification.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    myersBriggs: new VocabTerm(_NS$i("myersBriggs"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("myersBriggs")
        .addCommentNoLanguage("A Myers Briggs (MBTI) personality classification."),
    /**
     * A name for some thing.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    name: new VocabTerm(_NS$i("name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("name")
        .addCommentNoLanguage("A name for some thing."),
    /**
     * An OpenID for an Agent.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    openid: new VocabTerm(_NS$i("openid"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("openid")
        .addCommentNoLanguage("An OpenID for an Agent."),
    /**
     * A topic of some page or document.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    topic: new VocabTerm(_NS$i("topic"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("topic")
        .addCommentNoLanguage("A topic of some page or document."),
    /**
     * A project this person has previously worked on.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    pastProject: new VocabTerm(_NS$i("pastProject"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("past project")
        .addCommentNoLanguage("A project this person has previously worked on."),
    /**
     * A phone, specified using fully qualified tel: URI scheme (refs: http://www.w3.org/Addressing/schemes.html#tel).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    phone: new VocabTerm(_NS$i("phone"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("phone")
        .addCommentNoLanguage("A phone, specified using fully qualified tel: URI scheme (refs: http://www.w3.org/Addressing/schemes.html#tel)."),
    /**
     * A .plan comment, in the tradition of finger and '.plan' files.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    plan: new VocabTerm(_NS$i("plan"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("plan")
        .addCommentNoLanguage("A .plan comment, in the tradition of finger and '.plan' files."),
    /**
     * A link to the publications of this person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    publications: new VocabTerm(_NS$i("publications"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("publications")
        .addCommentNoLanguage("A link to the publications of this person."),
    /**
     * A homepage of a school attended by the person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    schoolHomepage: new VocabTerm(_NS$i("schoolHomepage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("schoolHomepage")
        .addCommentNoLanguage("A homepage of a school attended by the person."),
    /**
     * A sha1sum hash, in hex.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    sha1: new VocabTerm(_NS$i("sha1"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("sha1sum (hex)")
        .addCommentNoLanguage("A sha1sum hash, in hex."),
    /**
     * A Skype ID
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    skypeID: new VocabTerm(_NS$i("skypeID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Skype ID")
        .addCommentNoLanguage("A Skype ID"),
    /**
     * A string expressing what the user is happy for the general public (normally) to know about their current activity.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    status: new VocabTerm(_NS$i("status"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("status")
        .addCommentNoLanguage("A string expressing what the user is happy for the general public (normally) to know about their current activity."),
    /**
     * The surname of some person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    surname: new VocabTerm(_NS$i("surname"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Surname")
        .addCommentNoLanguage("The surname of some person."),
    /**
     * A theme.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    theme: new VocabTerm(_NS$i("theme"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("theme")
        .addCommentNoLanguage("A theme."),
    /**
     * A derived thumbnail image.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    thumbnail: new VocabTerm(_NS$i("thumbnail"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("thumbnail")
        .addCommentNoLanguage("A derived thumbnail image."),
    /**
     * A tipjar document for this agent, describing means for payment and reward.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    tipjar: new VocabTerm(_NS$i("tipjar"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("tipjar")
        .addCommentNoLanguage("A tipjar document for this agent, describing means for payment and reward."),
    /**
     * Title (Mr, Mrs, Ms, Dr. etc)
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    title: new VocabTerm(_NS$i("title"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("title")
        .addCommentNoLanguage("Title (Mr, Mrs, Ms, Dr. etc)"),
    /**
     * A thing of interest to this person.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    topic_interest: new VocabTerm(_NS$i("topic_interest"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("topic_interest")
        .addCommentNoLanguage("A thing of interest to this person."),
    /**
     * A weblog of some thing (whether person, group, company etc.).
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    weblog: new VocabTerm(_NS$i("weblog"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("weblog")
        .addCommentNoLanguage("A weblog of some thing (whether person, group, company etc.)."),
    /**
     * A work info homepage of some person; a page about their work for some organization.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    workInfoHomepage: new VocabTerm(_NS$i("workInfoHomepage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("work info homepage")
        .addCommentNoLanguage("A work info homepage of some person; a page about their work for some organization."),
    /**
     * A workplace homepage of some person; the homepage of an organization they work for.
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    workplaceHomepage: new VocabTerm(_NS$i("workplaceHomepage"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("workplace homepage")
        .addCommentNoLanguage("A workplace homepage of some person; the homepage of an organization they work for."),
    /**
     * A Yahoo chat ID
     *
     * Defined by the vocabulary: http://xmlns.com/foaf/0.1/
     */
    yahooChatID: new VocabTerm(_NS$i("yahooChatID"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$j("http://xmlns.com/foaf/0.1/"))
        .addLabelNoLanguage("Yahoo chat ID")
        .addCommentNoLanguage("A Yahoo chat ID"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$k = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$j(localName) {
    return namedNode$k("http://purl.org/linked-data/cube#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var QB = {
    PREFIX: "qb",
    NAMESPACE: "http://purl.org/linked-data/cube#",
    PREFIX_AND_NAMESPACE: { "qb": "http://purl.org/linked-data/cube#" },
    NS: _NS$j,
    // *****************
    // All the Classes.
    // *****************
    /**
     * Represents a collection of observations, possibly organized into various slices, conforming to some common dimensional structure.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    DataSet: new VocabTerm(_NS$j("DataSet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Data set", "en")
        .addComment("Represents a collection of observations, possibly organized into various slices, conforming to some common dimensional structure.", "en"),
    /**
     * Abstract superclass for everything that can have attributes and dimensions
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    Attachable: new VocabTerm(_NS$j("Attachable"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Attachable (abstract)", "en")
        .addComment("Abstract superclass for everything that can have attributes and dimensions", "en"),
    /**
     * A single observation in the cube, may have one or more associated measured values
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    Observation: new VocabTerm(_NS$j("Observation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Observation", "en")
        .addComment("A single observation in the cube, may have one or more associated measured values", "en"),
    /**
     * A, possibly arbitrary, group of observations.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    ObservationGroup: new VocabTerm(_NS$j("ObservationGroup"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Observation Group", "en")
        .addComment("A, possibly arbitrary, group of observations.", "en"),
    /**
     * Denotes a subset of a DataSet defined by fixing a subset of the dimensional values, component properties on the Slice
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    Slice: new VocabTerm(_NS$j("Slice"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Slice", "en")
        .addComment("Denotes a subset of a DataSet defined by fixing a subset of the dimensional values, component properties on the Slice", "en"),
    /**
     * Abstract super-property of all properties representing dimensions, attributes or measures
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    ComponentProperty: new VocabTerm(_NS$j("ComponentProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Component property (abstract)", "en")
        .addComment("Abstract super-property of all properties representing dimensions, attributes or measures", "en"),
    /**
     * The class of components which represent the dimensions of the cube
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    DimensionProperty: new VocabTerm(_NS$j("DimensionProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Dimension property", "en")
        .addComment("The class of components which represent the dimensions of the cube", "en"),
    /**
     * Superclass of all coded ComponentProperties
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    CodedProperty: new VocabTerm(_NS$j("CodedProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Coded property", "en")
        .addComment("Superclass of all coded ComponentProperties", "en"),
    /**
     * The class of components which represent the measured value of the phenomenon being observed
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    MeasureProperty: new VocabTerm(_NS$j("MeasureProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Measure property", "en")
        .addComment("The class of components which represent the measured value of the phenomenon being observed", "en"),
    /**
     * The class of components which represent attributes of observations in the cube, e.g. unit of measurement
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    AttributeProperty: new VocabTerm(_NS$j("AttributeProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Attribute property", "en")
        .addComment("The class of components which represent attributes of observations in the cube, e.g. unit of measurement", "en"),
    /**
     * Defines the structure of a DataSet or slice
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    DataStructureDefinition: new VocabTerm(_NS$j("DataStructureDefinition"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Data structure definition", "en")
        .addComment("Defines the structure of a DataSet or slice", "en"),
    /**
     * Abstract class of things which reference one or more ComponentProperties
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    ComponentSet: new VocabTerm(_NS$j("ComponentSet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Component set", "en")
        .addComment("Abstract class of things which reference one or more ComponentProperties", "en"),
    /**
     * Used to define properties of a component (attribute, dimension etc) which are specific to its usage in a DSD.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    ComponentSpecification: new VocabTerm(_NS$j("ComponentSpecification"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Component specification", "en")
        .addComment("Used to define properties of a component (attribute, dimension etc) which are specific to its usage in a DSD.", "en"),
    /**
     * Denotes a subset of the component properties of a DataSet which are fixed in the corresponding slices
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    SliceKey: new VocabTerm(_NS$j("SliceKey"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Slice key", "en")
        .addComment("Denotes a subset of the component properties of a DataSet which are fixed in the corresponding slices", "en"),
    /**
     * Represents a generalized hierarchy of concepts which can be used for coding. The hierarchy is defined by one or more roots together with a property which relates concepts in the hierarchy to thier child concept .  The same concepts may be members of multiple hierarchies provided that different qb:parentChildProperty values are used for each hierarchy.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    HierarchicalCodeList: new VocabTerm(_NS$j("HierarchicalCodeList"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("Hierarchical Code List", "en")
        .addComment("Represents a generalized hierarchy of concepts which can be used for coding. The hierarchy is defined by one or more roots together with a property which relates concepts in the hierarchy to thier child concept .  The same concepts may be members of multiple hierarchies provided that different qb:parentChildProperty values are used for each hierarchy.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * indicates the data set of which this observation is a part
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    dataSet: new VocabTerm(_NS$j("dataSet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("data set", "en")
        .addComment("indicates the data set of which this observation is a part", "en"),
    /**
     * indicates a observation contained within this slice of the data set
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    observation: new VocabTerm(_NS$j("observation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("observation", "en")
        .addComment("indicates a observation contained within this slice of the data set", "en"),
    /**
     * Indicates a group of observations. The domain of this property is left open so that a group may be attached to different resources and need not be restricted to a single DataSet
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    observationGroup: new VocabTerm(_NS$j("observationGroup"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("observation group", "en")
        .addComment("Indicates a group of observations. The domain of this property is left open so that a group may be attached to different resources and need not be restricted to a single DataSet", "en"),
    /**
     * Indicates a subset of a DataSet defined by fixing a subset of the dimensional values
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    slice: new VocabTerm(_NS$j("slice"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("slice", "en")
        .addComment("Indicates a subset of a DataSet defined by fixing a subset of the dimensional values", "en"),
    /**
     * Generic measure dimension, the value of this dimension indicates which measure (from the set of measures in the DSD) is being given by the obsValue (or other primary measure)
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    measureType: new VocabTerm(_NS$j("measureType"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("measure type", "en")
        .addComment("Generic measure dimension, the value of this dimension indicates which measure (from the set of measures in the DSD) is being given by the obsValue (or other primary measure)", "en"),
    /**
     * indicates the structure to which this data set conforms
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    structure: new VocabTerm(_NS$j("structure"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("structure", "en")
        .addComment("indicates the structure to which this data set conforms", "en"),
    /**
     * indicates a component specification which is included in the structure of the dataset
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    component: new VocabTerm(_NS$j("component"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("component specification", "en")
        .addComment("indicates a component specification which is included in the structure of the dataset", "en"),
    /**
     * indicates a ComponentProperty (i.e. attribute/dimension) expected on a DataSet, or a dimension fixed in a SliceKey
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    componentProperty: new VocabTerm(_NS$j("componentProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("component", "en")
        .addComment("indicates a ComponentProperty (i.e. attribute/dimension) expected on a DataSet, or a dimension fixed in a SliceKey", "en"),
    /**
     * indicates a priority order for the components of sets with this structure, used to guide presentations - lower order numbers come before higher numbers, un-numbered components come last
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    order: new VocabTerm(_NS$j("order"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("order", "en")
        .addComment("indicates a priority order for the components of sets with this structure, used to guide presentations - lower order numbers come before higher numbers, un-numbered components come last", "en"),
    /**
     * Indicates whether a component property is required (true) or optional (false) in the context of a DSD. Only applicable
      to components correspond to an attribute. Defaults to false (optional).
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    componentRequired: new VocabTerm(_NS$j("componentRequired"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("component required", "en")
        .addComment("Indicates whether a component property is required (true) or optional (false) in the context of a DSD. Only applicable\n    to components correspond to an attribute. Defaults to false (optional).", "en"),
    /**
     * Indicates the level at which the component property should be attached, this might an qb:DataSet, qb:Slice or qb:Observation, or a qb:MeasureProperty.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    componentAttachment: new VocabTerm(_NS$j("componentAttachment"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("component attachment", "en")
        .addComment("Indicates the level at which the component property should be attached, this might an qb:DataSet, qb:Slice or qb:Observation, or a qb:MeasureProperty.", "en"),
    /**
     * An alternative to qb:componentProperty which makes explicit that the component is a dimension
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    dimension: new VocabTerm(_NS$j("dimension"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("dimension", "en")
        .addComment("An alternative to qb:componentProperty which makes explicit that the component is a dimension", "en"),
    /**
     * An alternative to qb:componentProperty which makes explicit that the component is a measure
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    measure: new VocabTerm(_NS$j("measure"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("measure", "en")
        .addComment("An alternative to qb:componentProperty which makes explicit that the component is a measure", "en"),
    /**
     * An alternative to qb:componentProperty which makes explicit that the component is a attribute
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    attribute: new VocabTerm(_NS$j("attribute"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("attribute", "en")
        .addComment("An alternative to qb:componentProperty which makes explicit that the component is a attribute", "en"),
    /**
     * An alternative to qb:componentProperty which makes explicit that the component is a measure dimension
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    measureDimension: new VocabTerm(_NS$j("measureDimension"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("measure dimension", "en")
        .addComment("An alternative to qb:componentProperty which makes explicit that the component is a measure dimension", "en"),
    /**
     * indicates the sub-key corresponding to this slice
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    sliceStructure: new VocabTerm(_NS$j("sliceStructure"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("slice structure", "en")
        .addComment("indicates the sub-key corresponding to this slice", "en"),
    /**
     * indicates a slice key which is used for slices in this dataset
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    sliceKey: new VocabTerm(_NS$j("sliceKey"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("slice key", "en")
        .addComment("indicates a slice key which is used for slices in this dataset", "en"),
    /**
     * gives the concept which is being measured or indicated by a ComponentProperty
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    concept: new VocabTerm(_NS$j("concept"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("concept", "en")
        .addComment("gives the concept which is being measured or indicated by a ComponentProperty", "en"),
    /**
     * gives the code list associated with a CodedProperty
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    codeList: new VocabTerm(_NS$j("codeList"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("code list", "en")
        .addComment("gives the code list associated with a CodedProperty", "en"),
    /**
     * Specifies a root of the hierarchy. A hierarchy may have multiple roots but must have at least one.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    hierarchyRoot: new VocabTerm(_NS$j("hierarchyRoot"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addComment("Specifies a root of the hierarchy. A hierarchy may have multiple roots but must have at least one.", "en"),
    /**
     * Specifies a property which relates a parent concept in the hierarchy to a child concept.
     *
     * Defined by the vocabulary: http://purl.org/linked-data/cube
     */
    parentChildProperty: new VocabTerm(_NS$j("parentChildProperty"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$k("http://purl.org/linked-data/cube"))
        .addLabel("parent-child property", "en")
        .addComment("Specifies a property which relates a parent concept in the hierarchy to a child concept.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$l = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$k(localName) {
    return namedNode$l("http://purl.org/linked-data/sdmx/2009/dimension#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var SDMX_DIMENSION = {
    PREFIX: "sdmx-dimension",
    NAMESPACE: "http://purl.org/linked-data/sdmx/2009/dimension#",
    PREFIX_AND_NAMESPACE: { "sdmx-dimension": "http://purl.org/linked-data/sdmx/2009/dimension#" },
    NS: _NS$k,
    // *******************
    // All the Properties.
    // *******************
    /**
     * The length of time that a person has lived or a thing has existed.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    age: new VocabTerm(_NS$k("age"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Age", "en")
        .addComment("The length of time that a person has lived or a thing has existed.", "en"),
    /**
     * Legal, conjugal status of each individual in relation to the marriage laws or customs of the country.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    civilStatus: new VocabTerm(_NS$k("civilStatus"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Civil Status", "en")
        .addComment("Legal, conjugal status of each individual in relation to the marriage laws or customs of the country.", "en"),
    /**
     * Monetary denomination of the object being measured.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    currency: new VocabTerm(_NS$k("currency"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Currency", "en")
        .addComment("Monetary denomination of the object being measured.", "en"),
    /**
     * The highest level of an educational programme the person has successfully completed.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    educationLev: new VocabTerm(_NS$k("educationLev"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Education Level", "en")
        .addComment("The highest level of an educational programme the person has successfully completed.", "en"),
    /**
     * The time interval at which observations occur over a given time period.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    freq: new VocabTerm(_NS$k("freq"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Frequency", "en")
        .addComment("The time interval at which observations occur over a given time period.", "en"),
    /**
     * Job or position held by an individual who performs a set of tasks and duties.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    occupation: new VocabTerm(_NS$k("occupation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Occupation", "en")
        .addComment("Job or position held by an individual who performs a set of tasks and duties.", "en"),
    /**
     * The country or geographic area to which the measured statistical phenomenon relates.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    refArea: new VocabTerm(_NS$k("refArea"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Reference Area", "en")
        .addComment("The country or geographic area to which the measured statistical phenomenon relates.", "en"),
    /**
     * The period of time or point in time to which the measured observation is intended to refer.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    refPeriod: new VocabTerm(_NS$k("refPeriod"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Reference Period", "en")
        .addComment("The period of time or point in time to which the measured observation is intended to refer.", "en"),
    /**
     * The state of being male or female.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    sex: new VocabTerm(_NS$k("sex"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Sex", "en")
        .addComment("The state of being male or female.", "en"),
    /**
     * The period of time or point in time to which the measured observation refers.
     *
     * Defined by the vocabulary: https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf
     */
    timePeriod: new VocabTerm(_NS$k("timePeriod"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$l("https://sdmx.org/wp-content/uploads/01_sdmx_cog_annex_1_cdc_2009.pdf"))
        .addLabel("Time Period", "en")
        .addComment("The period of time or point in time to which the measured observation refers.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$m = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$l(localName) {
    return namedNode$m("http://www.w3.org/ns/shex#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var SHEX = {
    PREFIX: "shex",
    NAMESPACE: "http://www.w3.org/ns/shex#",
    PREFIX_AND_NAMESPACE: { "shex": "http://www.w3.org/ns/shex#" },
    NS: _NS$l,
    // *****************
    // All the Classes.
    // *****************
    /**
     * Annotations provide a format-independent way to provide additional information about elements in a schema.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Annotation: new VocabTerm(_NS$l("Annotation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Annotation", "en")
        .addComment("Annotations provide a format-independent way to provide additional information about elements in a schema. ", "en"),
    /**
     * A TripleExpression composed of one or more sub-expressions, all of which must match.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    EachOf: new VocabTerm(_NS$l("EachOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Each Of", "en")
        .addComment("A TripleExpression composed of one or more sub-expressions, all of which must match.", "en"),
    /**
     * The abstract class of Triple Expressions.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    TripleExpression: new VocabTerm(_NS$l("TripleExpression"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Triple Expression", "en")
        .addComment("The abstract class of Triple Expressions.", "en"),
    /**
     * An IRI prefix used for matching IRIs.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    IriStem: new VocabTerm(_NS$l("IriStem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("IRI Stem", "en")
        .addComment("An IRI prefix used for matching IRIs.", "en"),
    /**
     * Abstract class for Stems
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Stem: new VocabTerm(_NS$l("Stem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Stem", "en")
        .addComment("Abstract class for Stems", "en"),
    /**
     * An IRI prefix (or wildcard) along with a set of excluded values, used for node matching.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    IriStemRange: new VocabTerm(_NS$l("IriStemRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("IRI StemRange", "en")
        .addComment("An IRI prefix (or wildcard) along with a set of excluded values, used for node matching.", "en"),
    /**
     * Abstract Class for Stem Ranges
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    StemRange: new VocabTerm(_NS$l("StemRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("StemRange", "en")
        .addComment("Abstract Class for Stem Ranges", "en"),
    /**
     * An Language tag used for matching Literal Languages.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Language: new VocabTerm(_NS$l("Language"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Language", "en")
        .addComment("An Language tag used for matching Literal Languages.", "en"),
    /**
     * An Language prefix used for matching Literal Languages.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    LanguageStem: new VocabTerm(_NS$l("LanguageStem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Language Stem", "en")
        .addComment("An Language prefix used for matching Literal Languages.", "en"),
    /**
     * An Language prefix (or wildcard) along with a set of excluded values, used for node matching.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    LanguageStemRange: new VocabTerm(_NS$l("LanguageStemRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Language StemRange", "en")
        .addComment("An Language prefix (or wildcard) along with a set of excluded values, used for node matching.", "en"),
    /**
     * An Literal prefix used for matching Literals.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    LiteralStem: new VocabTerm(_NS$l("LiteralStem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Literal Stem", "en")
        .addComment("An Literal prefix used for matching Literals.", "en"),
    /**
     * An Literal prefix (or wildcard) along with a set of excluded values, used for node matching.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    LiteralStemRange: new VocabTerm(_NS$l("LiteralStemRange"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Literal StemRange", "en")
        .addComment("An Literal prefix (or wildcard) along with a set of excluded values, used for node matching.", "en"),
    /**
     * A constraint on the type or value of an RDF Node.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    NodeConstraint: new VocabTerm(_NS$l("NodeConstraint"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Node Constraint", "en")
        .addComment("A constraint on the type or value of an RDF Node.", "en"),
    /**
     * The abstract class of Shape Expressions.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    ShapeExpression: new VocabTerm(_NS$l("ShapeExpression"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape Expression", "en")
        .addComment("The abstract class of Shape Expressions.", "en"),
    /**
     * The set of kinds of RDF Nodes.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    NodeKind: new VocabTerm(_NS$l("NodeKind"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Node Kind", "en")
        .addComment("The set of kinds of RDF Nodes.", "en"),
    /**
     * A TripleExpression composed of one or more sub-expressions, one of which must match.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    OneOf: new VocabTerm(_NS$l("OneOf"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("One Of", "en")
        .addComment("A TripleExpression composed of one or more sub-expressions, one of which must match.", "en"),
    /**
     * A Schema contains the set of shapes, used for matching a focus node.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Schema: new VocabTerm(_NS$l("Schema"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Schema", "en")
        .addComment("A Schema contains the set of shapes, used for matching a focus node.", "en"),
    /**
     * A list of Semantic Actions that serve as an extension point for Shape Expressions. They appear in lists in Schema's startActs and Shape, OneOf, EachOf and TripleConstraint's semActs.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    SemAct: new VocabTerm(_NS$l("SemAct"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Semantic Actions", "en")
        .addComment("A list of Semantic Actions that serve as an extension point for Shape Expressions. They appear in lists in Schema's startActs and Shape, OneOf, EachOf and TripleConstraint's semActs.", "en"),
    /**
     * A shapes schema is captured in a Schema object where shapes is a mapping from shape label to shape expression.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Shape: new VocabTerm(_NS$l("Shape"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape Or", "en")
        .addComment("A shapes schema is captured in a Schema object where shapes is a mapping from shape label to shape expression.", "en"),
    /**
     * A ShapeExpression composed of one or more sub-expressions, all of which must match.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    ShapeAnd: new VocabTerm(_NS$l("ShapeAnd"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape And", "en")
        .addComment("A ShapeExpression composed of one or more sub-expressions, all of which must match.", "en"),
    /**
     * A reference to a shape defined in some external Schema.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    ShapeExternal: new VocabTerm(_NS$l("ShapeExternal"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape External", "en")
        .addComment("A reference to a shape defined in some external Schema.", "en"),
    /**
     * A ShapeNot is satisfied when it’s included ShapeExpression is not satisfied.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    ShapeNot: new VocabTerm(_NS$l("ShapeNot"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape Not", "en")
        .addComment("A ShapeNot is satisfied when it\u2019s included ShapeExpression is not satisfied.", "en"),
    /**
     * A ShapeExpression composed of one or more sub-expressions, one of which must match.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    ShapeOr: new VocabTerm(_NS$l("ShapeOr"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Shape Or", "en")
        .addComment("A ShapeExpression composed of one or more sub-expressions, one of which must match.", "en"),
    /**
     * A constraint on a triple having a specific predicate and optionally a shape expression used for matching values.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    TripleConstraint: new VocabTerm(_NS$l("TripleConstraint"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Triple Constraint", "en")
        .addComment("A constraint on a triple having a specific predicate and optionally a shape expression used for matching values.", "en"),
    /**
     * Indicates that a stem is a Wildcard, rather than a URI prefix.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    Wildcard: new VocabTerm(_NS$l("Wildcard"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("Wildcard", "en")
        .addComment("Indicates that a stem is a Wildcard, rather than a URI prefix.", "en"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * Annotations on a TripleExpression.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    annotation: new VocabTerm(_NS$l("annotation"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("annotation", "en")
        .addComment("Annotations on a TripleExpression.", "en"),
    /**
     * Indicates that a Shape is closed, meaning that it may contain no property values other than those used within TripleConstraints.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    closed: new VocabTerm(_NS$l("closed"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("closed", "en")
        .addComment("Indicates that a Shape is closed, meaning that it may contain no property values other than those used within TripleConstraints.", "en"),
    /**
     * Code executed by Semantic Action.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    code: new VocabTerm(_NS$l("code"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("code", "en")
        .addComment("Code executed by Semantic Action.", "en"),
    /**
     * A datatype constraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    datatype: new VocabTerm(_NS$l("datatype"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("datatype", "en")
        .addComment("A datatype constraint.", "en"),
    /**
     * Values that are excluded from value matching.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    exclusion: new VocabTerm(_NS$l("exclusion"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("exclusion", "en")
        .addComment("Values that are excluded from value matching.", "en"),
    /**
     * Expression associated with the TripleExpression.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    expression: new VocabTerm(_NS$l("expression"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("expression", "en")
        .addComment("Expression associated with the TripleExpression.", "en"),
    /**
     * List of 2 or more expressions associated with the TripleExpression.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    expressions: new VocabTerm(_NS$l("expressions"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("expressions", "en")
        .addComment("List of 2 or more expressions associated with the TripleExpression.", "en"),
    /**
     * Properties which may have extra values beyond those matched through a constraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    extra: new VocabTerm(_NS$l("extra"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("extra", "en")
        .addComment("Properties which may have extra values beyond those matched through a constraint.", "en"),
    /**
     * Regular expression flags
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    flags: new VocabTerm(_NS$l("flags"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("flags", "en")
        .addComment("Regular expression flags", "en"),
    /**
     * for "fractiondigits" constraints, v is less than or equals the number of digits to the right of the decimal place in the XML Schema canonical form[xmlschema-2] of the value of n, ignoring trailing zeros.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    fractiondigits: new VocabTerm(_NS$l("fractiondigits"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("fraction digits", "en")
        .addComment("for \"fractiondigits\" constraints, v is less than or equals the number of digits to the right of the decimal place in the XML Schema canonical form[xmlschema-2] of the value of n, ignoring trailing zeros.", "en"),
    /**
     * Abstract property of numeric facets on a NodeConstraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    numericFacet: new VocabTerm(_NS$l("numericFacet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("", "en")
        .addComment("Abstract property of numeric facets on a NodeConstraint.", "en"),
    /**
     * Constrains the subject of a triple, rather than the object.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    inverse: new VocabTerm(_NS$l("inverse"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("inverse", "en")
        .addComment("Constrains the subject of a triple, rather than the object.", "en"),
    /**
     * The value used to match the language tag of a language-tagged string.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    languageTag: new VocabTerm(_NS$l("languageTag"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("language tag", "en")
        .addComment("The value used to match the language tag of a language-tagged string.", "en"),
    /**
     * The exact length of the value of the cell.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    length: new VocabTerm(_NS$l("length"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("length", "en")
        .addComment("The exact length of the value of the cell.", "en"),
    /**
     * An abstract property of string facets on a NodeConstraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    stringFacet: new VocabTerm(_NS$l("stringFacet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("", "en")
        .addComment("An abstract property of string facets on a NodeConstraint.", "en"),
    /**
     * Maximum number of times this TripleExpression may match; -1 for “*”
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    max: new VocabTerm(_NS$l("max"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("maximum cardinality", "en")
        .addComment("Maximum number of times this TripleExpression may match; -1 for \u201C*\u201D", "en"),
    /**
     * An atomic property that contains a single number that is the maximum valid value (exclusive).
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    maxexclusive: new VocabTerm(_NS$l("maxexclusive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("max exclusive", "en")
        .addComment("An atomic property that contains a single number that is the maximum valid value (exclusive).", "en"),
    /**
     * An atomic property that contains a single number that is the maximum valid value (inclusive).
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    maxinclusive: new VocabTerm(_NS$l("maxinclusive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("max inclusive", "en")
        .addComment("An atomic property that contains a single number that is the maximum valid value (inclusive).", "en"),
    /**
     * A numeric atomic property that contains a single integer that is the maximum length of the value.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    maxlength: new VocabTerm(_NS$l("maxlength"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("max length", "en")
        .addComment("A numeric atomic property that contains a single integer that is the maximum length of the value.", "en"),
    /**
     * Minimum number of times this TripleExpression may match.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    min: new VocabTerm(_NS$l("min"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("minimum cardinatliy", "en")
        .addComment("Minimum number of times this TripleExpression may match.", "en"),
    /**
     * An atomic property that contains a single number that is the minimum valid value (exclusive).
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    minexclusive: new VocabTerm(_NS$l("minexclusive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("min exclusive", "en")
        .addComment("An atomic property that contains a single number that is the minimum valid value (exclusive).", "en"),
    /**
     * An atomic property that contains a single number that is the minimum valid value (inclusive).
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    mininclusive: new VocabTerm(_NS$l("mininclusive"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("min inclusive", "en")
        .addComment("An atomic property that contains a single number that is the minimum valid value (inclusive).", "en"),
    /**
     * An atomic property that contains a single integer that is the minimum length of the value.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    minlength: new VocabTerm(_NS$l("minlength"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("min length", "en")
        .addComment("An atomic property that contains a single integer that is the minimum length of the value.", "en"),
    /**
     * Identifier of SemAct extension.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    name: new VocabTerm(_NS$l("name"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("name", "en")
        .addComment("Identifier of SemAct extension.", "en"),
    /**
     * Restiction on the kind of node matched; restricted to the defined instances of NodeKind. One of shex:iri, shex:bnode, shex:literal, or shex:nonliteral.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    nodeKind: new VocabTerm(_NS$l("nodeKind"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("node kind", "en")
        .addComment("Restiction on the kind of node matched; restricted to the defined instances of NodeKind. One of shex:iri, shex:bnode, shex:literal, or shex:nonliteral.", "en"),
    /**
     * An abstract property of string and numeric facets on a NodeConstraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    xsFacet: new VocabTerm(_NS$l("xsFacet"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("", "en")
        .addComment("An abstract property of string and numeric facets on a NodeConstraint.", "en"),
    /**
     * The object of an Annotation.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    object: new VocabTerm(_NS$l("object"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("object", "en")
        .addComment("The object of an Annotation.", "en"),
    /**
     * A regular expression used for matching a value.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    pattern: new VocabTerm(_NS$l("pattern"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("pattern", "en")
        .addComment("A regular expression used for matching a value.", "en"),
    /**
     * The predicate of a TripleConstraint or Annotation.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    predicate: new VocabTerm(_NS$l("predicate"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("predicate", "en")
        .addComment("The predicate of a TripleConstraint or Annotation.", "en"),
    /**
     * Semantic Actions on this TripleExpression.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    semActs: new VocabTerm(_NS$l("semActs"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("semantic action", "en")
        .addComment("Semantic Actions on this TripleExpression.", "en"),
    /**
     * Shape Expression referenced by this shape.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    shapeExpr: new VocabTerm(_NS$l("shapeExpr"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("shape expression", "en")
        .addComment("Shape Expression referenced by this shape.", "en"),
    /**
     * A list of 2 or more Shape Expressions referenced by this shape.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    shapeExprs: new VocabTerm(_NS$l("shapeExprs"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("shape expressions", "en")
        .addComment("A list of 2 or more Shape Expressions referenced by this shape.", "en"),
    /**
     * Shapes in this Schema.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    shapes: new VocabTerm(_NS$l("shapes"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("shapes", "en")
        .addComment("Shapes in this Schema.", "en"),
    /**
     * A ShapeExpression matched against the focus node prior to any other mapped expressions.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    start: new VocabTerm(_NS$l("start"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("start", "en")
        .addComment("A ShapeExpression matched against the focus node prior to any other mapped expressions.", "en"),
    /**
     * Semantic Actions run on the Schema.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    startActs: new VocabTerm(_NS$l("startActs"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("start actions", "en")
        .addComment("Semantic Actions run on the Schema.", "en"),
    /**
     * A stem value used for matching or excluding values.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    stem: new VocabTerm(_NS$l("stem"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("stem", "en")
        .addComment("A stem value used for matching or excluding values.", "en"),
    /**
     * for "totaldigits" constraints, v equals the number of digits in the XML Schema canonical form[xmlschema-2] of the value of n
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    totaldigits: new VocabTerm(_NS$l("totaldigits"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("total digits", "en")
        .addComment("for \"totaldigits\" constraints, v equals the number of digits in the XML Schema canonical form[xmlschema-2] of the value of n", "en"),
    /**
     * A ShapeExpression used for matching the object (or subject if inverted) of a TripleConstraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    valueExpr: new VocabTerm(_NS$l("valueExpr"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("value expression", "en")
        .addComment("A ShapeExpression used for matching the object (or subject if inverted) of a TripleConstraint.", "en"),
    /**
     * A value restriction on a NodeConstraint.
     *
     * Defined by the vocabulary: http://www.w3.org/ns/shex#
     */
    values: new VocabTerm(_NS$l("values"), dataModel, getLocalStore(), false)
        .addIsDefinedBy(namedNode$m("http://www.w3.org/ns/shex#"))
        .addLabel("values", "en")
        .addComment("A value restriction on a NodeConstraint.", "en"),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$n = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].
 */
function _NS$m(localName) {
    return namedNode$n("http://www.w3.org/ns/auth/acl#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var ACL = {
    PREFIX: "acl",
    NAMESPACE: "http://www.w3.org/ns/auth/acl#",
    PREFIX_AND_NAMESPACE: { "acl": "http://www.w3.org/ns/auth/acl#" },
    NS: _NS$m,
    // *****************
    // All the Classes.
    // *****************
    /**
     * Any kind of access to a resource. Don't use this, use R W and RW
     */
    Access: new VocabTerm(_NS$m("Access"), dataModel, getLocalStore(), false)
        .addCommentNoLanguage("Any kind of access to a resource. Don't use this, use R W and RW"),
    /**
     * Append accesses are specific write access which only add information, and do not remove information.
      For text files, for example, append access allows bytes to be added onto the end of the file.
      For RDF graphs, Append access allows adds triples to the graph but does not remove any.
      Append access is useful for dropbox functionality.
      Dropbox can be used for link notification, which the information added is a notification
      that a some link has been made elsewhere relevant to the given resource.
      
     */
    Append: new VocabTerm(_NS$m("Append"), dataModel, getLocalStore(), false)
        .addLabel("append", "en")
        .addCommentNoLanguage("Append accesses are specific write access which only add information, and do not remove information.\n    For text files, for example, append access allows bytes to be added onto the end of the file.\n    For RDF graphs, Append access allows adds triples to the graph but does not remove any.\n    Append access is useful for dropbox functionality.\n    Dropbox can be used for link notification, which the information added is a notification\n    that a some link has been made elsewhere relevant to the given resource.\n    "),
    /**
     * write
     */
    Write: new VocabTerm(_NS$m("Write"), dataModel, getLocalStore(), false)
        .addLabel("write", "en"),
    /**
     * A class of agents who have been authenticated.
  In other words, anyone can access this resource, but not anonymously.
  The social expectation is that the authentication process will provide an
  identify and a name, or pseudonym.
  (A new ID should not be minted for every access: the intent is that the user
  is able to continue to use the ID for continues interactions with peers,
  and for example to develop a reputation)
  
     */
    AuthenticatedAgent: new VocabTerm(_NS$m("AuthenticatedAgent"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("Anyone authenticated")
        .addCommentNoLanguage("A class of agents who have been authenticated.\nIn other words, anyone can access this resource, but not anonymously.\nThe social expectation is that the authentication process will provide an\nidentify and a name, or pseudonym.\n(A new ID should not be minted for every access: the intent is that the user\nis able to continue to use the ID for continues interactions with peers,\nand for example to develop a reputation)\n"),
    /**
     * An element of access control,
      allowing agent to agents access of some kind to resources or classes of resources
     */
    Authorization: new VocabTerm(_NS$m("Authorization"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("authorization")
        .addCommentNoLanguage("An element of access control,\n    allowing agent to agents access of some kind to resources or classes of resources"),
    /**
     * Allows read/write access to the ACL for the resource(s)
     */
    Control: new VocabTerm(_NS$m("Control"), dataModel, getLocalStore(), false)
        .addLabel("control", "en")
        .addCommentNoLanguage("Allows read/write access to the ACL for the resource(s)"),
    /**
     * An Origin is basically a web site
          (Note WITHOUT the trailing slash after the domain name and port in its URI)
          and is the basis for controlling access to data by web apps
          in the Same Origin Model of web security.
          All scripts from the same origin are given the same right.
     */
    Origin: new VocabTerm(_NS$m("Origin"), dataModel, getLocalStore(), false)
        .addSeeAlso(namedNode$n("https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Origin"))
        .addLabelNoLanguage("Origin")
        .addCommentNoLanguage("An Origin is basically a web site\n        (Note WITHOUT the trailing slash after the domain name and port in its URI)\n        and is the basis for controlling access to data by web apps\n        in the Same Origin Model of web security.\n        All scripts from the same origin are given the same right."),
    /**
     * The class of read operations
     */
    Read: new VocabTerm(_NS$m("Read"), dataModel, getLocalStore(), false)
        .addLabel("read", "en")
        .addCommentNoLanguage("The class of read operations"),
    // *******************
    // All the Properties.
    // *******************
    /**
     * The Access Control file for this information resource.
          This may of course be a virtual resource implemented by the access control system.
          Note also HTTP's header  Link:  foo.meta ;rel=meta can be used for this.
     */
    accessControl: new VocabTerm(_NS$m("accessControl"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("access control")
        .addCommentNoLanguage("The Access Control file for this information resource.\n        This may of course be a virtual resource implemented by the access control system.\n        Note also HTTP's header  Link:  foo.meta ;rel=meta can be used for this."),
    /**
     * The information resource to which access is being granted.
     */
    accessTo: new VocabTerm(_NS$m("accessTo"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("to")
        .addCommentNoLanguage("The information resource to which access is being granted."),
    /**
     * A class of information resources to which access is being granted.
     */
    accessToClass: new VocabTerm(_NS$m("accessToClass"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("to all in")
        .addCommentNoLanguage("A class of information resources to which access is being granted."),
    /**
     * A person or social entity to being given the right
     */
    agent: new VocabTerm(_NS$m("agent"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("agent")
        .addCommentNoLanguage("A person or social entity to being given the right"),
    /**
     * A class of persons or social entities to being given the right
     */
    agentClass: new VocabTerm(_NS$m("agentClass"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("agent class")
        .addCommentNoLanguage("A class of persons or social entities to being given the right"),
    /**
     * A group of persons or social entities to being given the right.
            The right is given to any entity which is a vcard:member of the group,
            as defined by the document received when the Group is dereferenced.
     */
    agentGroup: new VocabTerm(_NS$m("agentGroup"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("agent group")
        .addCommentNoLanguage("A group of persons or social entities to being given the right.\n          The right is given to any entity which is a vcard:member of the group,\n          as defined by the document received when the Group is dereferenced."),
    /**
     * If a resource has no ACL file (it is 404),
          then access to the resource if given by the ACL of the immediately
          containing directory, or failing that (404) the ACL of the recursively next
          containing directory which has an ACL file.
          Within that ACL file,
          any Authentication which has that directory as its acl:default applies to the
          resource. (The highest directory must have an ACL file.)
  
     */
    default: new VocabTerm(_NS$m("default"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("default access for things in this")
        .addCommentNoLanguage("If a resource has no ACL file (it is 404),\n        then access to the resource if given by the ACL of the immediately\n        containing directory, or failing that (404) the ACL of the recursively next\n        containing directory which has an ACL file.\n        Within that ACL file,\n        any Authentication which has that directory as its acl:default applies to the\n        resource. (The highest directory must have an ACL file.)\n"),
    /**
     * THIS IS OBSOLETE AS OF 2017-08-01.   See 'default'.
          Was: A directory for which this authorization is used for new files in the directory.
     */
    defaultForNew: new VocabTerm(_NS$m("defaultForNew"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("default access for new things in the object")
        .addCommentNoLanguage("THIS IS OBSOLETE AS OF 2017-08-01.   See 'default'.\n        Was: A directory for which this authorization is used for new files in the directory."),
    /**
     * Delegates a person or another agent to act on behalf of the agent.
      For example, Alice delegates Bob to act on behalf of Alice for ACL purposes.
     */
    delegates: new VocabTerm(_NS$m("delegates"), dataModel, getLocalStore(), false)
        .addLabel("delegates", "en")
        .addCommentNoLanguage("Delegates a person or another agent to act on behalf of the agent.\n    For example, Alice delegates Bob to act on behalf of Alice for ACL purposes."),
    /**
     * A mode of access such as read or write.
     */
    mode: new VocabTerm(_NS$m("mode"), dataModel, getLocalStore(), false)
        .addLabelNoLanguage("access mode")
        .addCommentNoLanguage("A mode of access such as read or write."),
    /**
     * A web application, identified by its Origin, such as
          <https://scripts.example.com>, being given the right.
          When a user of the web application at a certain origin accesses the server,
          then the browser sets the Origin: header to warn that a possibly untrusted webapp
          is being used.
          Then, BOTH the user AND the origin must have the required access.
     */
    origin: new VocabTerm(_NS$m("origin"), dataModel, getLocalStore(), false)
        .addSeeAlso(namedNode$n("https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Origin"))
        .addLabelNoLanguage("origin")
        .addCommentNoLanguage("A web application, identified by its Origin, such as\n        <https://scripts.example.com>, being given the right.\n        When a user of the web application at a certain origin accesses the server,\n        then the browser sets the Origin: header to warn that a possibly untrusted webapp\n        is being used.\n        Then, BOTH the user AND the origin must have the required access."),
    /**
     * The person or other agent which owns this.
      For example, the owner of a file in a filesystem.
      There is a sense of right to control.   Typically defaults to the agent who craeted
      something but can be changed.
     */
    owner: new VocabTerm(_NS$m("owner"), dataModel, getLocalStore(), false)
        .addLabel("owner", "en")
        .addCommentNoLanguage("The person or other agent which owns this.\n    For example, the owner of a file in a filesystem.\n    There is a sense of right to control.   Typically defaults to the agent who craeted\n    something but can be changed."),
};

/**
 * MIT License
 *
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the &quot;Software&quot;), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var namedNode$o = dataModel.namedNode;
/**
  generated by the artifact generator [@inrupt/lit-artifact-generator], version [0.12.4]
  as part of artifact: [vocab-common-rdf], version: [0.6.3]
  at 'Tuesday, November 3, 2020 11:49 PM'.

  Vocabulary built from vocab list file: [common-rdf/Common/Vocab-List-Common-Rdf.yml].

  A vocabulary for the basic POSIX terms.
 */
function _NS$n(localName) {
    return namedNode$o("http://www.w3.org/ns/posix/stat#" + localName);
}
// Add 'any' type annotation in case this vocab includes 'NamedNode' instances,
// for example instances of Constant IRIs (TypeScript compiler will complain of
// "semantic error TS2742" otherwise).
var POSIX = {
    PREFIX: "posix",
    NAMESPACE: "http://www.w3.org/ns/posix/stat#",
    PREFIX_AND_NAMESPACE: { "posix": "http://www.w3.org/ns/posix/stat#" },
    NS: _NS$n,
    // *******************
    // All the Properties.
    // *******************
    /**
     * Time of last access
     */
    atime: new VocabTerm(_NS$n("atime"), dataModel, getLocalStore(), false)
        .addLabel("Access time", "en")
        .addComment("Time of last access", "en"),
    /**
     * Blocksize for file system I/O
     */
    blksize: new VocabTerm(_NS$n("blksize"), dataModel, getLocalStore(), false)
        .addLabel("Blocksize", "en")
        .addComment("Blocksize for file system I/O", "en"),
    /**
     * Number of 512B blocks allocated
     */
    blocks: new VocabTerm(_NS$n("blocks"), dataModel, getLocalStore(), false)
        .addLabel("Blocks", "en")
        .addComment("Number of 512B blocks allocated", "en"),
    /**
     * Time of last status change
     */
    ctime: new VocabTerm(_NS$n("ctime"), dataModel, getLocalStore(), false)
        .addLabel("Status change time", "en")
        .addComment("Time of last status change", "en"),
    /**
     * ID of device containing file
     */
    dev: new VocabTerm(_NS$n("dev"), dataModel, getLocalStore(), false)
        .addLabel("Device ID", "en")
        .addComment("ID of device containing file", "en"),
    /**
     * Group ID of owner
     */
    gid: new VocabTerm(_NS$n("gid"), dataModel, getLocalStore(), false)
        .addLabel("Owner Group ID", "en")
        .addComment("Group ID of owner", "en"),
    /**
     * inode number
     */
    ino: new VocabTerm(_NS$n("ino"), dataModel, getLocalStore(), false)
        .addLabel("iNode number", "en")
        .addComment("inode number", "en"),
    /**
     * Protection
     */
    mode: new VocabTerm(_NS$n("mode"), dataModel, getLocalStore(), false)
        .addLabel("Protection", "en")
        .addComment("Protection", "en"),
    /**
     * Time of last modification
     */
    mtime: new VocabTerm(_NS$n("mtime"), dataModel, getLocalStore(), false)
        .addLabel("Modified time", "en")
        .addComment("Time of last modification", "en"),
    /**
     * Number of hard links
     */
    nlink: new VocabTerm(_NS$n("nlink"), dataModel, getLocalStore(), false)
        .addLabel("Number of hard links", "en")
        .addComment("Number of hard links", "en"),
    /**
     * Device ID (if special file)
     */
    rdev: new VocabTerm(_NS$n("rdev"), dataModel, getLocalStore(), false)
        .addLabel("Device ID", "en")
        .addComment("Device ID (if special file)", "en"),
    /**
     * Total size, in bytes
     */
    size: new VocabTerm(_NS$n("size"), dataModel, getLocalStore(), false)
        .addLabel("Size", "en")
        .addComment("Total size, in bytes", "en"),
    /**
     * User ID of owner
     */
    uid: new VocabTerm(_NS$n("uid"), dataModel, getLocalStore(), false)
        .addLabel("Owner User ID", "en")
        .addComment("User ID of owner", "en"),
};

export { ACL, AS, DCTERMS, FOAF, HTTP, HTTPH, LDP, OLO, OWL, POSIX, QB, RDF, RDFS, RDF_INRUPT_EXT, RLOG, SCHEMA_INRUPT_EXT, SDMX_DIMENSION, SHEX, SKOS, UI, VANN, VCARD, VCARD_INRUPT_EXT, XSD };
